import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useAnimation, useDragControls, type PanInfo } from 'framer-motion';
import {
  X,
  MapPin,
  Pencil,
  Navigation2,
  ChevronLeft,
  Check,
  Loader2,
  Target,
  Square,
  Play,
  Pause,
  RotateCcw,
  Undo2,
  Redo2,
  MapPinOff,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Crosshair,
  Locate,
  Plus,
  Hash,
  Trash2,
  SkipForward,
  Building2,
  Search,
  Link,
  ChevronDown,
  ChevronUp,
  Scan,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useMapStore } from '../../stores/mapStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { roadSnapBandsFromSettings } from '../../features/geometry-editing/roadSnapBands';
import type { ContributionSheetSnap } from '../map/mapSheetOffset';
import {
  submitContribution,
  updateContribution,
  APIError,
  type GeoJSONGeometry,
  type ContributionDetail,
  getCategories,
  reverseGeocode,
  reverseGeocodeForContribution,
  getPlaceDetails,
  listRegions,
  listDistricts,
  searchTowns,
  searchSuburbs,
  type Category as ApiCategory,
  findNearestFeature,
  matchFeatureByName,
  type MapFeatureResult,
} from '../../services/api';
import { contributionGeometryInApiGhanaBounds } from '../../utils/geo';
import { SURVEY_MIN_ZOOM } from '../map/config/mapConfig';
import { ALLOW_FIELD_MAPPING_OUTSIDE_GHANA } from '../../lib/env';
import { isGssBoundaryPlaceCategory, contributionFeatureShowsBusinessDetails, metadataUpdateFields, type MetadataUpdateIntent, type OfficialAreaLevel } from '../../utils/contributionPlaceAdmin';
import { patchFromContributionExtras } from '../../utils/contributionEditPrefill';
import {
  inferCarriagewayCountFromDetails,
  isMultiCarriageway,
  parseLaneDirections,
  parseOptionalWidthM,
  roadFlowExtras,
  validateRoadFlowFields,
} from './roadFlowUtils';
import { type DividedCarriagewayGuideSide } from '../../utils/dividedCarriagewayGeometry';
import {
  deriveCentreFromOuterKerb,
  parseStripCaptureMode,
  stripNeedsMapTrace,
} from '../../utils/roadStripSurvey';
import { usesMotorRoadSurveyFlow } from '../../utils/roadContributionFlow';
import { RoadContributionDetailsForm } from '../../features/road-contribution/RoadContributionDetailsForm';
import { RoadGeometryWizard } from '../../features/road-contribution/RoadGeometryWizard';
import {
  EnclosureFeatureTypeStep,
  EnclosureMapStep,
  EnclosureMethodStep,
  EnclosurePreviewStep,
  type EnclosureMethod,
} from '../../features/enclosure-contribution/EnclosureDetectionPanel';
import {
  detectEnclosure,
  type EnclosureDetectResponse,
} from '../../features/enclosure-contribution/enclosureApi';
import { useRoadDetailsState } from '../../features/road-contribution/useRoadDetailsState';
import {
  resolveContributionLineFromSources,
  useRoadMarkingSession,
} from '../../features/road-contribution/useRoadMarkingSession';
import {
  EMPTY_SIDE_FACILITIES,
  sideFacilityExtras,
  supportsSideFacilityTags,
  validateSideFacilityFields,
} from '../../utils/roadSideFacilities';
import { validateContactPhone } from '../../utils/contactPhone';
import {
  formatUnitFloorLabel,
  normalizeBuildingUnits,
  parseUnitFloorInput,
  summarizeBuildingUnitsForReview,
} from '../../utils/buildingUnitsReview';
import type { Place } from '../../types';
import { composeAddressLine, composeBuildingAddressLine, composePlaceContextLine, composeRoadAddressLine, getContributionAnchor, getGeometryAnchor, placeAddressPrefill, mergeAddressFields, normalizeLocalityFields, applyAddressFields, placeHasStoredAddress, extractReverseGeocodeComponents, extractReverseGeocodeBuildingNumber, hasGeocodedAddressComponents, contributionShowsSuburbField, emptyContributionAddressFields, resolveBuildingHostAddressPrefill, resolveRoadContributionName, resolveRoadEnrichDisplayName, streetLineWithoutHouseNumber, usesBuildingHostAddressFormat } from '../../utils/contributionAddress';
import { formatJunctionExitTo, parseJunctionExitRoads, type JunctionExitRoad } from '../../utils/junctionExitRoads';
import { resolveCanonicalAdminFields } from '../../utils/contributionAddressLookup';
import { transitStopFieldsFromPlace } from '../../utils/transitStop';
import { openingHoursPrefill } from '../../utils/openingHours';
import { isValidFullGhanaPostGps } from '../../utils/ghanaPostGps';
import {
  buildTraceGeometry,
  drawProducesPolygon,
  hasDrawableGeometry,
  hasValidTraceGeometry,
  isLineStringGeometry,
  parseStoredGeometry,
  traceMinimumPoints,
  traceProducesPolygon,
} from '../../utils/contributionGeometry';
import {
  isRoadEnrichUpdate,
  shouldUseEnrichFeatureForContribution,
} from '../../utils/contributionEnrichMode';
import {
  DEFAULT_TRACE_MIN_DISTANCE_M,
  TRACE_DISTANCE_OPTIONS_M,
  type TraceCaptureMode,
  type TraceDistanceOptionM,
  type TraceSamplingMode,
} from '../../utils/fieldMappingGps';
import { computePolygonAreaSqM, formatAreaLabel } from '../../utils/polygonArea';
import { DynamicIcon, colorClasses } from '../ui/DynamicIcon';
import { applyCategoryHints, categorySearchText } from './contributionCategoryHints';
import {
  POI_FALLBACK_CATEGORIES,
  POI_SUPPLEMENTAL_CATEGORIES,
  POI_TRANSPORT_CATEGORY_IDS,
  type ContributionCategoryOption,
} from './contributionPoiCategories';
import { SearchableBoundaryInput } from './SearchableBoundaryInput';

import { isContributionImageFile, contributionImageMime } from '../../utils/contributionPhotos';

const MAX_CONTRIBUTION_PHOTOS = 3;
const MAX_PHOTO_BYTES = Math.floor(1.5 * 1024 * 1024);

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      let result = r.result as string;
      const mime = contributionImageMime(file);
      if (mime.startsWith('image/') && result.startsWith('data:') && !result.startsWith(`data:${mime}`)) {
        const base64 = result.split(',')[1];
        if (base64) result = `data:${mime};base64,${base64}`;
      }
      resolve(result);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

const ENRICH_CONTAIN_FEATURE_TYPES = new Set([
  'landuse',
  'landcover',
  'water',
  'building',
  'place',
  'aeroway',
]);
type GeometryMode = 'point' | 'draw' | 'trace' | 'coordinates' | 'link' | 'enrich';
type ContributionStep =
  | 'feature_type'
  | 'mode'
  | 'landmark_group'
  | 'landmark_placement'
  | 'category'
  | 'road_layout'
  | 'road_capture_plan'
  | 'marking'
  | 'enrich_select'
  | 'official_select'
  | 'details'
  | 'building_units'
  | 'review'
  | 'enclosure_method'
  | 'enclosure_capture_mode'
  | 'enclosure_map'
  | 'enclosure_marking'
  | 'enclosure_preview'
  | 'enclosure_feature_type';

interface BuildingUnit {
  label: string;
  floor: number;
}

type FloorBatchDraft = {
  autoNumberStart: string;
  autoNumberCount: string;
  unitInput: string;
  unitMode: 'manual' | 'auto';
};

interface FeatureTypeOption {
  id: string;
  name: string;
  description: string;
  icon: string;
  allowedModes: GeometryMode[];
  needsCategory?: boolean;
}

const FEATURE_TYPES: FeatureTypeOption[] = [
  { id: 'official_area', name: 'Official Area', description: 'Description or photos for Ghana, a region, or district already on the map', icon: 'landmark', allowedModes: [], needsCategory: false },
  { id: 'place', name: 'Place', description: 'Towns, villages, suburbs, and neighbourhoods', icon: 'map', allowedModes: ['point'], needsCategory: true },
  { id: 'building', name: 'Building', description: 'Houses, shops, offices, and other structures', icon: 'building-2', allowedModes: ['point', 'draw', 'trace'], needsCategory: true },
  { id: 'poi', name: 'Point of Interest', description: 'Restaurants, hospitals, schools, landmarks', icon: 'map-pin', allowedModes: ['point'], needsCategory: true },
  { id: 'transport_stop', name: 'Transport Stop', description: 'Trotro, bus, taxi, and rail stops', icon: 'bus', allowedModes: ['point'], needsCategory: true },
  { id: 'road', name: 'Road / Path', description: 'Streets, highways, footpaths, tracks', icon: 'route', allowedModes: ['draw', 'trace'], needsCategory: true },
  { id: 'landuse', name: 'Land Use', description: 'Parks, farms, industrial zones, residential areas', icon: 'trees', allowedModes: ['draw', 'trace'], needsCategory: true },
  { id: 'water', name: 'Water Body', description: 'Lakes, ponds, reservoirs', icon: 'droplets', allowedModes: ['draw', 'trace'], needsCategory: true },
  { id: 'waterway', name: 'Waterway', description: 'Rivers, streams, canals, drains', icon: 'waves', allowedModes: ['draw', 'trace'], needsCategory: true },
  { id: 'landmark', name: 'Landmark', description: 'Notable buildings, monuments, tourist attractions', icon: 'landmark', allowedModes: ['point'], needsCategory: true },
  { id: 'structure', name: 'Structure', description: 'Walls, fences, gates, and barriers', icon: 'construction', allowedModes: ['point', 'draw', 'trace'], needsCategory: true },
  { id: 'traffic_signal', name: 'Traffic Control', description: 'Traffic lights, street lights, speed cameras, signalized crossings', icon: 'traffic-cone', allowedModes: ['point'], needsCategory: true },
  { id: 'tree', name: 'Tree', description: 'Notable or mapped individual trees', icon: 'tree-pine', allowedModes: ['point'], needsCategory: true },
  { id: 'landcover', name: 'Land Cover', description: 'Forests, grasslands, bare rock, sand', icon: 'mountain', allowedModes: ['draw', 'trace'], needsCategory: true },
  { id: 'aeroway', name: 'Aeroway', description: 'Runways, taxiways, helipads, and airport features', icon: 'plane', allowedModes: ['point', 'draw', 'trace'], needsCategory: true },
  { id: 'motorway_junction', name: 'Motorway Junction', description: 'Highway exits and interchanges', icon: 'signpost', allowedModes: ['point'], needsCategory: false },
  { id: 'turn_restriction', name: 'Turn Restriction', description: 'Banned turn between two roads at an intersection', icon: 'ban', allowedModes: ['point'], needsCategory: false },
];

/** Polygon feature types offered after enclosure geometry is confirmed (same labels as the main grid). */
const ENCLOSURE_POLYGON_FEATURE_IDS = [
  'landuse',
  'landcover',
  'water',
  'building',
  'structure',
  'aeroway',
] as const;

function suggestEditContributionFeatureType(place: Place): string {
  const t = place.placeType ?? 'place';
  const mapped: Record<string, string> = {
    building: 'building',
    poi: 'poi',
    place: 'place',
    street: 'road',
    transit_stop: 'transport_stop',
    airport: 'aeroway',
    aeroway: 'aeroway',
    natural: 'landcover',
    water: 'water',
    traffic_signal: 'traffic_signal',
    country: 'place',
  };
  return mapped[t] ?? 'place';
}

const TRANSPORT_STOP_CATEGORY_IDS = POI_TRANSPORT_CATEGORY_IDS;

const TRANSPORT_STOP_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'bus_station', name: 'Bus / Trotro Station', icon: 'bus', color: 'sky' },
  { id: 'bus_stop', name: 'Bus Stop', icon: 'bus', color: 'sky' },
  { id: 'trotro', name: 'Trotro Stop', icon: 'bus', color: 'sky' },
  { id: 'taxi', name: 'Taxi Stand', icon: 'car-taxi-front', color: 'yellow' },
  { id: 'ferry_terminal', name: 'Ferry Terminal', icon: 'ship', color: 'cyan' },
  { id: 'rail_station', name: 'Rail Station', icon: 'train', color: 'slate' },
];

/** When /map/categories returns empty (e.g. dev DB), use POI_FALLBACK_CATEGORIES (synced with map.py). */

function mergePoiCategoryOptions(options: ContributionCategoryOption[]): ContributionCategoryOption[] {
  const seen = new Set(options.map((c) => c.id));
  const extras = POI_SUPPLEMENTAL_CATEGORIES.filter((c) => !seen.has(c.id));
  if (!extras.length) return options;
  return [...options, ...extras].sort((a, b) => a.name.localeCompare(b.name));
}

/** Landuse = area/zoning polygons (OSM landuse=*). Not the same as POI types (amenity/shop/etc.). */
const LANDUSE_CATEGORIES: ContributionCategoryOption[] = [
  // Settlement & civic
  { id: 'residential', name: 'Residential', icon: 'home', color: 'blue' },
  { id: 'apartment', name: 'Apartment Block Zone', icon: 'building-2', color: 'indigo' },
  { id: 'commercial', name: 'Commercial', icon: 'store', color: 'orange' },
  { id: 'retail', name: 'Retail', icon: 'shopping-bag', color: 'pink' },
  { id: 'industrial', name: 'Industrial', icon: 'factory', color: 'gray' },
  { id: 'education', name: 'Education', icon: 'graduation-cap', color: 'indigo' },
  { id: 'school', name: 'School Grounds', icon: 'school', color: 'indigo' },
  { id: 'religious', name: 'Religious', icon: 'church', color: 'purple' },
  { id: 'place_of_worship', name: 'Place of Worship Grounds', icon: 'church', color: 'purple' },
  { id: 'cemetery', name: 'Cemetery', icon: 'cross', color: 'slate' },
  { id: 'government', name: 'Government', icon: 'landmark', color: 'blue' },
  { id: 'public_office', name: 'Public Office', icon: 'building-2', color: 'indigo' },
  { id: 'hospital', name: 'Hospital Grounds', icon: 'hospital', color: 'red' },
  { id: 'military', name: 'Military', icon: 'shield', color: 'red' },
  { id: 'airport', name: 'Airport Land', icon: 'plane', color: 'sky' },
  { id: 'museum', name: 'Museum Grounds', icon: 'landmark', color: 'amber' },
  { id: 'nature_reserve', name: 'Nature Reserve', icon: 'leaf', color: 'emerald' },
  { id: 'refugee_site', name: 'Refugee / Camp Site', icon: 'tent', color: 'orange' },
  // Agriculture & rural
  { id: 'farm', name: 'Farm Land', icon: 'wheat', color: 'amber' },
  { id: 'farmland', name: 'Farmland', icon: 'wheat', color: 'amber' },
  { id: 'farmyard', name: 'Farmyard', icon: 'tractor', color: 'amber' },
  { id: 'orchard', name: 'Orchard', icon: 'apple', color: 'green' },
  { id: 'cultivation', name: 'Cultivation', icon: 'sprout', color: 'lime' },
  { id: 'greenhouse_horticulture', name: 'Greenhouse / Horticulture', icon: 'flower-2', color: 'green' },
  { id: 'plant_nursery', name: 'Plant Nursery', icon: 'leaf', color: 'emerald' },
  { id: 'aquaculture', name: 'Aquaculture / Fish Pond', icon: 'waves', color: 'cyan' },
  { id: 'animal_keeping', name: 'Animal Keeping / Livestock', icon: 'beef', color: 'amber' },
  { id: 'allotments', name: 'Allotments / Community Garden', icon: 'fence', color: 'green' },
  // Green space
  { id: 'forest', name: 'Forest', icon: 'trees', color: 'emerald' },
  { id: 'park', name: 'Park / Green Space', icon: 'trees', color: 'green' },
  { id: 'grass', name: 'Grassland / Park', icon: 'flower-2', color: 'green' },
  { id: 'meadow', name: 'Meadow', icon: 'sprout', color: 'lime' },
  { id: 'recreation_ground', name: 'Recreation', icon: 'dribbble', color: 'teal' },
  { id: 'stadium', name: 'Stadium Grounds', icon: 'circle-dot', color: 'emerald' },
  { id: 'pitch', name: 'Sports Pitch Area', icon: 'footprints', color: 'lime' },
  { id: 'village_green', name: 'Village Green', icon: 'trees', color: 'green' },
  { id: 'scrub', name: 'Scrub / Bushland', icon: 'shrub', color: 'lime' },
  { id: 'flowerbed', name: 'Flowerbed / Garden', icon: 'flower', color: 'pink' },
  { id: 'garden', name: 'Garden / Orchard Garden', icon: 'flower-2', color: 'pink' },
  { id: 'marketplace', name: 'Marketplace Zone', icon: 'store', color: 'emerald' },
  // Development & transport
  { id: 'construction', name: 'Construction', icon: 'hard-hat', color: 'yellow' },
  { id: 'brownfield', name: 'Brownfield', icon: 'construction', color: 'stone' },
  { id: 'greenfield', name: 'Greenfield', icon: 'map', color: 'green' },
  { id: 'railway', name: 'Railway Corridor', icon: 'train', color: 'slate' },
  { id: 'bus_station', name: 'Bus Station Area', icon: 'bus', color: 'sky' },
  { id: 'harbour', name: 'Harbour / Port Area', icon: 'anchor', color: 'indigo' },
  { id: 'reservoir', name: 'Reservoir', icon: 'droplets', color: 'blue' },
  { id: 'basin', name: 'Basin', icon: 'circle', color: 'slate' },
  { id: 'car_park', name: 'Car Park', icon: 'car', color: 'slate' },
  { id: 'parking', name: 'Parking Area', icon: 'parking-circle', color: 'slate' },
  { id: 'garages', name: 'Garages / Parking Structures', icon: 'warehouse', color: 'gray' },
  { id: 'fuel', name: 'Fuel Station Area', icon: 'fuel', color: 'red' },
  { id: 'car_repair', name: 'Car Repair Yard', icon: 'wrench', color: 'stone' },
  { id: 'hotel', name: 'Hotel Grounds', icon: 'hotel', color: 'violet' },
  { id: 'hostel', name: 'Hostel Grounds', icon: 'bed-single', color: 'violet' },
  // Extractive & waste
  { id: 'quarry', name: 'Quarry / Mining', icon: 'pickaxe', color: 'stone' },
  { id: 'rock', name: 'Rock / Exposed Stone Area', icon: 'mountain', color: 'gray' },
  { id: 'sand', name: 'Sand / Quarry Sand', icon: 'sun', color: 'amber' },
  { id: 'landfill', name: 'Landfill / Dump', icon: 'trash-2', color: 'gray' },
  { id: 'dump', name: 'Dump / Waste Pile', icon: 'trash-2', color: 'gray' },
  { id: 'logging', name: 'Logging / Forestry Operation', icon: 'axe', color: 'amber' },
  { id: 'salt_pond', name: 'Salt Pond / Salt Pan', icon: 'sun', color: 'yellow' },
];

/** Natural surface polygons (OSM natural=*). Distinct from landuse zoning. */
const LANDCOVER_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'wood', name: 'Woodland / Forest', icon: 'trees', color: 'emerald' },
  { id: 'grassland', name: 'Grassland', icon: 'sprout', color: 'green' },
  { id: 'scrub', name: 'Scrubland', icon: 'shrub', color: 'lime' },
  { id: 'shrub', name: 'Shrub', icon: 'leaf', color: 'green' },
  { id: 'heath', name: 'Heathland', icon: 'flower', color: 'purple' },
  { id: 'wetland', name: 'Wetland / Marsh', icon: 'waves', color: 'teal' },
  { id: 'water', name: 'Water', icon: 'droplets', color: 'blue' },
  { id: 'tree_row', name: 'Tree Row', icon: 'tree-pine', color: 'emerald' },
  { id: 'sand', name: 'Sand', icon: 'sun', color: 'amber' },
  { id: 'mud', name: 'Mud', icon: 'cloud-rain', color: 'stone' },
  { id: 'beach', name: 'Beach', icon: 'umbrella', color: 'yellow' },
  { id: 'shingle', name: 'Shingle / Pebble Shore', icon: 'circle-dot', color: 'stone' },
  { id: 'bare_rock', name: 'Bare Rock', icon: 'mountain', color: 'gray' },
  { id: 'cliff', name: 'Cliff / Escarpment', icon: 'triangle', color: 'stone' },
  { id: 'ridge', name: 'Ridge', icon: 'mountain', color: 'slate' },
  { id: 'peak', name: 'Peak / Hilltop', icon: 'mountain-snow', color: 'gray' },
  { id: 'arete', name: 'Arete / Sharp Ridge', icon: 'triangle', color: 'slate' },
  { id: 'mountain_range', name: 'Mountain Range', icon: 'layers', color: 'slate' },
  { id: 'land', name: 'Land / Peninsula', icon: 'land-plot', color: 'amber' },
];

const STRUCTURE_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'wall', name: 'Wall', icon: 'brick-wall', color: 'stone' },
  { id: 'fence', name: 'Fence', icon: 'fence', color: 'amber' },
  { id: 'gate', name: 'Gate', icon: 'door-open', color: 'blue' },
  { id: 'lift_gate', name: 'Lift Gate / Boom Barrier', icon: 'minus', color: 'orange' },
  { id: 'hedge', name: 'Hedge', icon: 'shrub', color: 'green' },
  { id: 'retaining_wall', name: 'Retaining Wall', icon: 'layers', color: 'gray' },
  { id: 'city_wall', name: 'City Wall', icon: 'castle', color: 'slate' },
  { id: 'bollard', name: 'Bollard', icon: 'cylinder', color: 'orange' },
  { id: 'guard_rail', name: 'Guard Rail', icon: 'minus', color: 'red' },
  { id: 'ditch', name: 'Ditch', icon: 'move-horizontal', color: 'teal' },
  { id: 'block', name: 'Block / Barrier', icon: 'square', color: 'gray' },
  { id: 'entrance', name: 'Entrance', icon: 'log-in', color: 'blue' },
  { id: 'water_well', name: 'Water Well', icon: 'droplet', color: 'blue' },
  { id: 'utility_pole', name: 'Utility Pole (Light + Camera)', icon: 'lamp', color: 'yellow' },
];

const TRAFFIC_SIGNAL_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'traffic_signals', name: 'Traffic Light', icon: 'traffic-cone', color: 'red' },
  { id: 'street_lamp', name: 'Street Light', icon: 'lamp', color: 'yellow' },
  { id: 'speed_camera', name: 'Speed Camera', icon: 'camera', color: 'teal' },
  { id: 'camera_traffic', name: 'Camera + Traffic Pole', icon: 'cctv', color: 'orange' },
  { id: 'crossing_signals', name: 'Signalized Crossing', icon: 'footprints', color: 'amber' },
  { id: 'crossing_traffic', name: 'Crossing + Traffic Pole', icon: 'traffic-cone', color: 'orange' },
];

const AEROWAY_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'aerodrome', name: 'Airport / Aerodrome', icon: 'plane', color: 'blue' },
  { id: 'runway', name: 'Runway', icon: 'plane-takeoff', color: 'indigo' },
  { id: 'taxiway', name: 'Taxiway', icon: 'route', color: 'slate' },
  { id: 'taxiway_centerline', name: 'Taxiway Centreline', icon: 'route', color: 'slate' },
  { id: 'apron', name: 'Apron / Ramp', icon: 'square', color: 'gray' },
  { id: 'helipad', name: 'Helipad', icon: 'helicopter', color: 'teal' },
  { id: 'terminal', name: 'Terminal', icon: 'building-2', color: 'violet' },
  { id: 'hangar', name: 'Hangar', icon: 'warehouse', color: 'stone' },
  { id: 'jet_bridge', name: 'Jet Bridge', icon: 'link', color: 'sky' },
  { id: 'gate', name: 'Gate', icon: 'door-open', color: 'amber' },
  { id: 'parking_position', name: 'Parking Position', icon: 'parking-circle', color: 'orange' },
  { id: 'holding_position', name: 'Holding Position', icon: 'circle-pause', color: 'yellow' },
  { id: 'stopway', name: 'Stopway', icon: 'octagon', color: 'red' },
  { id: 'displaced_threshold', name: 'Displaced Threshold', icon: 'move-horizontal', color: 'stone' },
  { id: 'windsock', name: 'Windsock', icon: 'wind', color: 'cyan' },
  { id: 'navigationaid', name: 'Navigation Aid', icon: 'radio', color: 'indigo' },
];

const WATER_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'lake', name: 'Lake', icon: 'waves', color: 'blue' },
  { id: 'pond', name: 'Pond', icon: 'droplets', color: 'cyan' },
  { id: 'water', name: 'Water (general)', icon: 'droplets', color: 'blue' },
  { id: 'river', name: 'River', icon: 'waves', color: 'blue' },
  { id: 'stream', name: 'Stream', icon: 'wind', color: 'cyan' },
  { id: 'reservoir', name: 'Reservoir', icon: 'database', color: 'indigo' },
  { id: 'lagoon', name: 'Lagoon', icon: 'palmtree', color: 'teal' },
  { id: 'canal', name: 'Canal', icon: 'move-horizontal', color: 'blue' },
  { id: 'dam', name: 'Dam', icon: 'blocks', color: 'gray' },
  { id: 'basin', name: 'Basin', icon: 'circle', color: 'slate' },
  { id: 'harbour', name: 'Harbour', icon: 'anchor', color: 'indigo' },
  { id: 'oxbow', name: 'Oxbow Lake', icon: 'rotate-ccw', color: 'blue' },
  { id: 'lock', name: 'Lock', icon: 'lock', color: 'slate' },
  { id: 'ditch', name: 'Ditch / Drainage Pond', icon: 'minus', color: 'gray' },
  { id: 'wastewater', name: 'Wastewater / Treatment Pond', icon: 'recycle', color: 'stone' },
];

const WATERWAY_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'river', name: 'River', icon: 'waves', color: 'blue' },
  { id: 'stream', name: 'Stream', icon: 'wind', color: 'cyan' },
  { id: 'canal', name: 'Canal', icon: 'move-horizontal', color: 'blue' },
  { id: 'drain', name: 'Drain', icon: 'pipette', color: 'gray' },
  { id: 'ditch', name: 'Ditch', icon: 'minus', color: 'slate' },
  { id: 'dam', name: 'Dam', icon: 'blocks', color: 'gray' },
  { id: 'waterfall', name: 'Waterfall', icon: 'cloud-rain', color: 'blue' },
  { id: 'dock', name: 'Dock', icon: 'anchor', color: 'indigo' },
  { id: 'weir', name: 'Weir', icon: 'waves', color: 'slate' },
  { id: 'lock_gate', name: 'Lock Gate', icon: 'lock', color: 'slate' },
];

const BUILDING_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'residential', name: 'Residential', icon: 'home', color: 'blue' },
  { id: 'apartments', name: 'Apartments', icon: 'building-2', color: 'indigo' },
  { id: 'commercial', name: 'Commercial', icon: 'store', color: 'orange' },
  { id: 'industrial', name: 'Industrial', icon: 'factory', color: 'gray' },
  { id: 'school', name: 'School', icon: 'graduation-cap', color: 'indigo' },
  { id: 'hospital', name: 'Hospital', icon: 'hospital', color: 'red' },
  { id: 'church', name: 'Church', icon: 'church', color: 'purple' },
  { id: 'mosque', name: 'Mosque', icon: 'landmark', color: 'green' },
  { id: 'hotel', name: 'Hotel / Hostel', icon: 'bed', color: 'teal' },
  { id: 'office', name: 'Office', icon: 'briefcase', color: 'slate' },
  { id: 'retail', name: 'Shop / Retail', icon: 'shopping-bag', color: 'pink' },
  { id: 'warehouse', name: 'Warehouse', icon: 'package', color: 'stone' },
  { id: 'government', name: 'Government', icon: 'landmark', color: 'blue' },
  { id: 'farm', name: 'Farm Building', icon: 'tractor', color: 'amber' },
  { id: 'garage', name: 'Garage', icon: 'car', color: 'gray' },
  { id: 'construction', name: 'Under Construction', icon: 'hard-hat', color: 'yellow' },
];

/** Hints synced from docs/data-collection/COORDAL_ROAD_CLASSIFICATION_POLICY.md §9 */
const ROAD_CATEGORIES: ContributionCategoryOption[] = [
  // Major corridors (GHA / DFR)
  {
    id: 'motorway',
    name: 'Motorway',
    icon: 'navigation-2',
    color: 'red',
    hint: 'Grade-separated highway, limited access, no at-grade crossings (e.g. Accra-Tema)',
  },
  {
    id: 'trunk',
    name: 'Trunk Road',
    icon: 'route',
    color: 'red',
    hint: 'National corridor (N-road), e.g. N6 Accra-Kumasi, put ref in ref field',
  },
  {
    id: 'primary',
    name: 'Primary Road',
    icon: 'route',
    color: 'orange',
    hint: 'Inter-regional route (IR) linking major centres, e.g. IR4',
  },
  {
    id: 'secondary',
    name: 'Secondary Road',
    icon: 'route',
    color: 'amber',
    hint: 'Major regional route (R10-R99) or urban arterial, put R ref if signed',
  },
  {
    id: 'tertiary',
    name: 'Tertiary Road',
    icon: 'route',
    color: 'yellow',
    hint: 'Minor regional (R100+) or urban collector road',
  },
  // Local streets (DUR / rural)
  {
    id: 'residential',
    name: 'Residential Street',
    icon: 'home',
    color: 'blue',
    hint: 'Neighbourhood street with homes or shops along it, inside a town or estate',
  },
  {
    id: 'living_street',
    name: 'Living Street',
    icon: 'users',
    color: 'sky',
    hint: 'Shared space, pedestrians have priority, slow traffic OK (~20 km/h zones)',
  },
  {
    id: 'unclassified',
    name: 'Unclassified Road',
    icon: 'route',
    color: 'gray',
    hint: 'Connects villages or areas, rural feeder, not a neighbourhood street',
  },
  {
    id: 'service',
    name: 'Service Road',
    icon: 'move-horizontal',
    color: 'slate',
    hint: 'Car park aisle, driveway, or access to one building or site',
  },
  {
    id: 'track',
    name: 'Track / Unpaved',
    icon: 'mountain',
    color: 'stone',
    hint: 'Farm, forest, or field access, not a public street for addressing',
  },
  // Pedestrian & cycle ways
  {
    id: 'footway',
    name: 'Footway / Sidewalk',
    icon: 'footprints',
    color: 'green',
    hint: 'Paved sidewalk or kerb-side pedestrian way — name optional; map shows Footway if unnamed',
  },
  {
    id: 'pedestrian',
    name: 'Pedestrian Street',
    icon: 'person-standing',
    color: 'emerald',
    hint: 'Car-free street or plaza, walkers only, no regular vehicle access',
  },
  {
    id: 'path',
    name: 'Path',
    icon: 'trending-up',
    color: 'lime',
    hint: 'Unpaved walking trail — name optional; map shows Path if unnamed (Road Labels layer on)',
  },
  {
    id: 'steps',
    name: 'Steps / Stairs',
    icon: 'arrow-up-down',
    color: 'cyan',
    hint: 'Outdoor stairs or stepped path between levels',
  },
  {
    id: 'cycleway',
    name: 'Cycleway',
    icon: 'bike',
    color: 'teal',
    hint: 'Dedicated bicycle lane or path',
  },
  {
    id: 'shoulder',
    name: 'Shoulder',
    icon: 'move-horizontal',
    color: 'stone',
    hint: 'Model B — trace a standalone shoulder strip (emergency/breakdown edge, not tagged on a motor road)',
  },
  {
    id: 'parking_lane',
    name: 'Parking Lane',
    icon: 'parking-circle',
    color: 'slate',
    hint: 'Model B — trace kerbside parking bays or a lane detached from a continuous motor-road tag',
  },
  {
    id: 'pedestrian_crossing',
    name: 'Pedestrian Crossing / Zebra',
    icon: 'footprints',
    color: 'amber',
    hint: 'Marked or zebra crossing, sets crossing_type for map styling',
  },
  // Temporary / other
  {
    id: 'construction',
    name: 'Under Construction',
    icon: 'hard-hat',
    color: 'yellow',
    hint: 'Road being built or closed, trace the planned alignment if visible',
  },
];

const LANDMARK_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'monument', name: 'Monument', icon: 'milestone', color: 'slate' },
  { id: 'memorial', name: 'Memorial', icon: 'flower-2', color: 'purple' },
  { id: 'castle', name: 'Castle / Fort', icon: 'castle', color: 'amber' },
  { id: 'aircraft', name: 'Historic Aircraft', icon: 'plane', color: 'sky' },
  { id: 'ruins', name: 'Ruins', icon: 'ruin', color: 'stone' },
  { id: 'tomb', name: 'Tomb / Shrine', icon: 'cross', color: 'gray' },
  { id: 'boundary_stone', name: 'Boundary Stone', icon: 'map-pin', color: 'slate' },
  { id: 'wayside_cross', name: 'Wayside Cross', icon: 'cross', color: 'purple' },
  { id: 'wayside_shrine', name: 'Wayside Shrine', icon: 'sparkles', color: 'amber' },
  { id: 'manor', name: 'Manor / Palace', icon: 'crown', color: 'amber' },
];

const PLACE_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'city', name: 'City', icon: 'building-2', color: 'blue' },
  { id: 'town', name: 'Town', icon: 'home', color: 'amber' },
  { id: 'village', name: 'Village', icon: 'trees', color: 'green' },
  { id: 'hamlet', name: 'Hamlet', icon: 'house', color: 'lime' },
  { id: 'suburb', name: 'Suburb', icon: 'map-pin', color: 'sky' },
  { id: 'neighbourhood', name: 'Neighbourhood', icon: 'map', color: 'cyan' },
  { id: 'quarter', name: 'Quarter', icon: 'grid-2x2', color: 'slate' },
  { id: 'locality', name: 'Locality', icon: 'map-pin', color: 'gray' },
];

const TREE_CATEGORIES: ContributionCategoryOption[] = [
  { id: 'mango', name: 'Mango', icon: 'tree-deciduous', color: 'amber' },
  { id: 'coconut', name: 'Coconut Palm', icon: 'palmtree', color: 'green' },
  { id: 'oil_palm', name: 'Oil Palm', icon: 'palmtree', color: 'emerald' },
  { id: 'neem', name: 'Neem', icon: 'tree-pine', color: 'green' },
  { id: 'mahogany', name: 'Mahogany', icon: 'tree-deciduous', color: 'amber' },
  { id: 'baobab', name: 'Baobab', icon: 'tree-deciduous', color: 'stone' },
  { id: 'shea', name: 'Shea', icon: 'tree-deciduous', color: 'lime' },
  { id: 'teak', name: 'Teak', icon: 'tree-pine', color: 'emerald' },
  { id: 'other', name: 'Other / Unknown', icon: 'tree-pine', color: 'gray' },
];

const LANDMARK_SOURCE_GROUPS: { id: string; name: string; icon: string; color: string; categories: ContributionCategoryOption[] | 'poi_api' }[] = [
  { id: 'historic', name: 'Historic & Monument', icon: 'landmark', color: 'purple', categories: LANDMARK_CATEGORIES },
  { id: 'poi', name: 'Place / Business', icon: 'map-pin', color: 'orange', categories: 'poi_api' },
  { id: 'water', name: 'Water Body', icon: 'droplets', color: 'blue', categories: WATER_CATEGORIES },
  { id: 'structure', name: 'Structure', icon: 'construction', color: 'stone', categories: STRUCTURE_CATEGORIES },
  { id: 'transport', name: 'Transport Stop', icon: 'bus', color: 'sky', categories: TRANSPORT_STOP_CATEGORIES },
  { id: 'landuse', name: 'Land Use', icon: 'trees', color: 'green', categories: LANDUSE_CATEGORIES },
  { id: 'landcover', name: 'Land Cover', icon: 'mountain', color: 'emerald', categories: LANDCOVER_CATEGORIES },
];

/** Per-group geometry modes when adding a new landmark (not enrich). */
const LANDMARK_GROUP_CONFIG: Record<string, { newModes: GeometryMode[] }> = {
  historic: { newModes: ['point'] },
  poi: { newModes: ['point'] },
  water: { newModes: ['draw', 'trace'] },
  structure: { newModes: ['point', 'draw', 'trace'] },
  transport: { newModes: ['point'] },
  landuse: { newModes: ['draw', 'trace'] },
  landcover: { newModes: ['draw', 'trace'] },
};

function landmarkNewGeometryModes(groupId: string | null | undefined): GeometryMode[] {
  if (!groupId) return ['point'];
  return LANDMARK_GROUP_CONFIG[groupId]?.newModes ?? ['point'];
}

const POINT_PLACEMENT_MODES: GeometryMode[] = ['point', 'coordinates', 'link'];

function featureSupportsPointPlacement(ft: FeatureTypeOption | null): boolean {
  return Boolean(ft?.allowedModes.includes('point'));
}

function isPointOnlyFeature(ft: FeatureTypeOption | null): boolean {
  return Boolean(ft && ft.allowedModes.length === 1 && ft.allowedModes[0] === 'point');
}

function effectivePlacementModes(
  ft: FeatureTypeOption | null,
  landmarkGroup?: string | null,
  forLandmarkModeStep = false,
): GeometryMode[] {
  if (!ft) return ['point', 'draw', 'trace'];
  if (ft.id === 'landmark' && landmarkGroup && forLandmarkModeStep) {
    const modes = landmarkNewGeometryModes(landmarkGroup);
    if (modes.includes('point')) {
      return [...new Set([...modes, 'coordinates', 'link'] as GeometryMode[])];
    }
    return modes;
  }
  const base = ft.allowedModes;
  if (base.includes('point')) {
    return [...new Set([...base, 'coordinates', 'link'] as GeometryMode[])];
  }
  return base;
}

function isPointPlacementOnlyModes(modes: GeometryMode[]): boolean {
  return modes.length > 0 && modes.every((mode) => POINT_PLACEMENT_MODES.includes(mode));
}

const FEATURE_TYPE_CATEGORIES: Record<string, ContributionCategoryOption[]> = {
  place: PLACE_CATEGORIES,
  building: BUILDING_CATEGORIES,
  road: ROAD_CATEGORIES,
  landuse: LANDUSE_CATEGORIES,
  landcover: LANDCOVER_CATEGORIES,
  structure: STRUCTURE_CATEGORIES,
  traffic_signal: TRAFFIC_SIGNAL_CATEGORIES,
  water: WATER_CATEGORIES,
  waterway: WATERWAY_CATEGORIES,
  landmark: LANDMARK_CATEGORIES,
  tree: TREE_CATEGORIES,
  aeroway: AEROWAY_CATEGORIES,
};

function landmarkCategoryFeatureType(groupId: string | null | undefined): string | undefined {
  if (!groupId) return 'landmark';
  const map: Record<string, string> = {
    historic: 'landmark',
    poi: 'poi',
    water: 'water',
    structure: 'structure',
    transport: 'transport_stop',
    landuse: 'landuse',
    landcover: 'landcover',
  };
  return map[groupId];
}

function flattenApiCategories(cats: ApiCategory[]): ContributionCategoryOption[] {
  const out: ContributionCategoryOption[] = [];
  for (const c of cats) {
    for (const sc of c.subcategories || []) {
      out.push({
        id: sc.id,
        name: sc.name,
        icon: sc.icon,
        color: sc.color,
      });
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

interface AddLocationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onEnableMapClick: (enabled: boolean) => void;
  onEnableDrawing: (enabled: boolean, options?: { lineMode?: boolean }) => void;
  selectedLocation?: { lat: number; lng: number } | null;
  drawnGeometry?: GeoJSON.Geometry | null;
  isTracing?: boolean;
  tracePoints?: Array<{ lat: number; lng: number }>;
  onStartTrace: () => void;
  onStopTrace: () => void;
  onUndoTracePoint?: () => void;
  onUndoFirstTracePoint?: () => void;
  onClearGeometry: (options?: { keepTrace?: boolean }) => void;
  traceDraftFeatureTypeId?: string | null;
  onTraceContextChange?: (context: {
    featureTypeId: string | null;
    landmarkGroup: string | null;
  }) => void;
  tracePostProcessSummary?: string | null;
  traceSamplingMode?: TraceSamplingMode;
  traceMinDistanceM?: TraceDistanceOptionM;
  onTraceSamplingModeChange?: (mode: TraceSamplingMode) => void;
  onTraceMinDistanceMChange?: (distanceM: TraceDistanceOptionM) => void;
  traceResampleOnStop?: boolean;
  onTraceResampleOnStopChange?: (enabled: boolean) => void;
  traceCaptureMode?: TraceCaptureMode;
  onTraceCaptureModeChange?: (mode: TraceCaptureMode) => void;
  onAddTraceCorner?: () => Promise<void>;
  isAddingTraceCorner?: boolean;
  onThinTrace?: () => void;
  onResampleFinerTrace?: () => void;
  traceThinSpacingM?: TraceDistanceOptionM;
  traceFinerSpacingM?: TraceDistanceOptionM | null;
  tracePathModified?: boolean;
  onResetTracePath?: () => void;
  /** When true, show Reset path (returns to raw GPS when available). */
  canResetTracePath?: boolean;
  onRestoreRawTrace?: () => void;
  canRestoreRawTrace?: boolean;
  selectedTraceVertexIndex?: number | null;
  onDeleteSelectedTraceVertex?: () => void;
  onTraceVertexEditActiveChange?: (active: boolean) => void;
  /** Continuous trace refine (Start/End labels): step undo/redo, not drop endpoints. */
  onTraceEditUndo?: () => void;
  onTraceEditRedo?: () => void;
  traceEditCanUndo?: boolean;
  traceEditCanRedo?: boolean;
  onPreviewGeometry?: (geometry: GeoJSONGeometry | null) => void;
  onPreviewGuideGeometries?: (
    guides: Array<{ side: DividedCarriagewayGuideSide; geometry: GeoJSON.LineString }> | null,
  ) => void;
  onTraceRingModeChange?: (ringMode: boolean) => void;
  onDrawTrash?: () => boolean;
  onDrawUndo?: () => boolean;
  onDrawUndoFirst?: () => boolean;
  /** Push existing draw/trace into map layers when reopening marking (e.g. edit contribution). */
  onHydrateMarkingGeometry?: (payload: {
    geometryMode: 'draw' | 'trace' | 'point';
    drawnGeometry: GeoJSON.Geometry | null;
    tracePoints: Array<{ lat: number; lng: number }>;
    markingFeatureType: string | null;
  }) => void;
  onSetLocation?: (location: { lat: number; lng: number }) => void;
  onFlyTo?: (center: { lat: number; lng: number }, zoom: number) => void;
  onPlaceMarker?: (lat: number, lng: number) => void;
  onLockZoom?: (zoom: number) => void;
  onUnlockZoom?: () => void;
  editContribution?: ContributionDetail | null;
  suggestEditForPlace?: Place | null;
  onDismissSuggestEdit?: () => void;
  onHighlightGeometry?: (geojson: GeoJSON.Geometry) => void;
  onClearHighlight?: () => void;
  campaignId?: string | null;
  zoneId?: string | null;
  /** When true, live session GPS is pre-captured; point features skip the marking step. */
  campaignFieldCapture?: boolean;
  userLocation?: { lat: number; lng: number; accuracyM?: number | null } | null;
  onUseMyLocation?: () => Promise<void>;
  /** Set parent draw state when enclosure geometry is confirmed (skip re-draw). */
  onSetDrawnGeometry?: (geometry: GeoJSON.Geometry | null) => void;
  roadSnappingEnabled?: boolean;
  onRoadSnappingEnabledChange?: (enabled: boolean) => void;
  snapStatusMessage?: string | null;
  roadSnapConnections?: Array<Record<string, unknown>>;
  onDrawEnterVertexEdit?: () => boolean;
  onRemoveTraceOutliers?: () => void;
  onStraightenTrace?: () => void;
  onSnapEndpointsNow?: (context: {
    featureTypeId: string | null;
    geometryMode: 'draw' | 'trace';
  }) => void;
  /** Keep map undo/trash FAB visible on marking step while draw tools are active. */
  onMarkingDrawFabActive?: (active: boolean) => void;
  /** Refresh dashed pending-survey overlay after a successful submit. */
  onPendingSurveyRefresh?: () => void;
}

export function AddLocationPanel({
  isOpen,
  onClose,
  onEnableMapClick,
  onEnableDrawing,
  selectedLocation,
  drawnGeometry,
  isTracing = false,
  tracePoints = [],
  onStartTrace,
  onStopTrace,
  onUndoTracePoint,
  onUndoFirstTracePoint,
  onClearGeometry,
  traceDraftFeatureTypeId = null,
  onTraceContextChange,
  tracePostProcessSummary = null,
  traceSamplingMode = 'distance',
  traceMinDistanceM = DEFAULT_TRACE_MIN_DISTANCE_M,
  onTraceSamplingModeChange,
  onTraceMinDistanceMChange,
  traceResampleOnStop = false,
  onTraceResampleOnStopChange,
  traceCaptureMode = 'continuous',
  onTraceCaptureModeChange,
  onAddTraceCorner,
  isAddingTraceCorner = false,
  onThinTrace,
  onResampleFinerTrace,
  traceThinSpacingM = 10,
  traceFinerSpacingM = null,
  tracePathModified: _tracePathModified = false,
  onResetTracePath,
  canResetTracePath = false,
  onRestoreRawTrace: _onRestoreRawTrace,
  canRestoreRawTrace: _canRestoreRawTrace = false,
  selectedTraceVertexIndex = null,
  onDeleteSelectedTraceVertex,
  onTraceVertexEditActiveChange,
  onTraceEditUndo,
  onTraceEditRedo,
  traceEditCanUndo = false,
  traceEditCanRedo = false,
  onPreviewGeometry,
  onPreviewGuideGeometries,
  onTraceRingModeChange,
  onDrawTrash,
  onDrawUndo,
  onDrawUndoFirst,
  onHydrateMarkingGeometry,
  onSetLocation,
  onFlyTo,
  onPlaceMarker,
  onLockZoom,
  onUnlockZoom,
  editContribution,
  suggestEditForPlace,
  onDismissSuggestEdit,
  onHighlightGeometry,
  onClearHighlight,
  campaignId,
  zoneId,
  campaignFieldCapture = false,
  userLocation = null,
  onUseMyLocation,
  onSetDrawnGeometry,
  roadSnappingEnabled = true,
  onRoadSnappingEnabledChange,
  snapStatusMessage = null,
  roadSnapConnections = [],
  onDrawEnterVertexEdit,
  onRemoveTraceOutliers,
  onStraightenTrace,
  onSnapEndpointsNow,
  onMarkingDrawFabActive,
  onPendingSurveyRefresh,
}: AddLocationPanelProps) {
  const { isAuthenticated, requireAuth } = useAuthStore();
  const roadSnapBandSettings = useSettingsStore((s) => ({
    roadSnapAutoRadiusM: s.settings.roadSnapAutoRadiusM,
    roadSnapSearchRadiusM: s.settings.roadSnapSearchRadiusM,
  }));
  const roadSnapBandsHint = useMemo(
    () => roadSnapBandsFromSettings(roadSnapBandSettings),
    [roadSnapBandSettings.roadSnapAutoRadiusM, roadSnapBandSettings.roadSnapSearchRadiusM],
  );
  const [step, setStep] = useState<ContributionStep>('feature_type');
  const [selectedFeatureType, setSelectedFeatureType] = useState<FeatureTypeOption | null>(null);
  const [geometryMode, setGeometryMode] = useState<GeometryMode | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationPlacementError, setLocationPlacementError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [facebook, setFacebook] = useState('');
  const [instagram, setInstagram] = useState('');
  const [openingHours, setOpeningHours] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionSourceUrl, setDescriptionSourceUrl] = useState('');
  const [photoDataUrls, setPhotoDataUrls] = useState<string[]>([]);
  const [matchingExisting, setMatchingExisting] = useState(false);


  const roadDetails = useRoadDetailsState();
  const {
    roadSurface,
    setRoadSurface,
    roadSmoothness,
    setRoadSmoothness,
    roadCarriagewayCount,
    setRoadCarriagewayCount,
    roadLanes,
    setRoadLanes,
    roadLanesForward,
    setRoadLanesForward,
    roadLanesBackward,
    setRoadLanesBackward,
    roadLaneDirections,
    setRoadLaneDirections,
    roadUsePerLaneWidths,
    roadLaneWidthsM,
    roadCarriagewayALaneWidthsM,
    roadCarriagewayBLaneWidthsM,
    roadManualLaneDirections,
    setRoadManualLaneDirections,
    roadCarriagewayALaneDirections,
    setRoadCarriagewayALaneDirections,
    roadCarriagewayBLaneDirections,
    setRoadCarriagewayBLaneDirections,
    roadManualDividedLaneDirections,
    setRoadManualDividedLaneDirections,
    roadToll,
    setRoadToll,
    roadFloodProne,
    setRoadFloodProne,
    roadPotholes,
    setRoadPotholes,
    roadStreetVendors,
    setRoadStreetVendors,
    roadAccidentProne,
    setRoadAccidentProne,
    roadHawkers,
    setRoadHawkers,
    roadLit,
    setRoadLit,
    roadDrainage,
    setRoadDrainage,
    roadMaxspeed,
    setRoadMaxspeed,
    roadCrossingType,
    setRoadCrossingType,
    roadWidth,
    setRoadWidth,
    roadCarriagewayAWidth,
    setRoadCarriagewayAWidth,
    roadCarriagewayBWidth,
    setRoadCarriagewayBWidth,
    roadSideFacilities,
    setRoadSideFacilities,
    resetRoadDetailsWidths,
  } = roadDetails;
  const [treeHeight, setTreeHeight] = useState('');
  const [aerowayRef, setAerowayRef] = useState('');
  const [aerowaySurface, setAerowaySurface] = useState('');
  const [aerowayWidth, setAerowayWidth] = useState('');
  const [junctionRef, setJunctionRef] = useState('');
  const [junctionExitRoads, setJunctionExitRoads] = useState<JunctionExitRoad[]>([]);
  const [turnFromRoad, setTurnFromRoad] = useState<{
    roadId: number;
    name: string;
    endpoint: string;
  } | null>(null);
  const [turnToRoad, setTurnToRoad] = useState<{
    roadId: number;
    name: string;
    endpoint: string;
  } | null>(null);
  const [turnRestrictionType, setTurnRestrictionType] = useState<
    'no_left_turn' | 'no_right_turn' | 'no_u_turn' | 'no_straight_on' | ''
  >('');
  const [stopRef, setStopRef] = useState('');
  const [stopRoutes, setStopRoutes] = useState<string[]>([]);
  const [stopOperators, setStopOperators] = useState<string[]>([]);
  const [stopShelter, setStopShelter] = useState(false);
  const [stopBench, setStopBench] = useState(false);
  const [stopLighting, setStopLighting] = useState(false);
  const [poiCuisine, setPoiCuisine] = useState('');
  const [poiBrand, setPoiBrand] = useState('');
  const [poiWheelchair, setPoiWheelchair] = useState('');
  const [paymentMobileMoney, setPaymentMobileMoney] = useState(false);
  const [paymentCard, setPaymentCard] = useState(false);
  const [mobileMoneyProvider, setMobileMoneyProvider] = useState('');

  const [categoryOptions, setCategoryOptions] = useState<ContributionCategoryOption[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [addrStreet, setAddrStreet] = useState('');
  const [addrSuburb, setAddrSuburb] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrDistrict, setAddrDistrict] = useState('');
  const [addrRegion, setAddrRegion] = useState('');
  const [ghanaPostGps, setGhanaPostGps] = useState('');
  const [hostHouseNumber, setHostHouseNumber] = useState('');
  const [hostFrontageStreetAddress, setHostFrontageStreetAddress] = useState('');

  // Building units
  const [buildingUnits, setBuildingUnits] = useState<BuildingUnit[]>([]);
  const [unitInput, setUnitInput] = useState('');
  const [unitBatchFloor, setUnitBatchFloor] = useState('1');
  const [autoNumberCount, setAutoNumberCount] = useState('');
  const [autoNumberStart, setAutoNumberStart] = useState('1');
  const [unitMode, setUnitMode] = useState<'manual' | 'auto'>('manual');
  const floorBatchDraftsRef = useRef<Record<string, FloorBatchDraft>>({});
  const lastActiveFloorRef = useRef('1');
  const [addressLookupStatus, setAddressLookupStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [addressLookupKey, setAddressLookupKey] = useState(0);
  const [suggestedAddressFields, setSuggestedAddressFields] = useState<string[]>([]);

  // Sheet expand/collapse
  const [sheetSnap, setSheetSnap] = useState<ContributionSheetSnap>('half');
  const setContributionSheetSnap = useMapStore((s) => s.setContributionSheetSnap);
  const setContributionSheetChromeBottom = useMapStore((s) => s.setContributionSheetChromeBottom);
  const sheetRef = useRef<HTMLDivElement>(null);
  const sheetControls = useAnimation();
  const sheetDragControls = useDragControls();
  const sheetDragging = useRef(false);
  const editPrefillKeyRef = useRef<string | null>(null);

  const ADD_DETAILS_SHEET_HEIGHTS: Record<ContributionSheetSnap, string> = {
    minimized: 'calc(6.25rem + env(safe-area-inset-bottom, 0px))',
    compact: '42vh',
    half: '65vh',
    expanded: '88vh',
  };

  const ADD_DETAILS_SHEET_SNAP: Record<ContributionSheetSnap, number> = {
    minimized: 0.1,
    compact: 0.42,
    half: 0.65,
    expanded: 0.88,
  };

  /** Steps with short content: shrink sheet to fit instead of fixed half/expanded height. */
  const COMPACT_SHEET_STEPS = new Set([
    'mode',
    'landmark_group',
    'landmark_placement',
    'road_layout',
    'road_capture_plan',
    'enclosure_method',
    'enclosure_capture_mode',
    'enclosure_feature_type',
  ]);

  const motorRoadSurveyFlow = usesMotorRoadSurveyFlow(selectedFeatureType?.id, selectedCategory);

  const COMPACT_SHEET_MAX_HEIGHT: Partial<Record<ContributionStep, string>> = {
    road_capture_plan: '72vh',
  };

  /** Divided capture plan needs two strip blocks — use snap height + scroll, not 40vh content-fit. */
  const usesCompactContributionSheet = useCallback(
    (currentStep: ContributionStep) => {
      if (!COMPACT_SHEET_STEPS.has(currentStep)) return false;
      if (currentStep === 'road_capture_plan' && (roadCarriagewayCount ?? 1) >= 2) return false;
      return true;
    },
    [roadCarriagewayCount],
  );

  const isCompactSheetStep = usesCompactContributionSheet(step);

  const resolveSheetHeight = (currentStep: typeof step, snap: typeof sheetSnap) => {
    if (usesCompactContributionSheet(currentStep)) return 'auto';
    return ADD_DETAILS_SHEET_HEIGHTS[snap];
  };

  const resolveSheetMaxHeight = (currentStep: typeof step): string | undefined => {
    if (!usesCompactContributionSheet(currentStep)) return undefined;
    return COMPACT_SHEET_MAX_HEIGHT[currentStep] ?? '40vh';
  };

  const SHEET_SNAP_ORDER: ContributionSheetSnap[] = ['minimized', 'compact', 'half', 'expanded'];

  useEffect(() => {
    if (!isOpen) {
      setContributionSheetSnap(null);
      return;
    }
    setSheetSnap('half');
    setContributionSheetSnap('half');
  }, [isOpen, setContributionSheetSnap]);

  useEffect(() => {
    if (!isOpen) return;
    setContributionSheetSnap(sheetSnap);
  }, [isOpen, sheetSnap, setContributionSheetSnap]);

  /** Keep zoom / GPS pills above the real sheet top (content-fit steps can exceed snap presets). */
  useLayoutEffect(() => {
    if (!isOpen) {
      setContributionSheetChromeBottom(null);
      return;
    }
    const el = sheetRef.current;
    if (!el) return;

    const CHROME_GAP_PX = 8;
    const updateChromeBottom = () => {
      const rect = el.getBoundingClientRect();
      const insetPx = Math.max(0, window.innerHeight - rect.top + CHROME_GAP_PX);
      setContributionSheetChromeBottom(`${insetPx}px`);
    };

    updateChromeBottom();
    const observer = new ResizeObserver(updateChromeBottom);
    observer.observe(el);
    window.addEventListener('resize', updateChromeBottom);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateChromeBottom);
      setContributionSheetChromeBottom(null);
    };
  }, [isOpen, step, sheetSnap, setContributionSheetChromeBottom]);

  const applySheetSnap = useCallback(
    (snap: ContributionSheetSnap) => {
      setSheetSnap(snap);
      void sheetControls.start({
        y: 0,
        transition: { type: 'spring', damping: 32, stiffness: 380 },
      });
    },
    [sheetControls],
  );

  useEffect(() => {
    if (!isOpen) return;
    void sheetControls.start({
      y: 0,
      transition: { type: 'spring', damping: 30, stiffness: 300 },
    });
  }, [isOpen, sheetSnap, sheetControls, step]);
  const campaignTraceAutoStartRef = useRef(false);

  // Coordinate input mode
  const [coordLat, setCoordLat] = useState('');
  const [coordLng, setCoordLng] = useState('');

  // Link input mode
  const [linkInput, setLinkInput] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [traceAdvancedOpen, setTraceAdvancedOpen] = useState(false);
  const [cornerCaptureError, setCornerCaptureError] = useState<string | null>(null);

  useEffect(() => {
    if (traceSamplingMode === 'always') {
      setTraceAdvancedOpen(true);
    }
  }, [traceSamplingMode]);

  // Enrich mode
  const [enrichFeature, setEnrichFeature] = useState<MapFeatureResult | null>(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const enrichTapRef = useRef<string | null>(null);

  const [enclosureMethod, setEnclosureMethod] = useState<EnclosureMethod | null>(null);
  const [enclosureDetectResult, setEnclosureDetectResult] = useState<EnclosureDetectResponse | null>(null);
  const [enclosureSelectedCandidateId, setEnclosureSelectedCandidateId] = useState<string | null>(null);
  const [enclosureLoading, setEnclosureLoading] = useState(false);
  const [enclosureFeedback, setEnclosureFeedback] = useState<string | null>(null);
  const [enclosureBoundaryRoadIds, setEnclosureBoundaryRoadIds] = useState<number[]>([]);
  const [enclosurePreviewAreaSqm, setEnclosurePreviewAreaSqm] = useState<number | null>(null);
  const [enclosureConfirmedGeometry, setEnclosureConfirmedGeometry] = useState<GeoJSONGeometry | null>(null);
  const enclosureTapRef = useRef<string | null>(null);
  const enclosureFromDetectionRef = useRef(false);
  const enclosureBoundaryTapRef = useRef<string | null>(null);

  // Official area (GSS metadata-only) flow
  const [officialLevel, setOfficialLevel] = useState<OfficialAreaLevel>('region');
  const [officialUpdateIntent, setOfficialUpdateIntent] = useState<MetadataUpdateIntent>('description');
  const [officialBoundaryName, setOfficialBoundaryName] = useState('');
  const [officialFilterRegion, setOfficialFilterRegion] = useState('');
  const [officialSelectLoading, setOfficialSelectLoading] = useState(false);

  // Category search
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedLandmarkGroup, setSelectedLandmarkGroup] = useState<string | null>(null);

  const fetchRegions = useCallback((q: string) => listRegions(q.trim() || undefined), []);

  const fetchDistricts = useCallback(
    async (q: string) => {
      const query = q.trim();
      if (!addrRegion?.trim() && !query) return [];
      return listDistricts(addrRegion.trim() || undefined, query || undefined);
    },
    [addrRegion]
  );

  const fetchTowns = useCallback(
    (q: string) =>
      searchTowns(q.trim(), {
        district: addrDistrict || undefined,
      }),
    [addrDistrict]
  );

  const fetchSuburbs = useCallback(
    (q: string) =>
      searchSuburbs(q.trim(), {
        district: addrDistrict || undefined,
        city: addrCity || undefined,
      }),
    [addrDistrict, addrCity]
  );

  const fetchOfficialRegions = useCallback(
    (q: string) => listRegions(q.trim() || undefined),
    [],
  );

  const fetchOfficialDistricts = useCallback(
    (q: string) =>
      listDistricts(officialFilterRegion.trim() || undefined, q.trim() || undefined),
    [officialFilterRegion],
  );

  const isOfficialAreaFlow = selectedFeatureType?.id === 'official_area';
  const isPlaceFeature = selectedFeatureType?.id === 'place';
  const isGssBoundaryPlace =
    isOfficialAreaFlow ||
    (isPlaceFeature && isGssBoundaryPlaceCategory(selectedCategory));
  const isGssMetadataEdit =
    isOfficialAreaFlow ||
    (isGssBoundaryPlaceCategory(selectedCategory) &&
      (!!suggestEditForPlace || !!enrichFeature || !!editContribution));

  const usesBuildingAddressFormat =
    selectedFeatureType?.id === 'building'
    || selectedFeatureType?.id === 'poi'
    || selectedFeatureType?.id === 'landmark';
  const isRoadFeature = selectedFeatureType?.id === 'road';

  const addressLine = useMemo(() => {
    const locality = normalizeLocalityFields(addrSuburb, addrCity);
    if (isPlaceFeature) {
      return composePlaceContextLine({
        placeName: name,
        suburb: locality.suburb,
        city: locality.city,
        district: addrDistrict,
        region: addrRegion,
      });
    }
    if (isRoadFeature) {
      return composeRoadAddressLine({
        roadName: name,
        suburb: locality.suburb,
        city: locality.city,
        district: addrDistrict,
        region: addrRegion,
        ghanaPostGps,
      });
    }
    if (usesBuildingAddressFormat) {
      return composeBuildingAddressLine({
        name,
        street: addrStreet,
        suburb: locality.suburb,
        city: locality.city,
        district: addrDistrict,
        ghanaPostGps,
        houseNumber: hostHouseNumber || undefined,
        frontageStreetAddress: hostFrontageStreetAddress || undefined,
      });
    }
    return composeAddressLine({
      name,
      street: addrStreet,
      suburb: locality.suburb,
      city: locality.city,
      district: addrDistrict,
      region: addrRegion,
      ghanaPostGps,
    });
  }, [
    isPlaceFeature,
    isRoadFeature,
    usesBuildingAddressFormat,
    name,
    addrStreet,
    addrSuburb,
    addrCity,
    addrDistrict,
    addrRegion,
    ghanaPostGps,
    hostHouseNumber,
    hostFrontageStreetAddress,
  ]);

  const resolvedCategories = useMemo(() => {
    const ftId = selectedFeatureType?.id;
    if (ftId === 'landmark' && selectedLandmarkGroup) {
      const group = LANDMARK_SOURCE_GROUPS.find((g) => g.id === selectedLandmarkGroup);
      if (group?.categories === 'poi_api') {
        const base = mergePoiCategoryOptions(
          categoryOptions.length > 0 ? categoryOptions : POI_FALLBACK_CATEGORIES,
        );
        return applyCategoryHints(
          base.filter((c) => !TRANSPORT_STOP_CATEGORY_IDS.has(c.id)),
          'poi',
        );
      }
      if (group?.categories) {
        return applyCategoryHints(group.categories, landmarkCategoryFeatureType(selectedLandmarkGroup));
      }
    }
    if (ftId === 'transport_stop') {
      return applyCategoryHints(TRANSPORT_STOP_CATEGORIES, 'transport_stop');
    }
    if (ftId && FEATURE_TYPE_CATEGORIES[ftId]) {
      return applyCategoryHints(FEATURE_TYPE_CATEGORIES[ftId], ftId);
    }
    const base = mergePoiCategoryOptions(
      categoryOptions.length > 0 ? categoryOptions : POI_FALLBACK_CATEGORIES,
    );
    if (ftId === 'poi') {
      return applyCategoryHints(
        base.filter((c) => !TRANSPORT_STOP_CATEGORY_IDS.has(c.id)),
        'poi',
      );
    }
    return applyCategoryHints(base, ftId);
  }, [categoryOptions, selectedFeatureType, selectedLandmarkGroup]);

  const enrichSearchFeatureType = useMemo(() => {
    if (selectedFeatureType?.id !== 'landmark' || !selectedLandmarkGroup) {
      return selectedFeatureType?.id;
    }
    const map: Record<string, string> = {
      historic: 'landmark',
      poi: 'poi',
      water: 'water',
      structure: 'structure',
      transport: 'transport_stop',
      landuse: 'landuse',
      landcover: 'landcover',
    };
    return map[selectedLandmarkGroup] ?? selectedFeatureType.id;
  }, [selectedFeatureType, selectedLandmarkGroup]);

  /** Source-table feature type for trace/draw helpers (landmark uses group, not "landmark"). */
  const landmarkGeometryFeatureType = useMemo(() => {
    if (selectedFeatureType?.id !== 'landmark' || !selectedLandmarkGroup) {
      return selectedFeatureType?.id ?? null;
    }
    const map: Record<string, string> = {
      historic: 'poi',
      poi: 'poi',
      water: 'water',
      structure: 'structure',
      transport: 'transport_stop',
      landuse: 'landuse',
      landcover: 'landcover',
    };
    return map[selectedLandmarkGroup] ?? selectedFeatureType.id;
  }, [selectedFeatureType, selectedLandmarkGroup]);

  const markingGeometryFeatureType =
    selectedFeatureType?.id === 'landmark' ? landmarkGeometryFeatureType : selectedFeatureType?.id;

  const isEnclosureMarkingStep = step === 'enclosure_marking';
  const activeMarkingFeatureType = isEnclosureMarkingStep ? 'landuse' : markingGeometryFeatureType;
  const activeMarkingDrawsPolygon = drawProducesPolygon(activeMarkingFeatureType);

  const enrichTargetLabel = useMemo(() => {
    if (selectedFeatureType?.id === 'landmark' && selectedLandmarkGroup) {
      return LANDMARK_SOURCE_GROUPS.find((g) => g.id === selectedLandmarkGroup)?.name ?? 'feature';
    }
    return selectedFeatureType?.name ?? 'feature';
  }, [selectedFeatureType, selectedLandmarkGroup]);

  const markingDrawsPolygon = drawProducesPolygon(markingGeometryFeatureType);

  const savedTraceFeatureLabel = useMemo(() => {
    if (!traceDraftFeatureTypeId) return null;
    return FEATURE_TYPES.find((f) => f.id === traceDraftFeatureTypeId)?.name ?? traceDraftFeatureTypeId;
  }, [traceDraftFeatureTypeId]);

  const traceReady =
    geometryMode === 'trace' && hasValidTraceGeometry(tracePoints, markingGeometryFeatureType);

  const markedPolygonAreaLabel = useMemo(() => {
    let geom: GeoJSON.Geometry | null = drawnGeometry as GeoJSON.Geometry | null;
    if (!geom && enrichFeature?.geometry) {
      geom = enrichFeature.geometry as GeoJSON.Geometry;
    }
    if (!geom && traceReady && traceProducesPolygon(markingGeometryFeatureType)) {
      geom = buildTraceGeometry(tracePoints, markingGeometryFeatureType) as GeoJSON.Geometry;
    }
    if (!geom || (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon')) return null;
    const sqM = computePolygonAreaSqM(geom as GeoJSON.Polygon | GeoJSON.MultiPolygon);
    return formatAreaLabel(sqM);
  }, [drawnGeometry, enrichFeature, tracePoints, traceReady, markingGeometryFeatureType]);

  const selectedCategoryMeta = useMemo(
    () => resolvedCategories.find((c) => c.id === selectedCategory),
    [resolvedCategories, selectedCategory]
  );

  // Load POI subcategories for contribution picker (only needed for POI/Landmark)
  useEffect(() => {
    if (!isOpen) return;
    const ftId = selectedFeatureType?.id;
    if (ftId === 'landmark' && selectedLandmarkGroup !== 'poi') return;
    // Skip API fetch for feature types with static categories
    if (ftId && FEATURE_TYPE_CATEGORIES[ftId] && ftId !== 'landmark') return;
    setCategoriesLoading(true);
    getCategories()
      .then((cats) => {
        const flat = mergePoiCategoryOptions(flattenApiCategories(cats));
        setCategoryOptions(flat.length > 0 ? flat : mergePoiCategoryOptions(POI_FALLBACK_CATEGORIES));
      })
      .catch(() => {
        setCategoryOptions(mergePoiCategoryOptions(POI_FALLBACK_CATEGORIES));
      })
      .finally(() => setCategoriesLoading(false));
  }, [isOpen, selectedFeatureType, selectedLandmarkGroup]);

  // Enrich: reverse-geocode district/region when feature details lack admin fields.
  useEffect(() => {
    if (step !== 'details' || !isOpen || !enrichFeature) return;

    const anchor =
      getGeometryAnchor(enrichFeature.geometry as GeoJSON.Geometry) ??
      getContributionAnchor(geometryMode, selectedLocation ?? null, drawnGeometry ?? null, tracePoints);
    if (!anchor) return;

    let cancelled = false;
    setAddressLookupStatus('loading');

    reverseGeocodeForContribution(anchor.lat, anchor.lng)
      .then(({ result: r, suggestedFields }) => {
        if (cancelled) return;
        if (!r) {
          setAddressLookupStatus('error');
          return;
        }
        setSuggestedAddressFields(suggestedFields);
        const incoming = extractReverseGeocodeComponents(r);
        const merged = mergeAddressFields(
          { street: addrStreet, suburb: addrSuburb, city: addrCity, district: addrDistrict, region: addrRegion },
          incoming,
        );
        if (!hasGeocodedAddressComponents(merged)) {
          setAddressLookupStatus('error');
          return;
        }
        const resolved = resolveCanonicalAdminFields(merged);
        if (cancelled) return;
        const enrichFeatureId = enrichFeature.feature_type || selectedFeatureType?.id;
        const enrichUsesBuilding = usesBuildingHostAddressFormat(enrichFeatureId);
        const enrichIsRoad = enrichFeatureId === 'road';
        const enrichHouseNumber =
          hostHouseNumber.trim()
          || (enrichFeature.details?.house_number
            ? String(enrichFeature.details.house_number)
            : enrichFeature.details?.building_house_number
              ? String(enrichFeature.details.building_house_number)
              : extractReverseGeocodeBuildingNumber(r)
              || '');
        if (enrichUsesBuilding && enrichHouseNumber) setHostHouseNumber(enrichHouseNumber);
        applyAddressFields(
          enrichUsesBuilding
            ? {
                ...resolved,
                street: streetLineWithoutHouseNumber(resolved.street, enrichHouseNumber),
              }
            : enrichIsRoad
              ? { ...resolved, street: '' }
              : resolved,
          {
          setStreet: enrichIsRoad ? () => {} : setAddrStreet,
          setSuburb: setAddrSuburb,
          setCity: setAddrCity,
          setDistrict: setAddrDistrict,
          setRegion: setAddrRegion,
        });
        setAddressLookupStatus('ok');
      })
      .catch(() => {
        if (!cancelled) setAddressLookupStatus('error');
      });

    return () => { cancelled = true; };
  }, [
    step,
    isOpen,
    enrichFeature?.table_id,
    enrichFeature?.location_id,
    addressLookupKey,
    geometryMode,
    selectedLocation?.lat,
    selectedLocation?.lng,
  ]);

  // Populate structured address from reverse geocode at the contribution anchor (center / pin / mid-trace).
  useEffect(() => {
    if (step !== 'details' || !isOpen) return;
    if (editContribution || suggestEditForPlace || enrichFeature) return;
    if (addressLookupStatus === 'ok') return;
    const anchor = getContributionAnchor(
      geometryMode,
      selectedLocation ?? null,
      drawnGeometry ?? null,
      tracePoints
    );
    if (!anchor) {
      setAddressLookupStatus('error');
      return;
    }

    let cancelled = false;
    setAddressLookupStatus('loading');
    reverseGeocodeForContribution(anchor.lat, anchor.lng)
      .then(async ({ result: r, suggestedFields }) => {
        if (cancelled) return;
        if (!r) {
          setSuggestedAddressFields([]);
          setAddressLookupStatus('error');
          return;
        }
        setSuggestedAddressFields(suggestedFields);
        const incoming = extractReverseGeocodeComponents(r);
        const incomingForMerge = selectedFeatureType?.id === 'road'
          ? { ...incoming, street: '' }
          : incoming;
        const merged = mergeAddressFields(
          {
            street: selectedFeatureType?.id === 'road' ? '' : addrStreet,
            suburb: addrSuburb,
            city: addrCity,
            district: addrDistrict,
            region: addrRegion,
          },
          incomingForMerge,
        );
        if (!hasGeocodedAddressComponents(merged)) {
          setAddressLookupStatus('error');
          return;
        }
        const resolved = resolveCanonicalAdminFields(merged);
        if (cancelled) return;
        const newContribUsesBuilding = usesBuildingHostAddressFormat(selectedFeatureType?.id);
        const newContribIsRoad = selectedFeatureType?.id === 'road';
        const rgHouseNumber =
          hostHouseNumber.trim()
          || extractReverseGeocodeBuildingNumber(r)
          || '';
        if (newContribUsesBuilding && rgHouseNumber) {
          setHostHouseNumber(rgHouseNumber);
          if (!name.trim() && selectedFeatureType?.id === 'building') {
            setName(resolveBuildingHostAddressPrefill({
              houseNumber: rgHouseNumber,
            }).name);
          }
        }
        applyAddressFields(
          newContribUsesBuilding
            ? {
                ...resolved,
                street: streetLineWithoutHouseNumber(resolved.street, rgHouseNumber),
              }
            : newContribIsRoad
              ? { ...resolved, street: '' }
              : resolved,
          {
          setStreet: newContribIsRoad ? () => {} : setAddrStreet,
          setSuburb: setAddrSuburb,
          setCity: setAddrCity,
          setDistrict: setAddrDistrict,
          setRegion: setAddrRegion,
        });
        setAddressLookupStatus('ok');
      })
      .catch(() => {
        if (!cancelled) setAddressLookupStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [
    step,
    isOpen,
    editContribution,
    suggestEditForPlace,
    enrichFeature,
    addressLookupKey,
    selectedFeatureType,
    geometryMode,
    selectedLocation,
    drawnGeometry,
    tracePoints,
  ]);

  const clearStructuredAddressFields = useCallback(() => {
    const empty = emptyContributionAddressFields();
    setAddrStreet(empty.street);
    setAddrSuburb(empty.suburb);
    setAddrCity(empty.city);
    setAddrDistrict(empty.district);
    setAddrRegion(empty.region);
    setSuggestedAddressFields([]);
  }, []);

  const retryAddressLookup = useCallback(() => {
    setAddressLookupStatus('idle');
    setAddressLookupKey((k) => k + 1);
  }, []);

  const clearEnrichSelectionState = useCallback(() => {
    enrichTapRef.current = null;
    setEnrichFeature(null);
    setEnrichLoading(false);
    onClearHighlight?.();
  }, [onClearHighlight]);

  const handleDrawUndo = useCallback(() => {
    if (onDrawUndo?.()) {
      onPreviewGeometry?.(null);
      return;
    }
    onClearGeometry();
    onPreviewGeometry?.(null);
  }, [onClearGeometry, onDrawUndo, onPreviewGeometry]);

  const handleDrawUndoFirst = useCallback(() => {
    if (onDrawUndoFirst?.()) {
      onPreviewGeometry?.(null);
      return;
    }
    onClearGeometry();
    onPreviewGeometry?.(null);
  }, [onClearGeometry, onDrawUndoFirst, onPreviewGeometry]);

  const requestHydrateMarkingGeometry = useCallback(() => {
    if (!geometryMode || geometryMode === 'enrich' || geometryMode === 'coordinates' || geometryMode === 'link') {
      return;
    }
    onHydrateMarkingGeometry?.({
      geometryMode,
      drawnGeometry: drawnGeometry ?? null,
      tracePoints,
      markingFeatureType: markingGeometryFeatureType ?? null,
    });
  }, [
    geometryMode,
    drawnGeometry,
    tracePoints,
    markingGeometryFeatureType,
    onHydrateMarkingGeometry,
  ]);

  const handleDrawClear = useCallback(() => {
    if (onDrawTrash?.()) {
      onPreviewGeometry?.(null);
      return;
    }
    onClearGeometry();
    onPreviewGeometry?.(null);
  }, [onClearGeometry, onDrawTrash, onPreviewGeometry]);

  const clearMarkingGeometryState = useCallback(() => {
    onClearGeometry();
    onDrawTrash?.();
    onPreviewGeometry?.(null);
    campaignTraceAutoStartRef.current = false;
  }, [onClearGeometry, onDrawTrash, onPreviewGeometry]);

  const resetEnrichSelection = useCallback(() => {
    clearEnrichSelectionState();
    clearStructuredAddressFields();
    setAddressLookupStatus('idle');
    setAddressLookupKey((k) => k + 1);
    setName('');
    setDescription('');
    setSelectedCategory('');
    clearMarkingGeometryState();
    onEnableMapClick(true);
    setSubmitFeedback(null);
    setStep('enrich_select');
  }, [
    clearEnrichSelectionState,
    clearMarkingGeometryState,
    clearStructuredAddressFields,
    onEnableMapClick,
  ]);

  const requestAddressLookup = useCallback(() => {
    clearStructuredAddressFields();
    setAddressLookupStatus('idle');
    setAddressLookupKey((k) => k + 1);
  }, [clearStructuredAddressFields]);

  useEffect(() => {
    onTraceRingModeChange?.(traceProducesPolygon(markingGeometryFeatureType));
  }, [markingGeometryFeatureType, onTraceRingModeChange]);

  useEffect(() => {
    if (geometryMode !== 'trace') return;
    onTraceContextChange?.({
      featureTypeId: markingGeometryFeatureType ?? null,
      landmarkGroup: selectedLandmarkGroup,
    });
  }, [
    geometryMode,
    markingGeometryFeatureType,
    selectedLandmarkGroup,
    onTraceContextChange,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (step === 'enrich_select' || step === 'landmark_placement') {
      setSheetSnap('expanded');
      return;
    }
    if (step === 'road_capture_plan') {
      setSheetSnap((roadCarriagewayCount ?? 1) >= 2 ? 'half' : 'compact');
      return;
    }
    if (COMPACT_SHEET_STEPS.has(step)) {
      setSheetSnap('compact');
      return;
    }
    if (step === 'marking' || step === 'enclosure_marking') {
      if (geometryMode === 'draw' || geometryMode === 'trace') {
        setSheetSnap('compact');
      } else if (geometryMode === 'point') {
        setSheetSnap('half');
      }
    }
    if (step === 'enclosure_map') {
      setSheetSnap('compact');
    }
  }, [isOpen, step, geometryMode]);

  useEffect(() => {
    if (!isOpen) {
      onMarkingDrawFabActive?.(false);
      return;
    }
    const drawFabActive =
      (step === 'marking' || step === 'enclosure_marking')
      && (
        geometryMode === 'draw'
        || (geometryMode === 'trace' && !isTracing && tracePoints.length >= 1)
      );
    onMarkingDrawFabActive?.(drawFabActive);
  }, [isOpen, step, geometryMode, isTracing, tracePoints.length, onMarkingDrawFabActive]);

  const vertexEditWasActiveRef = useRef(false);
  useEffect(() => {
    const vertexEditActive =
      isOpen
      && (
        step === 'marking'
        || step === 'enclosure_marking'
        || (step === 'details' && !!editContribution)
      )
      && geometryMode === 'trace'
      && !isTracing
      && tracePoints.length >= 2;
    onTraceVertexEditActiveChange?.(vertexEditActive);
    if (vertexEditActive) {
      onEnableMapClick(false);
      if (!vertexEditWasActiveRef.current) {
        onClearHighlight?.();
      }
    }
    vertexEditWasActiveRef.current = vertexEditActive;
  }, [
    isOpen,
    step,
    geometryMode,
    isTracing,
    tracePoints.length,
    editContribution,
    onTraceVertexEditActiveChange,
    onEnableMapClick,
    onClearHighlight,
  ]);

  // Safety: enrich mode belongs on enrich_select, not marking (empty panel / wrong title).
  useEffect(() => {
    if (!isOpen || step !== 'marking' || geometryMode !== 'enrich') return;
    onEnableMapClick(true);
    setSheetSnap('expanded');
    setStep('enrich_select');
  }, [isOpen, step, geometryMode, onEnableMapClick]);

  // Map preview — dashed boundary / pin before the user continues to label
  useEffect(() => {
    if (!isOpen || (step !== 'marking' && step !== 'enclosure_marking')) {
      onPreviewGeometry?.(null);
      return;
    }

    if (geometryMode === 'point' && selectedLocation) {
      onPreviewGeometry?.({
        type: 'Point',
        coordinates: [selectedLocation.lng, selectedLocation.lat],
      });
      return;
    }

    if (geometryMode === 'draw') {
      // MapboxDraw renders live geometry — a preview layer would duplicate it.
      onPreviewGeometry?.(null);
      return;
    }

    if (geometryMode === 'trace' && tracePoints.length > 0 && !isTracing) {
      onPreviewGeometry?.(null);
      return;
    }

    if (geometryMode !== 'trace' || tracePoints.length === 0) {
      onPreviewGeometry?.(null);
    }
  }, [
    isOpen,
    step,
    geometryMode,
    selectedLocation,
    drawnGeometry,
    tracePoints,
    isTracing,
    selectedFeatureType?.id,
    markingGeometryFeatureType,
    activeMarkingFeatureType,
    onPreviewGeometry,
  ]);

  // Auto-start continuous GPS trace when capturing from a live campaign session
  useEffect(() => {
    if (!isOpen) {
      campaignTraceAutoStartRef.current = false;
      return;
    }
    if (
      campaignFieldCapture &&
      step === 'marking' &&
      geometryMode === 'trace' &&
      traceCaptureMode === 'continuous' &&
      !isTracing &&
      tracePoints.length === 0 &&
      !campaignTraceAutoStartRef.current
    ) {
      campaignTraceAutoStartRef.current = true;
      onStartTrace();
    }
  }, [
    isOpen,
    campaignFieldCapture,
    step,
    geometryMode,
    traceCaptureMode,
    isTracing,
    tracePoints.length,
    onStartTrace,
  ]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      campaignTraceAutoStartRef.current = false;
      setStep('feature_type');
      setSelectedFeatureType(null);
      setGeometryMode(null);
      setSelectedCategory(null);
      setSelectedLandmarkGroup(null);
      setName('');
      setAddrStreet('');
      setAddrSuburb('');
      setAddrCity('');
      setAddrDistrict('');
      setAddrRegion('');
      setGhanaPostGps('');
      setHostHouseNumber('');
      setHostFrontageStreetAddress('');
      setBuildingUnits([]);
      setUnitInput('');
      setUnitBatchFloor('1');
      setAutoNumberCount('');
      setAutoNumberStart('1');
      setUnitMode('manual');
      floorBatchDraftsRef.current = {};
      lastActiveFloorRef.current = '1';
      setAddressLookupStatus('idle');
      setPhone('');
      setMobile('');
      setWhatsapp('');
      setContactEmail('');
      setWebsite('');
      setFacebook('');
      setInstagram('');
      setOpeningHours('');
      setSheetSnap('half');
      setRoadSurface('');
      setRoadSmoothness('');
      setRoadCarriagewayCount(null);
      setRoadLanes('');
      setRoadLanesForward('');
      setRoadLanesBackward('');
      setRoadLaneDirections([]);
      setRoadManualLaneDirections(false);
      setRoadToll(false);
      setRoadFloodProne(false);
      setRoadPotholes(false);
      setRoadStreetVendors(false);
      setRoadAccidentProne(false);
      setRoadHawkers(false);
      setRoadLit(false);
      setRoadDrainage(false);
      setRoadMaxspeed('');
      setRoadCrossingType('');
      setRoadSideFacilities(EMPTY_SIDE_FACILITIES);
      setTreeHeight('');
      setAerowayRef('');
      setAerowaySurface('');
      setAerowayWidth('');
      setJunctionRef('');
      setJunctionExitRoads([]);
      setStopRef('');
      setStopRoutes([]);
      setStopOperators([]);
      setStopShelter(false);
      setStopBench(false);
      setStopLighting(false);
      setPoiCuisine('');
      setPoiBrand('');
      setPoiWheelchair('');
      setPaymentMobileMoney(false);
      setPaymentCard(false);
      setMobileMoneyProvider('');
      setDescription('');
      setPhotoDataUrls([]);
      setCoordLat('');
      setCoordLng('');
      setLinkInput('');
      setLinkError(null);
      setEnrichFeature(null);
      setEnrichLoading(false);
      setEnclosureMethod(null);
      setEnclosureDetectResult(null);
      setEnclosureSelectedCandidateId(null);
      setEnclosureLoading(false);
      setEnclosureFeedback(null);
      setEnclosureBoundaryRoadIds([]);
      setEnclosurePreviewAreaSqm(null);
      setEnclosureConfirmedGeometry(null);
      enclosureTapRef.current = null;
      enclosureBoundaryTapRef.current = null;
      enclosureFromDetectionRef.current = false;
      setCategorySearch('');
      setOfficialLevel('region');
      setOfficialUpdateIntent('description');
      setOfficialBoundaryName('');
      setOfficialFilterRegion('');
      setOfficialSelectLoading(false);
      setDescriptionSourceUrl('');
      onEnableMapClick(false);
      onEnableDrawing(false);
    }
  }, [isOpen]);

  const restoreBuildingUnitsFromApi = useCallback((raw: unknown) => {
    const restored = normalizeBuildingUnits(raw).map((u) => ({
      label: u.label,
      floor: u.floor ?? 1,
    }));
    setBuildingUnits(restored);
    floorBatchDraftsRef.current = {};
    if (restored.length > 0) {
      const floorKey = String(Math.max(...restored.map((u) => u.floor ?? 1)));
      setUnitBatchFloor(floorKey);
      lastActiveFloorRef.current = floorKey;
    } else {
      setUnitBatchFloor('1');
      lastActiveFloorRef.current = '1';
    }
  }, []);

  // Pre-fill form when suggesting edits for an existing map place
  useEffect(() => {
    if (!isOpen || !suggestEditForPlace) return;
    const featureTypeId = suggestEditContributionFeatureType(suggestEditForPlace);
    const gssSuggestEdit =
      featureTypeId === 'place' && isGssBoundaryPlaceCategory(suggestEditForPlace.category);
    const ft = gssSuggestEdit
      ? FEATURE_TYPES.find((f) => f.id === 'official_area')!
      : FEATURE_TYPES.find((f) => f.id === featureTypeId) ?? FEATURE_TYPES.find((f) => f.id === 'place')!;
    setSelectedFeatureType(ft);
    if (gssSuggestEdit) {
      setOfficialLevel((suggestEditForPlace.category as OfficialAreaLevel) || 'region');
      setOfficialUpdateIntent('both');
      setOfficialBoundaryName(suggestEditForPlace.name || '');
    }
    setGeometryMode(gssSuggestEdit ? null : 'enrich');
    onEnableMapClick(false);
    onEnableDrawing(false);
    onClearGeometry();
    onClearHighlight?.();
    setEnrichFeature({
      feature_type: featureTypeId,
      table_id: 0,
      location_id: suggestEditForPlace.id,
      name: suggestEditForPlace.name,
      category: suggestEditForPlace.category,
      geometry: {
        type: 'Point',
        coordinates: [suggestEditForPlace.coordinates.lng, suggestEditForPlace.coordinates.lat],
      },
      distance_m: 0,
      details: {},
    });
    // Core info — building names must not prefill from street address lines
    const suggestHouseNumber = suggestEditForPlace.houseNumber?.trim() ?? '';
    const suggestBuildingPrefill = usesBuildingHostAddressFormat(featureTypeId)
      ? resolveBuildingHostAddressPrefill({
          name: suggestEditForPlace.name,
          houseNumber: suggestHouseNumber,
          addrStreet: suggestEditForPlace.addrStreet,
          frontageStreetAddress: suggestEditForPlace.frontageStreetAddress,
        })
      : null;
    setName(suggestBuildingPrefill?.name ?? (suggestEditForPlace.name ?? ''));
    if (suggestBuildingPrefill?.houseNumber) setHostHouseNumber(suggestBuildingPrefill.houseNumber);
    setSelectedCategory(suggestEditForPlace.category || null);
    const showBusinessPrefill = contributionFeatureShowsBusinessDetails(featureTypeId, selectedLandmarkGroup);
    // Contact & online fields — POI/building only
    if (showBusinessPrefill) {
      if (suggestEditForPlace.phone) setPhone(suggestEditForPlace.phone);
      if (suggestEditForPlace.mobile) setMobile(suggestEditForPlace.mobile);
      if (suggestEditForPlace.whatsapp) setWhatsapp(suggestEditForPlace.whatsapp);
      if (suggestEditForPlace.email) setContactEmail(suggestEditForPlace.email);
      if (suggestEditForPlace.website) setWebsite(suggestEditForPlace.website);
      if (suggestEditForPlace.facebook) setFacebook(suggestEditForPlace.facebook);
      if (suggestEditForPlace.instagram) setInstagram(suggestEditForPlace.instagram);
      const hoursPrefill = openingHoursPrefill(
        suggestEditForPlace.openingHours,
        suggestEditForPlace.hours,
      );
      if (hoursPrefill) setOpeningHours(hoursPrefill);
    }
    if (suggestEditForPlace.description) setDescription(suggestEditForPlace.description);
    if (suggestEditForPlace.descriptionSourceUrl) {
      setDescriptionSourceUrl(suggestEditForPlace.descriptionSourceUrl);
    }
    if (suggestEditForPlace.ghanaPostGPS) setGhanaPostGps(suggestEditForPlace.ghanaPostGPS);
    if (featureTypeId === 'building' && suggestEditForPlace.buildingUnits?.length) {
      restoreBuildingUnitsFromApi(suggestEditForPlace.buildingUnits);
    }
    if (featureTypeId === 'transport_stop') {
      const stopPrefill = transitStopFieldsFromPlace(suggestEditForPlace);
      if (stopPrefill.stopRef) setStopRef(stopPrefill.stopRef);
      if (stopPrefill.routes.length) setStopRoutes(stopPrefill.routes);
      if (stopPrefill.operators.length) setStopOperators(stopPrefill.operators);
      setStopShelter(stopPrefill.shelter);
      setStopBench(stopPrefill.bench);
      setStopLighting(stopPrefill.lighting);
    }

    let cancelled = false;
    void (async () => {
      let addressSource = suggestEditForPlace;
      try {
        const fresh = await getPlaceDetails(suggestEditForPlace.id);
        if (!cancelled && showBusinessPrefill) {
          const freshHours = openingHoursPrefill(fresh.opening_hours, fresh.hours);
          if (freshHours) setOpeningHours(freshHours);
        }
        if (!cancelled) {
          if (fresh.description) setDescription(fresh.description);
          if (fresh.description_source_url) {
            setDescriptionSourceUrl(fresh.description_source_url);
          }
          if (fresh.ghana_post_gps) setGhanaPostGps(fresh.ghana_post_gps);
          if (featureTypeId === 'building' && fresh.building_units?.length) {
            restoreBuildingUnitsFromApi(fresh.building_units);
          }
          if (featureTypeId === 'transport_stop') {
            const stopFields = transitStopFieldsFromPlace(fresh);
            if (stopFields.stopRef) setStopRef(stopFields.stopRef);
            setStopRoutes(stopFields.routes);
            setStopOperators(stopFields.operators);
            if (fresh.shelter != null) setStopShelter(Boolean(fresh.shelter));
            if (fresh.bench != null) setStopBench(Boolean(fresh.bench));
            if (fresh.lighting != null) setStopLighting(Boolean(fresh.lighting));
          }
        }
        if (gssSuggestEdit && !cancelled) {
          const matched = await matchFeatureByName(suggestEditForPlace.name || '', 'place');
          if (matched) {
            setEnrichFeature(matched);
            onHighlightGeometry?.(matched.geometry as GeoJSON.Geometry);
          } else if (fresh.geometry) {
            setEnrichFeature((prev) =>
              prev
                ? {
                    ...prev,
                    geometry: fresh.geometry as GeoJSONGeometry,
                  }
                : prev,
            );
            onHighlightGeometry?.(fresh.geometry as GeoJSON.Geometry);
          }
        }
        addressSource = {
          ...suggestEditForPlace,
          name: fresh.name ?? suggestEditForPlace.name,
          houseNumber: fresh.house_number ?? suggestEditForPlace.houseNumber,
          frontageStreetAddress: fresh.frontage_street_address ?? suggestEditForPlace.frontageStreetAddress,
          addrStreet: fresh.addr_street ?? suggestEditForPlace.addrStreet,
          addrSuburb: fresh.addr_suburb ?? suggestEditForPlace.addrSuburb,
          addrCity: fresh.addr_city ?? suggestEditForPlace.addrCity,
          district: fresh.district ?? suggestEditForPlace.district,
          region: fresh.region ?? suggestEditForPlace.region,
          streetAddress: fresh.street_address ?? suggestEditForPlace.streetAddress,
          openingHours: fresh.opening_hours ?? suggestEditForPlace.openingHours,
          hours: fresh.hours
            ? {
                open: fresh.hours.open,
                close: fresh.hours.close,
                isOpen: fresh.hours.is_open,
              }
            : suggestEditForPlace.hours,
        };
      } catch {
        // Use cached place card data when refresh fails.
      }

      if (cancelled) return;

      const houseNumber =
        addressSource.houseNumber?.trim()
        || (featureTypeId === 'building' ? suggestEditForPlace.houseNumber?.trim() : '')
        || '';
      if (houseNumber) setHostHouseNumber(houseNumber);
      if (addressSource.frontageStreetAddress) {
        setHostFrontageStreetAddress(addressSource.frontageStreetAddress);
      }

      setName(
        usesBuildingHostAddressFormat(featureTypeId)
          ? resolveBuildingHostAddressPrefill({
              name: addressSource.name,
              houseNumber,
              addrStreet: addressSource.addrStreet,
              frontageStreetAddress: addressSource.frontageStreetAddress,
            }).name
          : (addressSource.name ?? ''),
      );

      const placePrefill = placeAddressPrefill(addressSource);
      const streetForEdit = usesBuildingHostAddressFormat(featureTypeId)
        ? resolveBuildingHostAddressPrefill({
            houseNumber,
            street: placePrefill.street,
            addrStreet: addressSource.addrStreet,
            frontageStreetAddress: addressSource.frontageStreetAddress,
          }).street
        : (placePrefill.street || addressSource.addrStreet || addressSource.frontageStreetAddress || '');
      applyAddressFields(
        mergeAddressFields({}, { ...placePrefill, street: streetForEdit }),
        {
          setStreet: setAddrStreet,
          setSuburb: setAddrSuburb,
          setCity: setAddrCity,
          setDistrict: setAddrDistrict,
          setRegion: setAddrRegion,
        },
      );

      if (placeHasStoredAddress(addressSource) && suggestEditForPlace.placeType !== 'street') {
        setAddressLookupStatus('ok');
        return;
      }

      setAddressLookupStatus('loading');
      const { lat, lng } = suggestEditForPlace.coordinates;
      const isStreetPlace = suggestEditForPlace.placeType === 'street';
      try {
        const lookup = isStreetPlace
          ? { result: await reverseGeocode(lat, lng, { adminOnly: true }), suggestedFields: [] as string[] }
          : await reverseGeocodeForContribution(lat, lng);
        const r = lookup.result;
        if (cancelled || !r) return;
        setSuggestedAddressFields(lookup.suggestedFields);
        const incoming = extractReverseGeocodeComponents(r);
        const merged = mergeAddressFields({ ...placePrefill, street: streetForEdit }, incoming);
        const resolved = resolveCanonicalAdminFields(mergeAddressFields({}, merged));
        if (cancelled) return;
        applyAddressFields(
          {
            ...resolved,
            street: usesBuildingHostAddressFormat(featureTypeId)
              ? streetLineWithoutHouseNumber(resolved.street, houseNumber)
              : resolved.street,
          },
          {
            setStreet: setAddrStreet,
            setSuburb: setAddrSuburb,
            setCity: setAddrCity,
            setDistrict: setAddrDistrict,
            setRegion: setAddrRegion,
          },
        );
      } catch {
        // Keep whatever we already have from the place record.
      } finally {
        if (!cancelled) setAddressLookupStatus('ok');
      }
    })();

    setSubmitFeedback(null);
    setStep('details');
    return () => {
      cancelled = true;
    };
  }, [isOpen, suggestEditForPlace, restoreBuildingUnitsFromApi]);

  // Pre-fill form when editing a contribution
  useEffect(() => {
    if (!isOpen || !editContribution) {
      if (!editContribution) editPrefillKeyRef.current = null;
      return;
    }
    const prefillKey = String(editContribution.id);
    if (editPrefillKeyRef.current === prefillKey) return;
    editPrefillKeyRef.current = prefillKey;
    // Feature type
    let ft = FEATURE_TYPES.find((f) => f.id === editContribution.feature_type);
    const gssEdit =
      editContribution.feature_type === 'official_area' ||
      (editContribution.feature_type === 'place' &&
        isGssBoundaryPlaceCategory(editContribution.category));
    if (gssEdit) {
      ft = FEATURE_TYPES.find((f) => f.id === 'official_area') ?? ft;
      setOfficialBoundaryName(editContribution.name || '');
      setOfficialLevel((editContribution.category as OfficialAreaLevel) || 'region');
      const uf = editContribution.extra_fields?.update_fields;
      if (Array.isArray(uf)) {
        if (uf.includes('description') && uf.includes('photos')) setOfficialUpdateIntent('both');
        else if (uf.includes('photos')) setOfficialUpdateIntent('photos');
        else setOfficialUpdateIntent('description');
      }
      if (editContribution.geometry) {
        const targetTableId = editContribution.extra_fields?.target_table_id;
        setEnrichFeature({
          feature_type: editContribution.feature_type ?? 'official_area',
          table_id: typeof targetTableId === 'number' ? targetTableId : 0,
          location_id:
            typeof editContribution.extra_fields?.target_location_id === 'string'
              ? editContribution.extra_fields.target_location_id
              : undefined,
          name: editContribution.name,
          category: editContribution.category,
          geometry: editContribution.geometry as GeoJSONGeometry,
          distance_m: 0,
          details: {},
        });
        setGeometryMode('enrich');
      }
    }
    if (ft) setSelectedFeatureType(ft);
    // Geometry mode
    const geomType = editContribution.geometry?.type;
    if (geomType === 'Point') setGeometryMode('point');
    else if (geomType === 'Polygon' || geomType === 'MultiPolygon') setGeometryMode('draw');
    else if (geomType === 'LineString') {
      setGeometryMode(editContribution.feature_type === 'road' ? 'trace' : 'draw');
    }
    // Core fields
    setSelectedCategory(editContribution.category || null);
    const ef = editContribution.extra_fields ?? {};
    const editFeatureTypeId = editContribution.feature_type;
    const editUsesBuildingFormat = usesBuildingHostAddressFormat(editFeatureTypeId);
    const editHouseNumber =
      String(ef.house_number ?? ef.building_house_number ?? '').trim();
    const editFrontage =
      String(ef.frontage_street_address ?? ef.building_frontage_street_address ?? '').trim();
    if (editUsesBuildingFormat) {
      const buildingPrefill = resolveBuildingHostAddressPrefill({
        name: editContribution.name,
        houseNumber: editHouseNumber,
        addrStreet: editContribution.addr_street,
        frontageStreetAddress: editFrontage || undefined,
      });
      setName(buildingPrefill.name);
      if (buildingPrefill.houseNumber) setHostHouseNumber(buildingPrefill.houseNumber);
    } else if (editFeatureTypeId === 'road') {
      setName(
        editContribution.name?.trim()
        || editContribution.addr_street?.trim()
        || '',
      );
    } else {
      setName(editContribution.name || '');
    }
    setPhone(editContribution.phone || '');
    setWebsite(editContribution.website || '');
    setDescription(editContribution.description || '');
    setPhotoDataUrls(editContribution.photos || []);
    // Restore extra_fields (mobile, whatsapp, email, facebook, instagram, opening_hours, ghana_post_gps)
    setMobile(String(ef.mobile ?? ''));
    setWhatsapp(String(ef.whatsapp ?? ''));
    setContactEmail(String(ef.email ?? ''));
    setFacebook(String(ef.facebook ?? ''));
    setInstagram(String(ef.instagram ?? ''));
    setOpeningHours(String(ef.opening_hours ?? ''));
    setGhanaPostGps(String(ef.ghana_post_gps ?? ''));
    setDescriptionSourceUrl(String(ef.description_source_url ?? ''));
    if (ef.house_number) setHostHouseNumber(String(ef.house_number));
    else if (ef.building_house_number) setHostHouseNumber(String(ef.building_house_number));
    if (ef.frontage_street_address) {
      setHostFrontageStreetAddress(String(ef.frontage_street_address));
    } else if (ef.building_frontage_street_address) {
      setHostFrontageStreetAddress(String(ef.building_frontage_street_address));
    }
    const extraPatch = patchFromContributionExtras(editContribution.feature_type ?? '', ef);
    if (extraPatch.landmarkSourceGroup) {
      setSelectedLandmarkGroup(extraPatch.landmarkSourceGroup);
    }
    if (extraPatch.treeHeight) setTreeHeight(extraPatch.treeHeight);
    if (extraPatch.road) {
      setRoadSurface(extraPatch.road.surface);
      setRoadSmoothness(extraPatch.road.smoothness);
      setRoadCarriagewayCount(extraPatch.road.carriagewayCount);
      setRoadLanes(extraPatch.road.lanes);
      setRoadLanesForward(extraPatch.road.lanesForward);
      setRoadLanesBackward(extraPatch.road.lanesBackward);
      setRoadLaneDirections(extraPatch.road.laneDirections);
      setRoadManualLaneDirections(extraPatch.road.manualLaneDirections);
      setRoadCarriagewayALaneDirections(extraPatch.road.carriagewayALaneDirections);
      setRoadCarriagewayBLaneDirections(extraPatch.road.carriagewayBLaneDirections);
      setRoadManualDividedLaneDirections(extraPatch.road.carriagewayCount >= 2);
      if (extraPatch.road.width) setRoadWidth(extraPatch.road.width);
      if (extraPatch.road.carriagewayAWidth) setRoadCarriagewayAWidth(extraPatch.road.carriagewayAWidth);
      if (extraPatch.road.carriagewayBWidth) setRoadCarriagewayBWidth(extraPatch.road.carriagewayBWidth);
      setRoadToll(extraPatch.road.toll);
      setRoadFloodProne(extraPatch.road.floodProne);
      setRoadPotholes(extraPatch.road.potholes);
      setRoadStreetVendors(extraPatch.road.streetVendors);
      setRoadAccidentProne(extraPatch.road.accidentProne);
      setRoadHawkers(extraPatch.road.hawkers);
      setRoadLit(extraPatch.road.lit);
      setRoadDrainage(extraPatch.road.drainage);
      setRoadMaxspeed(extraPatch.road.maxspeed);
      setRoadCrossingType(extraPatch.road.crossingType);
      setRoadSideFacilities(extraPatch.road.sideFacilities);
      if (extraPatch.road.carriagewayCount >= 2) {
        setStripACaptureMode(parseStripCaptureMode(ef.strip_a_capture_mode));
        setStripBCaptureMode(parseStripCaptureMode(ef.strip_b_capture_mode));
        if (ef.divide_width_m != null && ef.divide_width_m !== '') {
          setDivideWidthM(String(ef.divide_width_m));
        }
        const divideGeom = parseStoredGeometry(ef.divide_geometry);
        if (isLineStringGeometry(divideGeom)) {
          setDividedDivideLine(divideGeom);
        }
        const stripModeA = parseStripCaptureMode(ef.strip_a_capture_mode);
        const stripModeB = parseStripCaptureMode(ef.strip_b_capture_mode);
        const refA = parseStoredGeometry(ef.strip_a_reference_geometry);
        if (isLineStringGeometry(refA)) {
          setStripReferenceA(refA);
        } else if (
          stripModeA === 'trace_centerline'
          && isLineStringGeometry(editContribution.geometry)
        ) {
          setStripReferenceA(editContribution.geometry);
        }
        const refB = parseStoredGeometry(ef.strip_b_reference_geometry);
        if (isLineStringGeometry(refB)) {
          setStripReferenceB(refB);
        } else if (
          stripModeB === 'trace_centerline'
          && isLineStringGeometry(ef.carriageway_b_geometry as GeoJSON.Geometry)
        ) {
          setStripReferenceB(ef.carriageway_b_geometry as GeoJSON.LineString);
        }
      } else {
        setSingleStripCaptureMode(parseStripCaptureMode(ef.strip_capture_mode));
        const singleRef = parseStoredGeometry(ef.strip_reference_geometry);
        if (isLineStringGeometry(singleRef)) {
          setSingleStripReference(singleRef);
        } else if (isLineStringGeometry(editContribution.geometry)) {
          setSingleStripReference(editContribution.geometry);
        }
      }
    }
    if (extraPatch.aeroway) {
      setAerowayRef(extraPatch.aeroway.ref);
      setAerowaySurface(extraPatch.aeroway.surface);
      setAerowayWidth(extraPatch.aeroway.width);
    }
    if (extraPatch.transportStop) {
      setStopRef(extraPatch.transportStop.ref);
      setStopRoutes(extraPatch.transportStop.routes);
      setStopOperators(extraPatch.transportStop.operators);
      setStopShelter(extraPatch.transportStop.shelter);
      setStopBench(extraPatch.transportStop.bench);
      setStopLighting(extraPatch.transportStop.lighting);
    }
    if (extraPatch.motorwayJunction) {
      setJunctionRef(extraPatch.motorwayJunction.ref);
      if (extraPatch.motorwayJunction.exitTo || extraPatch.motorwayJunction.exitToRoads) {
        setJunctionExitRoads(parseJunctionExitRoads(
          extraPatch.motorwayJunction.exitTo,
          extraPatch.motorwayJunction.exitToRoads,
        ));
      }
    }
    if (extraPatch.business) {
      setPoiCuisine(extraPatch.business.cuisine);
      setPoiBrand(extraPatch.business.brand);
      setPoiWheelchair(extraPatch.business.wheelchair);
      setPaymentMobileMoney(extraPatch.business.paymentMobileMoney);
      setPaymentCard(extraPatch.business.paymentCard);
      setMobileMoneyProvider(extraPatch.business.mobileMoneyProvider);
    }
    // Address: restore each structured field when present (saved since v070 migration).
    if (editUsesBuildingFormat) {
      const streetPrefill = resolveBuildingHostAddressPrefill({
        houseNumber: editHouseNumber,
        addrStreet: editContribution.addr_street,
        frontageStreetAddress: editFrontage || undefined,
      }).street;
      if (streetPrefill || editContribution.addr_street) setAddrStreet(streetPrefill);
    } else if (editContribution.addr_street && editFeatureTypeId !== 'road') {
      setAddrStreet(editContribution.addr_street);
    } else if (editFeatureTypeId === 'road') {
      setAddrStreet('');
    }
    {
      const locality = normalizeLocalityFields(
        editContribution.addr_suburb ?? '',
        editContribution.addr_city ?? '',
      );
      if (locality.suburb) setAddrSuburb(locality.suburb);
      if (locality.city) setAddrCity(locality.city);
    }
    if (editContribution.addr_district) setAddrDistrict(editContribution.addr_district);
    if (editContribution.addr_region) setAddrRegion(editContribution.addr_region);

    const restoredUnits = normalizeBuildingUnits(editContribution.building_units);
    restoreBuildingUnitsFromApi(restoredUnits);

    const hasStoredAddr =
      editContribution.addr_region ||
      editContribution.addr_district ||
      editContribution.addr_city ||
      editContribution.addr_suburb ||
      editContribution.addr_street;

    if (hasStoredAddr) {
      setAddressLookupStatus('ok');
    } else {
      // Legacy: reverse-geocode the geometry centroid for contributions without stored fields
      const coords = editContribution.geometry?.coordinates;
      let lat: number | null = null;
      let lng: number | null = null;
      if (geomType === 'Point' && Array.isArray(coords) && coords.length >= 2) {
        [lng, lat] = coords as [number, number];
      } else if ((geomType === 'Polygon' || geomType === 'MultiPolygon') && Array.isArray(coords)) {
        const ring = geomType === 'Polygon' ? (coords as number[][][])[0] : (coords as number[][][][])[0][0];
        if (ring?.length) { lng = ring[0][0]; lat = ring[0][1]; }
      }
      if (lat !== null && lng !== null) {
        reverseGeocodeForContribution(lat, lng)
          .then(async ({ result: r, suggestedFields }) => {
            if (!r) {
              setSuggestedAddressFields([]);
              setAddressLookupStatus('error');
              return;
            }
            setSuggestedAddressFields(suggestedFields);
            const incoming = extractReverseGeocodeComponents(r);
            const merged = mergeAddressFields(
              {
                street: editContribution.addr_street ?? '',
                suburb: editContribution.addr_suburb ?? '',
                city: editContribution.addr_city ?? '',
                district: editContribution.addr_district ?? '',
                region: editContribution.addr_region ?? '',
              },
              incoming,
            );
            const resolved = resolveCanonicalAdminFields(merged);
            const rgHouseNumber =
              editHouseNumber
              || extractReverseGeocodeBuildingNumber(r)
              || '';
            if (editUsesBuildingFormat && rgHouseNumber) setHostHouseNumber(rgHouseNumber);
            applyAddressFields(
              editUsesBuildingFormat
                ? {
                    ...resolved,
                    street: streetLineWithoutHouseNumber(resolved.street, rgHouseNumber),
                  }
                : resolved,
              {
              setStreet: setAddrStreet,
              setSuburb: setAddrSuburb,
              setCity: setAddrCity,
              setDistrict: setAddrDistrict,
              setRegion: setAddrRegion,
            });
            setAddressLookupStatus('ok');
          })
          .catch(() => {
            setAddressLookupStatus('ok');
          });
      }
    }
    setSubmitFeedback(null);
    setStep('details');
  }, [isOpen, editContribution, restoreBuildingUnitsFromApi]);

  const prefillFromMatchedFeature = useCallback((result: MapFeatureResult) => {
    const d = result.details;
    const featureId = result.feature_type || selectedFeatureType?.id;
    const usesBuildingFormat = usesBuildingHostAddressFormat(featureId);

    let houseNumber = '';
    if (featureId === 'building') {
      if (d.house_number) houseNumber = String(d.house_number);
      else if (d.addr_housenumber) houseNumber = String(d.addr_housenumber);
    } else if (featureId === 'poi' || featureId === 'landmark') {
      if (d.building_house_number) houseNumber = String(d.building_house_number);
    }

    if (usesBuildingFormat) {
      const buildingPrefill = resolveBuildingHostAddressPrefill({
        name: result.name,
        houseNumber,
        addrStreet: d.addr_street ? String(d.addr_street) : undefined,
        frontageStreetAddress:
          featureId === 'building' && d.frontage_street_address
            ? String(d.frontage_street_address)
            : featureId !== 'building' && d.building_frontage_street_address
              ? String(d.building_frontage_street_address)
              : undefined,
      });
      setName(buildingPrefill.name);
      if (buildingPrefill.houseNumber) setHostHouseNumber(buildingPrefill.houseNumber);
      if (buildingPrefill.street || d.addr_street) setAddrStreet(buildingPrefill.street);
    } else if (featureId === 'road') {
      const roadDisplay = resolveRoadEnrichDisplayName(result);
      if (roadDisplay) setName(roadDisplay);
      setAddrStreet('');
    } else {
      if (result.name) setName(result.name);
      if (d.addr_street) setAddrStreet(String(d.addr_street));
    }

    if (result.category) setSelectedCategory(result.category);
    if (d.description) setDescription(String(d.description));
    if (d.phone) setPhone(String(d.phone));
    {
      const locality = normalizeLocalityFields(
        d.addr_suburb ? String(d.addr_suburb) : '',
        d.addr_city ? String(d.addr_city) : '',
      );
      if (locality.suburb) setAddrSuburb(locality.suburb);
      if (locality.city) setAddrCity(locality.city);
    }
    if (d.district) setAddrDistrict(String(d.district));
    if (featureId === 'building') {
      if (d.frontage_street_address) setHostFrontageStreetAddress(String(d.frontage_street_address));
      if (d.building_units) restoreBuildingUnitsFromApi(d.building_units);
    } else if (featureId === 'poi' || featureId === 'landmark') {
      if (d.building_frontage_street_address) {
        setHostFrontageStreetAddress(String(d.building_frontage_street_address));
      }
    }
    if (placeHasStoredAddress({
      addrStreet: d.addr_street ? String(d.addr_street) : undefined,
      addrSuburb: d.addr_suburb ? String(d.addr_suburb) : undefined,
      addrCity: d.addr_city ? String(d.addr_city) : undefined,
      district: d.district ? String(d.district) : undefined,
    })) {
      setAddressLookupStatus('ok');
    }
    if (!isGssBoundaryPlaceCategory(result.category)) {
      onHighlightGeometry?.(result.geometry as unknown as GeoJSON.Geometry);
    }
  }, [onHighlightGeometry, restoreBuildingUnitsFromApi]);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    if (
      selectedFeatureType?.id === 'place' &&
      enrichFeature &&
      value.trim().toLowerCase() !== (enrichFeature.name ?? '').trim().toLowerCase()
    ) {
      setEnrichFeature(null);
      onClearHighlight?.();
    }
  }, [selectedFeatureType, enrichFeature, onClearHighlight]);

  // Enrich: when user clicks map in enrich_select step, find nearest feature
  useEffect(() => {
    if (step !== 'enrich_select' || !selectedLocation || !selectedFeatureType) return;

    const tapKey = `${selectedLocation.lat.toFixed(7)},${selectedLocation.lng.toFixed(7)}`;
    if (enrichTapRef.current === tapKey) return;
    enrichTapRef.current = tapKey;

    let cancelled = false;
    setEnrichLoading(true);
    setSubmitFeedback(null);

    (async () => {
      try {
        const result = await findNearestFeature(
          selectedLocation.lat,
          selectedLocation.lng,
          enrichSearchFeatureType ?? selectedFeatureType.id,
          100,
        );
        if (cancelled) return;
        if (!result) {
          enrichTapRef.current = null;
          const searchType = enrichSearchFeatureType ?? selectedFeatureType.id;
          const insideHint = ENRICH_CONTAIN_FEATURE_TYPES.has(searchType)
            ? 'Tap inside the polygon you want to update.'
            : 'Tap on or closer to the feature.';
          setSubmitFeedback({
            type: 'error',
            message: `No ${enrichTargetLabel.toLowerCase()} found at that spot. ${insideHint} The shape you see may be a different feature type — go back and pick the correct type if needed.`,
          });
          setEnrichLoading(false);
          return;
        }
        setEnrichFeature(result);
        prefillFromMatchedFeature(result);
        setAddressLookupStatus('idle');
        setAddressLookupKey((k) => k + 1);
        const d = result.details;
        const meta = d.metadata as Record<string, unknown> | undefined;
        if (d.surface) setRoadSurface(String(d.surface));
        if (d.smoothness) setRoadSmoothness(String(d.smoothness));
        setRoadCarriagewayCount(inferCarriagewayCountFromDetails({
          parent_road_id: typeof meta?.parent_road_id === 'string' ? meta.parent_road_id : null,
          carriageway_group_id: typeof meta?.carriageway_group_id === 'string' ? meta.carriageway_group_id : null,
        }));
        if (d.lanes != null && d.lanes !== '') setRoadLanes(String(d.lanes));
        if (d.lanes_forward != null && d.lanes_forward !== '') setRoadLanesForward(String(d.lanes_forward));
        if (d.lanes_backward != null && d.lanes_backward !== '') setRoadLanesBackward(String(d.lanes_backward));
        const laneDirs = parseLaneDirections(meta?.lane_directions ?? d.lane_directions);
        if (laneDirs.length) {
          setRoadLaneDirections(laneDirs);
          setRoadManualLaneDirections(true);
        }
        if (d.toll !== undefined) setRoadToll(Boolean(d.toll));
        if (d.flood_prone !== undefined) setRoadFloodProne(Boolean(d.flood_prone));
        if (d.potholes !== undefined) setRoadPotholes(Boolean(d.potholes));
        if (d.street_vendors !== undefined) setRoadStreetVendors(Boolean(d.street_vendors));
        if (d.accident_prone !== undefined) setRoadAccidentProne(Boolean(d.accident_prone));
        if (d.hawkers !== undefined) setRoadHawkers(Boolean(d.hawkers));
        if (d.lit !== undefined) setRoadLit(Boolean(d.lit));
        if (d.drainage !== undefined) setRoadDrainage(Boolean(d.drainage));
        if (d.maxspeed) setRoadMaxspeed(String(d.maxspeed));
        if (d.crossing_type) setRoadCrossingType(String(d.crossing_type));
        if (d.width != null && d.width !== '') setRoadWidth(String(d.width));
        if (d.height != null && d.height !== '') setTreeHeight(String(d.height));
        if (d.ref) {
          if (selectedFeatureType?.id === 'aeroway') setAerowayRef(String(d.ref));
          else if (selectedFeatureType?.id === 'motorway_junction') setJunctionRef(String(d.ref));
        }
        if (d.surface) setAerowaySurface(String(d.surface));
        if (d.width != null && d.width !== '') setAerowayWidth(String(d.width));
        if (d.exit_to || d.exit_to_roads) {
          setJunctionExitRoads(parseJunctionExitRoads(
            d.exit_to ? String(d.exit_to) : undefined,
            Array.isArray(d.exit_to_roads) ? d.exit_to_roads as Array<{ location_id?: string; name?: string }> : undefined,
          ));
        }
        // Disable map click, go to details
        onEnableMapClick(false);
        setStep('details');
      } catch {
        if (!cancelled) {
          setSubmitFeedback({ type: 'error', message: 'Failed to find feature. Please try again.' });
        }
      } finally {
        if (!cancelled) setEnrichLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [step, selectedLocation?.lat, selectedLocation?.lng, selectedFeatureType?.id, enrichSearchFeatureType, enrichTargetLabel, prefillFromMatchedFeature, onEnableMapClick]);

  const resetEnclosureFlow = useCallback(() => {
    setEnclosureMethod(null);
    setEnclosureDetectResult(null);
    setEnclosureSelectedCandidateId(null);
    setEnclosureLoading(false);
    setEnclosureFeedback(null);
    setEnclosureBoundaryRoadIds([]);
    setEnclosurePreviewAreaSqm(null);
    setEnclosureConfirmedGeometry(null);
    enclosureTapRef.current = null;
    enclosureBoundaryTapRef.current = null;
    enclosureFromDetectionRef.current = false;
    onEnableMapClick(false);
    onEnableDrawing(false);
    onClearHighlight?.();
    onPreviewGeometry?.(null);
  }, [onClearHighlight, onEnableDrawing, onEnableMapClick, onPreviewGeometry]);

  const startEnclosureDetection = () => {
    if (!isAuthenticated) {
      requireAuth(startEnclosureDetection);
      return;
    }
    resetEnclosureFlow();
    onClearGeometry();
    setStep('enclosure_method');
  };

  const beginEnclosureMapStep = (method: EnclosureMethod) => {
    setEnclosureMethod(method);
    setEnclosureDetectResult(null);
    setEnclosureSelectedCandidateId(null);
    setEnclosurePreviewAreaSqm(null);
    setEnclosureConfirmedGeometry(null);
    setEnclosureFeedback(null);
    enclosureTapRef.current = null;
    enclosureBoundaryTapRef.current = null;
    setEnclosureBoundaryRoadIds([]);
    setStep('enclosure_map');
    onLockZoom?.(SURVEY_MIN_ZOOM);
    const anchor = selectedLocation ?? userLocation;
    if (anchor) {
      onFlyTo?.({ lat: anchor.lat, lng: anchor.lng }, SURVEY_MIN_ZOOM);
    }
    if (method === 'point' || method === 'boundary') {
      onEnableDrawing(false);
      onEnableMapClick(true);
    } else {
      onEnableMapClick(false);
      onClearGeometry();
      onEnableDrawing(false);
      setStep('enclosure_capture_mode');
    }
  };

  const enterEnclosureMarking = (mode: GeometryMode) => {
    setLocationPlacementError(null);
    if (mode === 'draw' || mode === 'trace') {
      setSheetSnap('compact');
    }
    onLockZoom?.(SURVEY_MIN_ZOOM);
    const anchor = selectedLocation ?? userLocation;
    if (anchor) {
      onFlyTo?.({ lat: anchor.lat, lng: anchor.lng }, SURVEY_MIN_ZOOM);
    }
    if (mode === 'draw') {
      onEnableDrawing(true, { lineMode: false });
    } else {
      onEnableDrawing(false);
    }
  };

  const handleEnclosureCaptureModeSelect = (mode: 'draw' | 'trace') => {
    onClearGeometry();
    setGeometryMode(mode);
    onTraceContextChange?.({ featureTypeId: 'landuse', landmarkGroup: null });
    setStep('enclosure_marking');
    enterEnclosureMarking(mode);
  };

  const runEnclosurePointDetect = useCallback(async (lat: number, lng: number) => {
    setEnclosureLoading(true);
    setEnclosureFeedback(null);
    try {
      const result = await detectEnclosure({ mode: 'point', lat, lng });
      setEnclosureDetectResult(result);
      if (result.outcome === 'derived_candidates' && result.candidates.length === 1) {
        setEnclosureSelectedCandidateId(result.candidates[0].candidate_id);
      }
      if (result.outcome === 'existing_polygon' && result.existing?.geometry) {
        onHighlightGeometry?.(result.existing.geometry as unknown as GeoJSON.Geometry);
      } else if (result.outcome === 'derived_candidates' && result.candidates.length === 1) {
        onPreviewGeometry?.(result.candidates[0].geometry as unknown as GeoJSONGeometry);
      }
      setStep('enclosure_preview');
    } catch {
      setEnclosureFeedback('Could not analyse geometry. Try again or draw manually.');
    } finally {
      setEnclosureLoading(false);
    }
  }, [onHighlightGeometry, onPreviewGeometry]);

  const runEnclosureBoundaryDetect = useCallback(async () => {
    if (!selectedLocation || enclosureBoundaryRoadIds.length === 0) {
      setEnclosureFeedback('Select at least one boundary road on the map.');
      return;
    }
    setEnclosureLoading(true);
    setEnclosureFeedback(null);
    try {
      const result = await detectEnclosure({
        mode: 'boundary_roads',
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        road_ids: enclosureBoundaryRoadIds,
      });
      setEnclosureDetectResult(result);
      if (result.outcome === 'derived_candidates' && result.candidates.length === 1) {
        setEnclosureSelectedCandidateId(result.candidates[0].candidate_id);
      }
      setStep('enclosure_preview');
    } catch {
      setEnclosureFeedback('Could not derive an enclosure from those roads.');
    } finally {
      setEnclosureLoading(false);
    }
  }, [enclosureBoundaryRoadIds, selectedLocation]);

  useEffect(() => {
    if (step !== 'enclosure_map' || enclosureMethod !== 'point' || !selectedLocation) return;
    const tapKey = `${selectedLocation.lat.toFixed(7)},${selectedLocation.lng.toFixed(7)}`;
    if (enclosureTapRef.current === tapKey) return;
    enclosureTapRef.current = tapKey;
    void runEnclosurePointDetect(selectedLocation.lat, selectedLocation.lng);
  }, [step, enclosureMethod, selectedLocation?.lat, selectedLocation?.lng, runEnclosurePointDetect]);

  useEffect(() => {
    if (step !== 'enclosure_map' || enclosureMethod !== 'boundary' || !selectedLocation) return;
    const tapKey = `${selectedLocation.lat.toFixed(7)},${selectedLocation.lng.toFixed(7)}`;
    if (enclosureBoundaryTapRef.current === tapKey) return;
    enclosureBoundaryTapRef.current = tapKey;

    let cancelled = false;
    (async () => {
      try {
        const road = await findNearestFeature(
          selectedLocation.lat,
          selectedLocation.lng,
          'road',
          40,
        );
        if (cancelled) return;
        if (!road) {
          enclosureBoundaryTapRef.current = null;
          setEnclosureFeedback('No road found at that spot. Tap closer to a road centreline.');
          return;
        }
        setEnclosureBoundaryRoadIds((prev) =>
          prev.includes(road.table_id) ? prev : [...prev, road.table_id],
        );
        setEnclosureFeedback(null);
      } catch {
        if (!cancelled) {
          enclosureBoundaryTapRef.current = null;
          setEnclosureFeedback('Could not select that road. Try again.');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [step, enclosureMethod, selectedLocation?.lat, selectedLocation?.lng]);

  useEffect(() => {
    if (!isOpen || step !== 'enclosure_preview') return;
    if (enclosurePreviewAreaSqm != null && enclosureConfirmedGeometry) {
      onPreviewGeometry?.(enclosureConfirmedGeometry);
      return;
    }
    if (enclosureDetectResult?.outcome === 'existing_polygon' && enclosureDetectResult.existing?.geometry) {
      onHighlightGeometry?.(enclosureDetectResult.existing.geometry as unknown as GeoJSON.Geometry);
    }
  }, [
    isOpen,
    step,
    enclosurePreviewAreaSqm,
    enclosureConfirmedGeometry,
    enclosureDetectResult,
    onHighlightGeometry,
    onPreviewGeometry,
  ]);

  useEffect(() => {
    if (step !== 'enclosure_preview' || enclosurePreviewAreaSqm != null) return;
    const candidates = enclosureDetectResult?.candidates ?? [];
    if (enclosureDetectResult?.outcome !== 'derived_candidates' || candidates.length !== 1) return;
    const only = candidates[0];
    setEnclosureSelectedCandidateId(only.candidate_id);
    setEnclosureConfirmedGeometry(only.geometry);
    setEnclosurePreviewAreaSqm(only.area_sqm);
    onPreviewGeometry?.(only.geometry);
    onClearHighlight?.();
  }, [
    step,
    enclosurePreviewAreaSqm,
    enclosureDetectResult,
    onClearHighlight,
    onPreviewGeometry,
  ]);

  const handleEnclosureRetry = () => {
    const pickNewMethod =
      !enclosureMethod
      || enclosureDetectResult?.outcome === 'none'
      || enclosureDetectResult?.outcome === 'existing_polygon';
    if (pickNewMethod) {
      setEnclosureDetectResult(null);
      setEnclosureSelectedCandidateId(null);
      setEnclosurePreviewAreaSqm(null);
      setEnclosureConfirmedGeometry(null);
      setEnclosureFeedback(null);
      setStep('enclosure_method');
      return;
    }
    if (enclosureMethod) {
      beginEnclosureMapStep(enclosureMethod);
    }
  };

  const handleEnclosurePreviewSelected = () => {
    let candidate = enclosureDetectResult?.candidates.find(
      (c) => c.candidate_id === enclosureSelectedCandidateId,
    );
    if (!candidate && enclosureDetectResult?.candidates.length === 1) {
      candidate = enclosureDetectResult.candidates[0];
    }
    if (!candidate) return;
    setEnclosureConfirmedGeometry(candidate.geometry);
    setEnclosurePreviewAreaSqm(candidate.area_sqm);
    onPreviewGeometry?.(candidate.geometry);
    onClearHighlight?.();
  };

  const handleEnclosureConfirmBoundary = () => {
    if (!enclosureConfirmedGeometry) return;
    onSetDrawnGeometry?.(enclosureConfirmedGeometry as GeoJSON.Geometry);
    setStep('enclosure_feature_type');
  };

  const handleEnclosureEnrichExisting = () => {
    const ex = enclosureDetectResult?.existing;
    if (!ex) return;
    const ft = FEATURE_TYPES.find((f) => f.id === ex.feature_type);
    if (!ft) return;
    setSelectedFeatureType(ft);
    setGeometryMode('enrich');
    const matched: MapFeatureResult = {
      feature_type: ex.feature_type,
      table_id: ex.table_id,
      name: ex.name ?? undefined,
      category: ex.category ?? undefined,
      geometry: ex.geometry,
      distance_m: 0,
      details: {},
    };
    setEnrichFeature(matched);
    if (ex.category) setSelectedCategory(ex.category);
    prefillFromMatchedFeature(matched);
    onEnableMapClick(false);
    onEnableDrawing(false);
    onHighlightGeometry?.(ex.geometry as unknown as GeoJSON.Geometry);
    resetEnclosureFlow();
    setStep('details');
  };

  const handleEnclosureTargetFeatureType = (featureTypeId: string) => {
    if (!ENCLOSURE_POLYGON_FEATURE_IDS.includes(featureTypeId as (typeof ENCLOSURE_POLYGON_FEATURE_IDS)[number])) {
      return;
    }
    const ft = FEATURE_TYPES.find((f) => f.id === featureTypeId);
    if (!ft || !enclosureConfirmedGeometry) return;
    setSelectedFeatureType(ft);
    setGeometryMode('draw');
    enclosureFromDetectionRef.current = true;
    onSetDrawnGeometry?.(enclosureConfirmedGeometry as GeoJSON.Geometry);
    onPreviewGeometry?.(enclosureConfirmedGeometry);
    onEnableDrawing(false);
    setStep('category');
  };


  const parseLinkUrl = (input: string): { lat: number; lng: number } | null => {
    const s = input.trim();
    // !3d{lat}!4d{lng} — exact place-pin coords in Maps data segment.
    // A URL can contain multiple such pairs (parent entities first, actual place last).
    // Use the LAST match so we get the specific place, not a parent region.
    const dataMatches = [...s.matchAll(/!3d([-\d.]+)!4d([-\d.]+)/g)];
    if (dataMatches.length > 0) {
      const last = dataMatches[dataMatches.length - 1];
      const lat = parseFloat(last[1]);
      const lng = parseFloat(last[2]);
      if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
    }
    // OpenStreetMap URLs: #map=zoom/lat/lng or ?lat=&lon=
    if (s.includes('openstreetmap.org')) {
      const hashMatch = s.match(/#map=\d+\/([-\d.]+)\/([-\d.]+)/);
      if (hashMatch) {
        const lat = parseFloat(hashMatch[1]);
        const lng = parseFloat(hashMatch[2]);
        if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
      }
      try {
        const osmUrl = new URL(s);
        const osmLat = osmUrl.searchParams.get('lat');
        const osmLon = osmUrl.searchParams.get('lon');
        if (osmLat && osmLon) {
          const lat = parseFloat(osmLat);
          const lng = parseFloat(osmLon);
          if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
        }
      } catch { }
    }
    // /@lat,lng — map viewport
    const atMatch = s.match(/@([-\d.]+),([-\d.]+)/);
    if (atMatch) {
      const lat = parseFloat(atMatch[1]);
      const lng = parseFloat(atMatch[2]);
      if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
    }
    // ?q=lat,lng or ?ll=lat,lng
    try {
      const url = new URL(s.startsWith('http') ? s : `https://maps.google.com/?q=${s}`);
      for (const param of ['q', 'll', 'query']) {
        const val = url.searchParams.get(param);
        if (val) {
          const parts = val.split(',');
          if (parts.length >= 2) {
            const lat = parseFloat(parts[0]);
            const lng = parseFloat(parts[1]);
            if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
          }
        }
      }
    } catch { }
    // bare lat,lng
    const bare = s.match(/^([-\d.]+)[,\s]+([-\d.]+)$/);
    if (bare) {
      const lat = parseFloat(bare[1]);
      const lng = parseFloat(bare[2]);
      if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };
    }
    return null;
  };

  const handleLinkPlot = () => {
    setLinkError(null);
    const parsed = parseLinkUrl(linkInput);
    if (!parsed) {
      setLinkError('Could not read coordinates from that link. Try pasting the full map link.');
      return;
    }
    if (parsed.lat < -90 || parsed.lat > 90 || parsed.lng < -180 || parsed.lng > 180) {
      setLinkError('Coordinates are out of range.');
      return;
    }
    onSetLocation?.({ lat: parsed.lat, lng: parsed.lng });
    onPlaceMarker?.(parsed.lat, parsed.lng);
    onFlyTo?.({ lat: parsed.lat, lng: parsed.lng }, SURVEY_MIN_ZOOM);
  };

  const enterMarking = (mode: GeometryMode) => {
    setStep('marking');
    setLocationPlacementError(null);
    if (mode === 'draw' || mode === 'trace') {
      setSheetSnap('compact');
    } else if (mode === 'point') {
      setSheetSnap('half');
    }
    if (mode === 'coordinates' || mode === 'link') return;

    onLockZoom?.(SURVEY_MIN_ZOOM);

    const anchor = selectedLocation ?? userLocation;
    if (anchor) {
      onFlyTo?.({ lat: anchor.lat, lng: anchor.lng }, SURVEY_MIN_ZOOM);
    }

    if (mode === 'point') {
      onEnableDrawing(false);
      onEnableMapClick(true);
      return;
    }

    onEnableMapClick(false);

    if (mode === 'draw') {
      onEnableDrawing(true, { lineMode: !drawProducesPolygon(markingGeometryFeatureType) });
      if (editContribution && drawnGeometry) {
        window.setTimeout(() => requestHydrateMarkingGeometry(), 60);
      }
    }
    if (mode === 'trace') {
      onEnableDrawing(false);
      onClearHighlight?.();
      if (editContribution && tracePoints.length > 0) {
        window.setTimeout(() => requestHydrateMarkingGeometry(), 0);
      }
    }
  };

  const leaveMarking = () => {
    onEnableMapClick(false);
    // Keep drawing visible — only disable new draw interactions
    onUnlockZoom?.();
  };

  const resolveContributionLineGeometry = useCallback(
    () =>
      resolveContributionLineFromSources({
        geometryMode,
        drawnGeometry,
        tracePoints,
        traceReady,
        markingGeometryFeatureType,
      }),
    [geometryMode, drawnGeometry, tracePoints, traceReady, markingGeometryFeatureType],
  );

  const roadSession = useRoadMarkingSession({
    isOpen,
    motorRoadSurveyFlow,
    roadCarriagewayCount,
    setRoadCarriagewayCount,
    roadWidth,
    roadCarriagewayAWidth,
    roadCarriagewayBWidth,
    setRoadLaneDirections,
    setRoadManualLaneDirections,
    setRoadCarriagewayALaneDirections,
    setRoadCarriagewayBLaneDirections,
    geometryMode,
    drawnGeometry,
    tracePoints,
    traceReady,
    markingGeometryFeatureType,
    isTracing,
    setStep: (s: string) => setStep(s as ContributionStep),
    setSheetSnap,
    setGeometryMode: (mode: GeometryMode) => setGeometryMode(mode),
    setSubmitFeedback,
    enterMarking,
    leaveMarking,
    requestAddressLookup,
    onPreviewGuideGeometries,
    onPreviewGeometry,
    onClearGeometry,
    onDrawTrash,
    onEnableDrawing,
    onStopTrace,
    campaignTraceAutoStartRef,
    resolveContributionLineGeometry,
    onHydrateMarkingGeometry: requestHydrateMarkingGeometry,
  });

  const {
    roadMarkingTargetRef,
    roadCapturePlanError,
    setRoadCapturePlanError,
    singleStripCaptureMode,
    setSingleStripCaptureMode,
    stripACaptureMode,
    setStripACaptureMode,
    stripBCaptureMode,
    setStripBCaptureMode,
    divideWidthM,
    setDivideWidthM,
    dividedDivideLine,
    setDividedDivideLine,
    stripReferenceA,
    setStripReferenceA,
    stripReferenceB,
    setStripReferenceB,
    singleStripReference,
    setSingleStripReference,
    awaitingCarriagewayB,
    setAwaitingCarriagewayB,
    dividedSurveySetupHint,
    setDividedSurveySetupHint,
    dividedCarriagewayBGeom,
    dividedCarriagewayCentreA,
    stripATraceReady,
    stripBTraceReady,
    dividedCarriagewayAReady,
    dividedCarriagewayBReady,
    dividedCarriagewayReady,
    singleStripTraceReady,
    roadGeometryStatusRows,
    resetMarkingSession,
    handleStripACaptureModeChange,
    handleStripBCaptureModeChange,
    handleSingleStripCaptureModeChange,
    handleStartMarkingDivide,
    handleStartMarkingStripA,
    handleStartMarkingStripB,
    handleStartMarkingSingleStrip,
    handleEditRoadGeometry,
    handleRoadLayoutSelect,
    handleRoadCapturePlanContinue,
    handleRoadCarriagewayCountChange,
    confirmRoadMarkingCapture,
  } = roadSession;

  const canReopenContributionGeometryCapture =
    !!editContribution
    && geometryMode != null
    && geometryMode !== 'enrich'
    && geometryMode !== 'coordinates'
    && geometryMode !== 'link';

  const handleEditContributionGeometry = useCallback(() => {
    if (!canReopenContributionGeometryCapture || !geometryMode) return;
    setSubmitFeedback(null);
    if (
      selectedFeatureType?.id === 'road'
      && motorRoadSurveyFlow
      && roadCarriagewayCount != null
    ) {
      handleEditRoadGeometry();
      window.setTimeout(() => requestHydrateMarkingGeometry(), 60);
      return;
    }
    if (geometryMode === 'draw' || geometryMode === 'trace') {
      setSheetSnap('compact');
      enterMarking(geometryMode);
      if (!motorRoadSurveyFlow || roadCarriagewayCount == null) {
        window.setTimeout(() => requestHydrateMarkingGeometry(), 60);
      }
      return;
    }
    if (geometryMode === 'point') {
      enterMarking('point');
    }
  }, [
    canReopenContributionGeometryCapture,
    geometryMode,
    selectedFeatureType?.id,
    motorRoadSurveyFlow,
    roadCarriagewayCount,
    handleEditRoadGeometry,
    setSheetSnap,
    enterMarking,
    requestHydrateMarkingGeometry,
  ]);

  const handleSheetDragStart = () => {
    sheetDragging.current = true;
  };

  const handleSheetDragEnd = (_: unknown, info: PanInfo) => {
    sheetDragging.current = false;
    const velocity = info.velocity.y;
    const offset = info.offset.y;
    const currentHeight = sheetRef.current?.offsetHeight || 0;
    const draggedRatio = (currentHeight - offset) / window.innerHeight;
    const currentIndex = SHEET_SNAP_ORDER.indexOf(sheetSnap);

    if (velocity < -400 && currentIndex < SHEET_SNAP_ORDER.length - 1) {
      applySheetSnap(SHEET_SNAP_ORDER[currentIndex + 1]);
      return;
    }
    if (velocity > 400 && currentIndex > 0) {
      applySheetSnap(SHEET_SNAP_ORDER[currentIndex - 1]);
      return;
    }

    let nearest: ContributionSheetSnap = 'half';
    let nearestDist = Number.POSITIVE_INFINITY;
    for (const snap of SHEET_SNAP_ORDER) {
      const dist = Math.abs(draggedRatio - ADD_DETAILS_SHEET_SNAP[snap]);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = snap;
      }
    }
    applySheetSnap(nearest);
  };

  const handleSheetHandleClick = () => {
    if (sheetDragging.current) return;
    const idx = SHEET_SNAP_ORDER.indexOf(sheetSnap);
    applySheetSnap(SHEET_SNAP_ORDER[Math.min(idx + 1, SHEET_SNAP_ORDER.length - 1)]);
  };

  const handleClose = () => {
    resetEnclosureFlow();
    onEnableMapClick(false);
    onEnableDrawing(false);
    onUnlockZoom?.();
    onClearGeometry({ keepTrace: true });
    resetMarkingSession();
    resetRoadDetailsWidths();
    onPreviewGuideGeometries?.(null);
    onClose();
  };

  const roadFlowValidationFields = useMemo(
    () => ({
      carriagewayCount: roadCarriagewayCount,
      lanes: roadLanes,
      lanesForward: roadLanesForward,
      lanesBackward: roadLanesBackward,
      laneDirections: roadLaneDirections,
      manualLaneDirections: roadManualLaneDirections,
      roadWidth,
      divideWidthM,
      singleStripCaptureMode: isMultiCarriageway(roadCarriagewayCount) ? undefined : singleStripCaptureMode,
      singleStripTraceReady: isMultiCarriageway(roadCarriagewayCount) ? undefined : singleStripTraceReady,
      stripACaptureMode: isMultiCarriageway(roadCarriagewayCount) ? stripACaptureMode : undefined,
      stripBCaptureMode: isMultiCarriageway(roadCarriagewayCount) ? stripBCaptureMode : undefined,
      stripATraceReady: isMultiCarriageway(roadCarriagewayCount) ? stripATraceReady : undefined,
      stripBTraceReady: isMultiCarriageway(roadCarriagewayCount) ? stripBTraceReady : undefined,
      dividedDivideReady: isMultiCarriageway(roadCarriagewayCount) ? !!dividedDivideLine : undefined,
      dividedCarriagewayBReady: isMultiCarriageway(roadCarriagewayCount) ? dividedCarriagewayReady : undefined,
      carriagewayAWidth: roadCarriagewayAWidth,
      carriagewayBWidth: roadCarriagewayBWidth,
      carriagewayALaneDirections: roadCarriagewayALaneDirections,
      carriagewayBLaneDirections: roadCarriagewayBLaneDirections,
      manualDividedLaneDirections: roadManualDividedLaneDirections,
      usePerLaneWidths: roadUsePerLaneWidths,
      laneWidthsM: roadLaneWidthsM,
      carriagewayALaneWidthsM: roadCarriagewayALaneWidthsM,
      carriagewayBLaneWidthsM: roadCarriagewayBLaneWidthsM,
    }),
    [
      roadCarriagewayCount,
      roadLanes,
      roadLanesForward,
      roadLanesBackward,
      roadLaneDirections,
      roadManualLaneDirections,
      roadWidth,
      divideWidthM,
      singleStripCaptureMode,
      singleStripTraceReady,
      stripACaptureMode,
      stripBCaptureMode,
      stripATraceReady,
      stripBTraceReady,
      dividedDivideLine,
      dividedCarriagewayReady,
      roadCarriagewayAWidth,
      roadCarriagewayBWidth,
      roadCarriagewayALaneDirections,
      roadCarriagewayBLaneDirections,
      roadManualDividedLaneDirections,
      roadUsePerLaneWidths,
      roadLaneWidthsM,
      roadCarriagewayALaneWidthsM,
      roadCarriagewayBLaneWidthsM,
    ],
  );

  const handleUseMyLocation = useCallback(async () => {
    if (!onUseMyLocation) return;
    setIsLocating(true);
    setLocationPlacementError(null);
    try {
      await onUseMyLocation();
    } catch {
      setLocationPlacementError('Unable to get your location. Enable location services or tap the map.');
    } finally {
      setIsLocating(false);
    }
  }, [onUseMyLocation]);

  const handleAddTraceCorner = useCallback(async () => {
    if (!onAddTraceCorner) return;
    setCornerCaptureError(null);
    try {
      await onAddTraceCorner();
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Unable to get your location. Enable location services and try again.';
      setCornerCaptureError(message);
    }
  }, [onAddTraceCorner]);

  const applyCampaignFieldCaptureType = (ft: FeatureTypeOption): boolean => {
    if (!campaignFieldCapture) return false;

    if (ft.allowedModes.length === 1) {
      const mode = ft.allowedModes[0];
      if (mode !== 'point') setGeometryMode(mode);
      if (ft.needsCategory) {
        if (ft.id === 'landmark') setStep('landmark_group');
        else setStep('category');
      } else if (mode === 'point' && selectedLocation) {
        onFlyTo?.(selectedLocation, SURVEY_MIN_ZOOM);
        onPlaceMarker?.(selectedLocation.lat, selectedLocation.lng);
        requestAddressLookup();
        setStep('details');
      } else if (mode === 'trace') {
        setStep('marking');
      } else {
        enterMarking(mode);
      }
      return true;
    }

    if (ft.id === 'road' && ft.needsCategory) {
      setStep('category');
      return true;
    }

    setStep('mode');
    return true;
  };

  const handleFeatureTypeSelect = (ft: FeatureTypeOption) => {
    if (!isAuthenticated) {
      requireAuth(() => handleFeatureTypeSelect(ft));
      return;
    }
    setSelectedFeatureType(ft);
    if (ft.id === 'road') {
      setAddrStreet('');
    }
    if (applyCampaignFieldCaptureType(ft)) {
      return;
    }
    if (ft.id === 'official_area') {
      setStep('official_select');
      return;
    }
    // Places: pick type (town vs suburb) before choosing how to mark location
    if (ft.id === 'place' && ft.needsCategory) {
      setStep('category');
      return;
    }
    // Landmarks: pick source table group before subtype (historic, water, poi, …)
    if (ft.id === 'landmark') {
      setStep('landmark_group');
      return;
    }
    if (ft.id === 'road' && ft.needsCategory) {
      setStep('category');
      return;
    }
    // If only one mode is allowed, skip mode selection (except point — offer placement options)
    if (ft.allowedModes.length === 1) {
      const mode = ft.allowedModes[0];
      if (mode === 'point') {
        if (ft.needsCategory) {
          setStep('category');
        } else {
          setStep('mode');
        }
      } else {
        setGeometryMode(mode);
        if (ft.needsCategory) {
          setStep('category');
        } else {
          enterMarking(mode);
        }
      }
    } else {
      setStep('mode');
    }
  };

  const handleModeSelect = (mode: GeometryMode) => {
    if (!isAuthenticated) {
      requireAuth(() => handleModeSelect(mode));
      return;
    }

    // Switching modes must not leave stale enrich vs trace/draw state attached to submit.
    if (mode === 'enrich') {
      clearMarkingGeometryState();
      onEnableDrawing(false);
    } else {
      clearEnrichSelectionState();
      if (
        (mode === 'trace' || mode === 'draw') &&
        (geometryMode === 'trace' || geometryMode === 'draw') &&
        mode !== geometryMode
      ) {
        clearMarkingGeometryState();
      }
    }

    setGeometryMode(mode);

    if (campaignFieldCapture && mode === 'point' && selectedLocation) {
      onFlyTo?.(selectedLocation, SURVEY_MIN_ZOOM);
      onPlaceMarker?.(selectedLocation.lat, selectedLocation.lng);
      if (selectedFeatureType?.needsCategory) {
        if (selectedFeatureType?.id === 'landmark') setStep('landmark_group');
        else setStep('category');
      } else {
        requestAddressLookup();
        setStep('details');
      }
      return;
    }

    if (campaignFieldCapture && mode === 'trace') {
      if (selectedFeatureType?.needsCategory) {
        if (selectedFeatureType?.id === 'landmark') setStep('landmark_group');
        else setStep('category');
      } else {
        setStep('marking');
      }
      return;
    }

    // Enrich mode — landmark picks source group first; others go straight to map tap
    if (mode === 'enrich') {
      if (selectedFeatureType?.id === 'landmark') {
        setStep('landmark_group');
        return;
      }
      onEnableMapClick(true);
      setSheetSnap('expanded');
      setStep('enrich_select');
      return;
    }

    // Landmark new geometry after category (draw/trace/point for source table)
    if (selectedFeatureType?.id === 'landmark' && step === 'mode') {
      enterMarking(mode);
      return;
    }

    // Category already chosen — placement step after category (POI, traffic control, etc.)
    if (step === 'mode' && selectedCategory) {
      if (motorRoadSurveyFlow && (mode === 'draw' || mode === 'trace')) {
        setRoadCapturePlanError(null);
        setStep('road_capture_plan');
        return;
      }
      enterMarking(mode);
      return;
    }

    // Skip category for feature types that don't need it (place picks category before mode)
    if (!selectedFeatureType?.needsCategory || selectedFeatureType?.id === 'place') {
      enterMarking(mode);
    } else {
      setStep('category');
    }
  };

  const handleLandmarkGroupSelect = (groupId: string) => {
    setSelectedLandmarkGroup(groupId);
    setCategorySearch('');
    setStep('landmark_placement');
  };

  const handleLandmarkPlacementSelect = (placement: 'enrich' | 'new') => {
    if (placement === 'enrich') {
      setGeometryMode('enrich');
      onEnableMapClick(true);
      setSheetSnap('expanded');
      setStep('enrich_select');
      return;
    }
    setStep('category');
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (enclosureFromDetectionRef.current && enclosureConfirmedGeometry) {
      enclosureFromDetectionRef.current = false;
      onEnableDrawing(false);
      onUnlockZoom?.();
      requestAddressLookup();
      setSheetSnap('half');
      setStep('details');
      return;
    }
    if (
      selectedFeatureType?.id === 'road'
      && usesMotorRoadSurveyFlow('road', categoryId)
      && !editContribution
      && geometryMode !== 'enrich'
    ) {
      setStep('road_layout');
      return;
    }
    if (selectedFeatureType?.id === 'road') {
      setStep('mode');
      return;
    }
    if (selectedFeatureType?.id === 'place') {
      setStep('mode');
      return;
    }
    if (selectedFeatureType?.id === 'landmark' && selectedLandmarkGroup) {
      const modes = landmarkNewGeometryModes(selectedLandmarkGroup);
      if (modes.length === 1 && modes[0] === 'point') {
        setStep('mode');
        return;
      }
      if (modes.length === 1) {
        setGeometryMode(modes[0]);
        enterMarking(modes[0]);
      } else {
        setStep('mode');
      }
      return;
    }
    if (featureSupportsPointPlacement(selectedFeatureType) && !geometryMode) {
      setStep('mode');
      return;
    }
    if (geometryMode) {
      if (campaignFieldCapture && geometryMode === 'trace') {
        setStep('marking');
      } else {
        enterMarking(geometryMode);
      }
    } else {
      setStep('mode');
    }
  };

  const handleCoordinatePlot = () => {
    const lat = parseFloat(coordLat);
    const lng = parseFloat(coordLng);
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setSubmitFeedback({ type: 'error', message: 'Enter valid coordinates (Lat: -90 to 90, Lng: -180 to 180).' });
      return;
    }
    onSetLocation?.({ lat, lng });
    onPlaceMarker?.(lat, lng);
    onFlyTo?.({ lat, lng }, SURVEY_MIN_ZOOM);
  };

  const handleLocationConfirm = () => {
    if (step === 'enclosure_marking') {
      const enclosureFt = 'landuse';
      const hasDrawn =
        geometryMode === 'draw'
          ? hasDrawableGeometry(drawnGeometry, enclosureFt)
          : false;
      const enclosureTraceReady =
        geometryMode === 'trace' && hasValidTraceGeometry(tracePoints, enclosureFt);
      if (!hasDrawn && !enclosureTraceReady) return;

      let geom: GeoJSONGeometry;
      if (geometryMode === 'draw' && drawnGeometry) {
        geom = drawnGeometry as GeoJSONGeometry;
      } else {
        geom = buildTraceGeometry(tracePoints, enclosureFt) as GeoJSONGeometry;
      }
      const sqM = computePolygonAreaSqM(geom as GeoJSON.Polygon);
      setEnclosureDetectResult(null);
      setEnclosureConfirmedGeometry(geom);
      setEnclosurePreviewAreaSqm(sqM);
      onEnableDrawing(false);
      onPreviewGeometry?.(geom);
      leaveMarking();
      setStep('enclosure_preview');
      return;
    }

    const hasDrawn =
      geometryMode === 'draw'
        ? hasDrawableGeometry(drawnGeometry, markingGeometryFeatureType)
        : !!drawnGeometry;
    const hasGeom =
      selectedLocation ||
      hasDrawn ||
      traceReady;
    if (!hasGeom) return;

    const markingTarget = roadMarkingTargetRef.current;
    const capturedLine = resolveContributionLineGeometry();

    if (markingTarget) {
      if (!capturedLine) {
        setSubmitFeedback({
          type: 'error',
          message: 'Finish the line trace or draw on the map, then confirm again.',
        });
        return;
      }
      setSubmitFeedback(null);
      if (confirmRoadMarkingCapture(capturedLine)) {
        return;
      }
    }

    if (hasGeom) {
      if (
        selectedFeatureType?.id === 'road'
        && roadCarriagewayCount === 1
        && capturedLine
        && stripNeedsMapTrace(singleStripCaptureMode)
      ) {
        setSingleStripReference(capturedLine);
      }
      leaveMarking();
      requestAddressLookup();
      setSheetSnap('half');
      setStep('details');
    }
  };

  const handleReplayTrace = () => {
    if (isTracing) onStopTrace();
    onClearGeometry();
    onPreviewGeometry?.(null);
    campaignTraceAutoStartRef.current = false;
    setCornerCaptureError(null);
  };

  const handleTraceCaptureModeChange = (mode: TraceCaptureMode) => {
    if (mode === traceCaptureMode) return;
    if (isTracing) onStopTrace();
    if (tracePoints.length > 0) {
      onClearGeometry();
      onPreviewGeometry?.(null);
      campaignTraceAutoStartRef.current = false;
    }
    setCornerCaptureError(null);
    onTraceCaptureModeChange?.(mode);
  };

  const handleOfficialSelectContinue = async () => {
    const boundaryName =
      officialLevel === 'country' ? 'Ghana' : officialBoundaryName.trim();
    if (officialLevel !== 'country' && !boundaryName) {
      setSubmitFeedback({
        type: 'error',
        message: `Select a ${officialLevel} from the list.`,
      });
      return;
    }

    setOfficialSelectLoading(true);
    setSubmitFeedback(null);
    try {
      const match = await matchFeatureByName(boundaryName, 'place');
      if (!match || !isGssBoundaryPlaceCategory(match.category)) {
        setSubmitFeedback({
          type: 'error',
          message: 'Could not find that official area on the map. Check your selection and try again.',
        });
        return;
      }
      if (match.category !== officialLevel) {
        setSubmitFeedback({
          type: 'error',
          message: `“${match.name}” is a ${match.category}, not a ${officialLevel}.`,
        });
        return;
      }
      setEnrichFeature(match);
      setName(match.name ?? '');
      setSelectedCategory(match.category);
      setOfficialBoundaryName(match.name ?? '');
      setStep('details');
    } catch {
      setSubmitFeedback({
        type: 'error',
        message: 'Could not verify the official area. Try again.',
      });
    } finally {
      setOfficialSelectLoading(false);
    }
  };

  const handleDetailsSubmit = async () => {
    const ftId = selectedFeatureType?.id ?? '';

    if (ftId === 'official_area') {
      if (!enrichFeature) return;
      if (officialUpdateIntent === 'description' && !description.trim()) {
        setSubmitFeedback({ type: 'error', message: 'Enter a description or choose Photos in the previous step.' });
        return;
      }
      if (officialUpdateIntent === 'photos' && photoDataUrls.length === 0) {
        setSubmitFeedback({ type: 'error', message: 'Add at least one photo or choose Description in the previous step.' });
        return;
      }
      if (officialUpdateIntent === 'both' && !description.trim() && photoDataUrls.length === 0) {
        setSubmitFeedback({ type: 'error', message: 'Add a description, photos, or both.' });
        return;
      }
      setStep('review');
      return;
    }

    const optionalNamePoiCategories = new Set(['bench', 'waste_basket']);
    const needsName = ['poi', 'landmark', 'building', 'transport_stop', 'place'].includes(ftId)
      && !(ftId === 'poi' && selectedCategory && optionalNamePoiCategories.has(selectedCategory));
    const needsCategory = selectedFeatureType?.needsCategory ?? false;
    if (ftId === 'place') {
      if (!name.trim() || !selectedCategory) return;
    } else {
      const canContinue = !!name.trim() || (!!selectedCategory && !needsName);
      if (!canContinue) return;
      if (needsCategory && !selectedCategory) return;
    }

    if (contributionFeatureShowsBusinessDetails(ftId, selectedLandmarkGroup)) {
      const phoneErr = validateContactPhone(phone, 'Phone');
      const mobileErr = validateContactPhone(mobile, 'Mobile');
      const whatsappErr = validateContactPhone(whatsapp, 'WhatsApp');
      if (phoneErr || mobileErr || whatsappErr) {
        setSubmitFeedback({
          type: 'error',
          message: phoneErr || mobileErr || whatsappErr || 'Check contact numbers.',
        });
        return;
      }
    }

    if (ftId === 'road' && roadCarriagewayCount == null) {
      setSubmitFeedback({
        type: 'error',
        message: 'Select how many carriageways this road has before continuing.',
      });
      return;
    }

    if (ftId === 'road' && roadCarriagewayCount != null) {
      const laneErr = validateRoadFlowFields(roadFlowValidationFields);
      if (laneErr) {
        setSubmitFeedback({ type: 'error', message: laneErr });
        return;
      }
      if (selectedCategory && supportsSideFacilityTags(selectedCategory)) {
        const sideErr = validateSideFacilityFields({
          category: selectedCategory,
          divided: isMultiCarriageway(roadCarriagewayCount),
          state: roadSideFacilities,
          roadWidth,
          carriagewayAWidth: roadCarriagewayAWidth,
          carriagewayBWidth: roadCarriagewayBWidth,
        });
        if (sideErr) {
          setSubmitFeedback({ type: 'error', message: sideErr });
          return;
        }
      }
    }

    if (selectedFeatureType?.id === 'place' && name.trim() && !editContribution && !isGssBoundaryPlaceCategory(selectedCategory)) {
      setMatchingExisting(true);
      try {
        const anchor = getContributionAnchor(
          geometryMode,
          selectedLocation ?? null,
          drawnGeometry ?? null,
          tracePoints,
        );
        const match = await matchFeatureByName(
          name.trim(),
          'place',
          anchor?.lat,
          anchor?.lng,
        );
        if (match) {
          setEnrichFeature(match);
          prefillFromMatchedFeature(match);
        } else {
          setEnrichFeature(null);
          onClearHighlight?.();
        }
      } catch {
        // Non-fatal — user may be adding a new place
      } finally {
        setMatchingExisting(false);
      }
    }

    if (selectedFeatureType?.id === 'building') {
      setStep('building_units');
    } else {
      setStep('review');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (ghanaPostGps.trim() && !isValidFullGhanaPostGps(ghanaPostGps.trim())) {
        setSubmitFeedback({
          type: 'error',
          message:
            'Ghana Post GPS must be a full code like AK-039-5028 or GH-GA141-2554 (15 characters max).',
        });
        setIsSubmitting(false);
        return;
      }

      if (contributionFeatureShowsBusinessDetails(selectedFeatureType?.id, selectedLandmarkGroup)) {
        const phoneErr = validateContactPhone(phone, 'Phone');
        const mobileErr = validateContactPhone(mobile, 'Mobile');
        const whatsappErr = validateContactPhone(whatsapp, 'WhatsApp');
        if (phoneErr || mobileErr || whatsappErr) {
          setSubmitFeedback({
            type: 'error',
            message: phoneErr || mobileErr || whatsappErr || 'Check contact numbers.',
          });
          setIsSubmitting(false);
          return;
        }
      }

      // Build geometry based on mode
      let geometry: GeoJSONGeometry;
      const useEnrichFeature = shouldUseEnrichFeatureForContribution(
        selectedFeatureType?.id,
        geometryMode,
        !!enrichFeature,
      );

      if (isGssBoundaryPlace) {
        if (!enrichFeature) {
          throw new Error('Select an official country, region, or district that already exists on the map.');
        }
        geometry = enrichFeature.geometry;
      } else if (useEnrichFeature && enrichFeature) {
        geometry = enrichFeature.geometry;
      } else if ((geometryMode === 'point' || geometryMode === 'coordinates' || geometryMode === 'link') && selectedLocation) {
        geometry = {
          type: 'Point',
          coordinates: [selectedLocation.lng, selectedLocation.lat],
        };
      } else if (geometryMode === 'draw' && drawnGeometry) {
        geometry = drawnGeometry as GeoJSONGeometry;
      } else if (geometryMode === 'trace' && traceReady) {
        const traced = buildTraceGeometry(tracePoints, markingGeometryFeatureType);
        if (!traced) throw new Error('No valid geometry');
        geometry = traced as GeoJSONGeometry;
      } else if (geometryMode === 'trace' && drawnGeometry?.type === 'LineString') {
        geometry = drawnGeometry as GeoJSONGeometry;
      } else if (
        selectedFeatureType?.id === 'road'
        && isMultiCarriageway(roadCarriagewayCount)
        && dividedCarriagewayCentreA
      ) {
        geometry = dividedCarriagewayCentreA;
      } else if (
        selectedFeatureType?.id === 'road'
        && roadCarriagewayCount === 1
        && singleStripCaptureMode === 'trace_outer_kerb'
        && singleStripReference
      ) {
        const w = parseOptionalWidthM(roadWidth);
        const centre = w != null ? deriveCentreFromOuterKerb(singleStripReference, w) : null;
        if (!centre) throw new Error('Could not derive centre line from outer kerb trace and width.');
        geometry = centre;
      } else if (
        selectedFeatureType?.id === 'road'
        && roadCarriagewayCount === 1
        && singleStripReference
      ) {
        geometry = singleStripReference;
      } else {
        throw new Error('No valid geometry');
      }

      let submitCarriagewayBGeom: GeoJSON.LineString | null = null;
      if (selectedFeatureType?.id === 'road' && isMultiCarriageway(roadCarriagewayCount)) {
        if (!dividedCarriagewayCentreA || !dividedCarriagewayBGeom) {
          throw new Error('Divided road geometry is incomplete — check divide, widths, and strip captures.');
        }
        geometry = dividedCarriagewayCentreA;
        submitCarriagewayBGeom = dividedCarriagewayBGeom;
      }

      if (selectedFeatureType?.id === 'road' && roadCarriagewayCount == null) {
        setSubmitFeedback({
          type: 'error',
          message: 'Select how many carriageways this road has before submitting.',
        });
        setIsSubmitting(false);
        return;
      }

      if (selectedFeatureType?.id === 'road' && roadCarriagewayCount != null) {
        const laneErr = validateRoadFlowFields(roadFlowValidationFields);
        if (laneErr) {
          setSubmitFeedback({ type: 'error', message: laneErr });
          setIsSubmitting(false);
          return;
        }
        if (selectedCategory && supportsSideFacilityTags(selectedCategory)) {
          const sideErr = validateSideFacilityFields({
            category: selectedCategory,
            divided: isMultiCarriageway(roadCarriagewayCount),
            state: roadSideFacilities,
            roadWidth,
            carriagewayAWidth: roadCarriagewayAWidth,
            carriagewayBWidth: roadCarriagewayBWidth,
          });
          if (sideErr) {
            setSubmitFeedback({ type: 'error', message: sideErr });
            setIsSubmitting(false);
            return;
          }
        }
      }

      const withinGhana = contributionGeometryInApiGhanaBounds(geometry);
      if (!withinGhana && !ALLOW_FIELD_MAPPING_OUTSIDE_GHANA) {
        setSubmitFeedback({
          type: 'error',
          message:
            'Location must be within Ghana. For local testing outside Ghana (e.g. UK), set VITE_ALLOW_FIELD_MAPPING_OUTSIDE_GHANA=true in the frontend and ALLOW_FIELD_MAPPING_OUTSIDE_GHANA=true on the API server, then restart both.',
        });
        setIsSubmitting(false);
        return;
      }
      const singleStripReferenceForSubmit =
        geometryMode === 'trace'
        && geometry.type === 'LineString'
        && roadCarriagewayCount === 1
        && stripNeedsMapTrace(singleStripCaptureMode)
          ? (geometry as GeoJSON.LineString)
          : singleStripReference;

      // Build lean extra fields per feature type
      const extra: Record<string, unknown> = {};
      if (selectedFeatureType?.id === 'road') {
        if (roadSurface) extra.surface = roadSurface;
        if (roadSmoothness) extra.smoothness = roadSmoothness;
        Object.assign(extra, roadFlowExtras({
          carriagewayCount: roadCarriagewayCount,
          lanes: roadLanes,
          lanesForward: roadLanesForward,
          lanesBackward: roadLanesBackward,
          laneDirections: roadLaneDirections,
          manualLaneDirections: roadManualLaneDirections,
          carriagewayALaneDirections: roadCarriagewayALaneDirections,
          carriagewayBLaneDirections: roadCarriagewayBLaneDirections,
          manualDividedLaneDirections: roadManualDividedLaneDirections,
          usePerLaneWidths: roadUsePerLaneWidths,
          laneWidthsM: roadLaneWidthsM,
          carriagewayALaneWidthsM: roadCarriagewayALaneWidthsM,
          carriagewayBLaneWidthsM: roadCarriagewayBLaneWidthsM,
          roadWidth,
          carriagewayAWidth: roadCarriagewayAWidth,
          carriagewayBWidth: roadCarriagewayBWidth,
          divideGeometry: isMultiCarriageway(roadCarriagewayCount) ? dividedDivideLine : null,
          divideWidthM,
          stripACaptureMode,
          stripBCaptureMode,
          stripAReferenceGeometry: stripReferenceA,
          stripBReferenceGeometry: stripReferenceB,
          singleStripCaptureMode,
          singleStripReferenceGeometry: singleStripReferenceForSubmit,
        }));
        extra.toll = roadToll;
        extra.flood_prone = roadFloodProne;
        extra.potholes = roadPotholes;
        extra.street_vendors = roadStreetVendors;
        extra.accident_prone = roadAccidentProne;
        extra.hawkers = roadHawkers;
        extra.lit = roadLit;
        extra.drainage = roadDrainage;
        if (roadMaxspeed.trim()) {
          const spd = parseInt(roadMaxspeed.trim(), 10);
          if (!Number.isNaN(spd) && spd >= 5 && spd <= 130) extra.maxspeed = spd;
        }
        if (roadCrossingType.trim()) extra.crossing_type = roadCrossingType.trim();
        Object.assign(
          extra,
          sideFacilityExtras(
            selectedCategory ?? '',
            isMultiCarriageway(roadCarriagewayCount),
            roadSideFacilities,
          ),
        );
        if (isMultiCarriageway(roadCarriagewayCount) && submitCarriagewayBGeom) {
          extra.carriageway_b_geometry = submitCarriagewayBGeom;
        }
        if (roadSnapConnections.length > 0) {
          extra.snap_connections = roadSnapConnections;
        }
      }
      if (selectedFeatureType?.id === 'landmark' && selectedLandmarkGroup) {
        extra.landmark_source_group = selectedLandmarkGroup;
        extra.landmark_contribution_mode = enrichFeature ? 'enrich' : 'new';
      }
      if (selectedFeatureType?.id === 'tree' && treeHeight.trim()) {
        const height = parseFloat(treeHeight.trim());
        if (!Number.isNaN(height) && height > 0) extra.height = height;
      }
      if (selectedFeatureType?.id === 'aeroway') {
        if (aerowayRef.trim()) extra.ref = aerowayRef.trim();
        if (aerowaySurface.trim()) extra.surface = aerowaySurface.trim();
        if (aerowayWidth.trim()) {
          const width = parseFloat(aerowayWidth.trim());
          if (!Number.isNaN(width) && width > 0) extra.width = width;
        }
      }
      if (selectedFeatureType?.id === 'motorway_junction') {
        if (junctionRef.trim()) extra.ref = junctionRef.trim();
        if (junctionExitRoads.length > 0) {
          extra.exit_to = formatJunctionExitTo(junctionExitRoads);
          extra.exit_to_roads = junctionExitRoads.map((road) => ({
            name: road.name,
            ...(road.locationId ? { location_id: road.locationId } : {}),
          }));
        }
      }
      if (selectedFeatureType?.id === 'turn_restriction') {
        if (!turnFromRoad?.roadId || !turnToRoad?.roadId || !turnRestrictionType) {
          throw new Error('Select from road, to road, and restriction rule.');
        }
        if (turnFromRoad.roadId === turnToRoad.roadId) {
          throw new Error('From and to roads must differ.');
        }
        extra.from_road_id = turnFromRoad.roadId;
        extra.to_road_id = turnToRoad.roadId;
        extra.restriction_type = turnRestrictionType;
      }
      if (selectedFeatureType?.id === 'transport_stop') {
        if (stopRef.trim()) extra.ref = stopRef.trim();
        if (stopRoutes.length) extra.routes = stopRoutes;
        if (stopOperators.length) extra.operators = stopOperators;
        if (stopShelter) extra.shelter = true;
        if (stopBench) extra.bench = true;
        if (stopLighting) extra.lighting = true;
      }
      if (showBusinessContactDetails && ['poi', 'landmark', 'building'].includes(selectedFeatureType?.id ?? '')) {
        if (poiCuisine.trim()) extra.cuisine = poiCuisine.trim();
        if (poiBrand.trim()) extra.brand = poiBrand.trim();
        if (poiWheelchair.trim()) extra.wheelchair = poiWheelchair.trim();
        if (paymentMobileMoney) extra.payment_mobile_money = true;
        if (paymentCard) extra.payment_card = true;
        if (mobileMoneyProvider.trim()) extra.mobile_money_provider = mobileMoneyProvider.trim();
        if (mobile.trim()) extra.mobile = mobile.trim();
        if (whatsapp.trim()) extra.whatsapp = whatsapp.trim();
        if (contactEmail.trim()) extra.email = contactEmail.trim();
        if (facebook.trim()) extra.facebook = facebook.trim();
        if (instagram.trim()) extra.instagram = instagram.trim();
        if (openingHours.trim()) extra.opening_hours = openingHours.trim();
        if (ghanaPostGps.trim()) extra.ghana_post_gps = ghanaPostGps.trim();
        if (hostHouseNumber.trim()) extra.house_number = hostHouseNumber.trim();
        if (hostFrontageStreetAddress.trim()) {
          extra.frontage_street_address = hostFrontageStreetAddress.trim();
        }
      }
      if (ghanaPostGps.trim() && (isOfficialAreaFlow || isGssBoundaryPlace || selectedFeatureType?.id === 'place')) {
        extra.ghana_post_gps = ghanaPostGps.trim();
      }
      if (useEnrichFeature && enrichFeature) {
        extra.enrich_mode = true;
        const crossTypePromote =
          selectedFeatureType?.id === 'landmark' &&
          enrichSearchFeatureType &&
          enrichSearchFeatureType !== 'landmark';
        if (crossTypePromote) {
          extra.enrich_source_feature_type = enrichFeature.feature_type;
          extra.enrich_source_table_id = enrichFeature.table_id;
        } else {
          if (enrichFeature.table_id > 0) extra.target_table_id = enrichFeature.table_id;
          if (enrichFeature.location_id) extra.target_location_id = enrichFeature.location_id;
        }
      }
      if (isOfficialAreaFlow) {
        extra.metadata_only = true;
        extra.update_fields = metadataUpdateFields(officialUpdateIntent);
      }
      if (descriptionSourceUrl.trim()) {
        extra.description_source_url = descriptionSourceUrl.trim();
      }

      const sendsBusinessContact = showBusinessContactDetails;

      const locality = normalizeLocalityFields(addrSuburb, addrCity);
      const resolvedRoadName = isRoadFeature
        ? resolveRoadContributionName({
            roadName: name,
            enrichDisplayName:
              isRoadEnrichUpdate(
                selectedFeatureType?.id,
                geometryMode,
                !!enrichFeature,
                !!editContribution,
              ) && enrichFeature
                ? resolveRoadEnrichDisplayName(enrichFeature)
                : null,
            categoryLabel: selectedCategoryMeta?.name,
            featureTypeLabel: selectedFeatureType?.name,
          })
        : '';

      const payload = {
        name: isRoadFeature
          ? resolvedRoadName
          : (
            name.trim() ||
            selectedCategoryMeta?.name ||
            selectedFeatureType?.name ||
            'Contribution'
          ),
        category:
          selectedCategory ||
          (selectedFeatureType?.id === 'motorway_junction' ? 'motorway_junction' : 'other'),
        feature_type:
          selectedFeatureType?.id === 'official_area'
            ? 'official_area'
            : selectedFeatureType?.id || undefined,
        geometry,
        address: addressLine.trim() || undefined,
        addr_street: isRoadFeature
          ? (resolvedRoadName || undefined)
          : (addrStreet.trim() || undefined),
        addr_suburb: locality.suburb || undefined,
        addr_city: locality.city || undefined,
        addr_district: addrDistrict.trim() || undefined,
        addr_region: addrRegion.trim() || undefined,
        phone: sendsBusinessContact ? phone.trim() || undefined : undefined,
        website: sendsBusinessContact ? website.trim() || undefined : undefined,
        description: description.trim() || undefined,
        photos: photoDataUrls.length > 0 ? photoDataUrls : undefined,
        building_units: selectedFeatureType?.id === 'building' ? buildingUnits : undefined,
        extra_fields: Object.keys(extra).length > 0 ? extra : undefined,
        ...(campaignId ? { campaign_id: campaignId } : {}),
        ...(zoneId ? { zone_id: zoneId } : {}),
      };

      const isApprovedRevise = editContribution?.status === 'approved';
      const isEdit = !!editContribution && !isApprovedRevise;
      if (isEdit) {
        await updateContribution(editContribution.id, payload);
      } else {
        await submitContribution(payload);
      }

      setSubmitFeedback({
        type: 'success',
        message: isEdit
          ? 'Contribution resubmitted for review!'
          : isApprovedRevise
            ? 'Revision submitted for review!'
            : 'Location submitted for review! Thank you for contributing.',
      });
      if (selectedFeatureType?.id === 'road' || payload.feature_type === 'road') {
        onPendingSurveyRefresh?.();
      }
      onClearGeometry();

      if (isEdit || isApprovedRevise) {
        setTimeout(handleClose, 1500);
      }
    } catch (error) {
      let msg =
        error instanceof APIError || error instanceof Error
          ? error.message
          : 'Failed to submit. Please try again.';
      if (error instanceof APIError && /within Ghana/i.test(msg) && ALLOW_FIELD_MAPPING_OUTSIDE_GHANA) {
        msg =
          'The API rejected this location outside Ghana. Set ALLOW_FIELD_MAPPING_OUTSIDE_GHANA=true on the API server and restart it (frontend QA flag alone is not enough).';
      }
      setSubmitFeedback({ type: 'error', message: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addPhotoFiles = async (files: File[]) => {
    const incoming = files.filter((f) => {
      if (f.type === 'image/svg+xml') {
        setSubmitFeedback({ type: 'error', message: 'SVG files are not supported. Please use JPG, PNG, or WebP photos.' });
        return false;
      }
      if (!isContributionImageFile(f)) {
        setSubmitFeedback({ type: 'error', message: 'Unsupported image format. Please use JPG, PNG, WebP, or HEIC photos.' });
        return false;
      }
      return true;
    });
    const additions: string[] = [];
    for (const file of incoming) {
      if (photoDataUrls.length + additions.length >= MAX_CONTRIBUTION_PHOTOS) break;
      if (file.size > MAX_PHOTO_BYTES) {
        setSubmitFeedback({ type: 'error', message: `Each photo must be under ${Math.round(MAX_PHOTO_BYTES / 1024 / 1024)}MB.` });
        continue;
      }
      try {
        additions.push(await readFileAsDataUrl(file));
      } catch {
        setSubmitFeedback({ type: 'error', message: 'Could not read that image.' });
      }
    }
    if (additions.length > 0) {
      setPhotoDataUrls((prev) => [...prev, ...additions].slice(0, MAX_CONTRIBUTION_PHOTOS));
    }
  };

  const handlePhotoInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[photo] handlePhotoInputChange fired — files:', e.target.files?.length ?? 0);
    const files = e.target.files;
    if (!files?.length) { console.log('[photo] no files in input change event'); return; }
    await addPhotoFiles(Array.from(files));
    e.target.value = '';
  };

  const handlePhotoDrop = async (files: File[]) => {
    console.log('[photo] handlePhotoDrop in AddLocationPanel — files:', files.length);
    await addPhotoFiles(files);
  };

  const removePhotoAt = (index: number) => {
    setPhotoDataUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBack = () => {
    switch (step) {
      case 'enclosure_method':
        resetEnclosureFlow();
        setStep('feature_type');
        break;
      case 'enclosure_capture_mode':
        setStep('enclosure_method');
        break;
      case 'enclosure_map':
        onEnableDrawing(false);
        onEnableMapClick(false);
        onClearGeometry();
        onPreviewGeometry?.(null);
        onClearHighlight?.();
        setStep('enclosure_method');
        break;
      case 'enclosure_marking':
        onEnableDrawing(false);
        onClearGeometry();
        onPreviewGeometry?.(null);
        leaveMarking();
        setStep('enclosure_capture_mode');
        break;
      case 'enclosure_preview':
        setEnclosurePreviewAreaSqm(null);
        setEnclosureConfirmedGeometry(null);
        setEnclosureDetectResult(null);
        setEnclosureSelectedCandidateId(null);
        onPreviewGeometry?.(null);
        onClearHighlight?.();
        if (enclosureMethod === 'draw') {
          onClearGeometry();
          onEnableDrawing(false);
          setStep('enclosure_capture_mode');
        } else if (enclosureMethod) {
          beginEnclosureMapStep(enclosureMethod);
        } else {
          setStep('enclosure_method');
        }
        break;
      case 'enclosure_feature_type':
        setStep('enclosure_preview');
        break;
      case 'mode':
        if (motorRoadSurveyFlow && roadCarriagewayCount != null) {
          setGeometryMode(null);
          setStep('road_layout');
          break;
        }
        if (selectedFeatureType?.id === 'road') {
          setGeometryMode(null);
          setStep('category');
          setSelectedCategory(null);
          break;
        }
        if (
          selectedFeatureType?.id === 'place'
          || (selectedFeatureType?.needsCategory && (
            isPointOnlyFeature(selectedFeatureType)
            || (selectedFeatureType?.id === 'landmark' && selectedLandmarkGroup)
          ))
        ) {
          setStep('category');
          setGeometryMode(null);
        } else {
          setStep('feature_type');
          setSelectedFeatureType(null);
          setGeometryMode(null);
        }
        break;
      case 'landmark_group':
        setStep('feature_type');
        setSelectedFeatureType(null);
        setGeometryMode(null);
        setSelectedLandmarkGroup(null);
        break;
      case 'landmark_placement':
        setStep('landmark_group');
        setGeometryMode(null);
        break;
      case 'road_capture_plan':
        setRoadCapturePlanError(null);
        setStep('mode');
        break;
      case 'road_layout':
        setRoadCarriagewayCount(null);
        setStep('category');
        setSelectedCategory(null);
        break;
      case 'category':
        if (selectedFeatureType?.id === 'landmark') {
          setStep('landmark_placement');
          setSelectedCategory(null);
        } else if (selectedFeatureType?.id === 'place') {
          setStep('feature_type');
          setSelectedFeatureType(null);
          setSelectedCategory(null);
          setGeometryMode(null);
        } else if (selectedFeatureType?.id === 'road') {
          setStep('feature_type');
          setSelectedFeatureType(null);
          setSelectedCategory(null);
          setGeometryMode(null);
          setRoadCarriagewayCount(null);
        } else if (selectedFeatureType && isPointOnlyFeature(selectedFeatureType)) {
          setStep('feature_type');
          setSelectedFeatureType(null);
          setGeometryMode(null);
        } else if (selectedFeatureType && selectedFeatureType.allowedModes.length === 1) {
          setStep('feature_type');
          setSelectedFeatureType(null);
          setGeometryMode(null);
        } else {
          setStep('mode');
          setGeometryMode(null);
        }
        break;
      case 'marking':
        leaveMarking();
        onEnableDrawing(false);
        onDrawTrash?.();
        onClearGeometry({ keepTrace: geometryMode === 'trace' });
        if (motorRoadSurveyFlow) {
          roadMarkingTargetRef.current = null;
          setAwaitingCarriagewayB(false);
          setStep('road_capture_plan');
          break;
        }
        if (selectedFeatureType?.id === 'road') {
          roadMarkingTargetRef.current = null;
          setStep('mode');
          break;
        }
        // If this feature type doesn't need categories, go back to mode or feature_type
        if (!selectedFeatureType?.needsCategory) {
          if (selectedFeatureType && isPointOnlyFeature(selectedFeatureType)) {
            setStep('mode');
            setGeometryMode(null);
          } else if (selectedFeatureType && selectedFeatureType.allowedModes.length === 1) {
            setStep('feature_type');
            setSelectedFeatureType(null);
            setGeometryMode(null);
          } else {
            setStep('mode');
            setGeometryMode(null);
          }
        } else {
          if (selectedFeatureType?.id === 'place' || isPointOnlyFeature(selectedFeatureType)) {
            setStep('mode');
            setGeometryMode(null);
          } else if (selectedFeatureType?.id === 'landmark' && selectedLandmarkGroup) {
            const modes = landmarkNewGeometryModes(selectedLandmarkGroup);
            setStep(modes.length > 1 || (modes.length === 1 && modes[0] === 'point') ? 'mode' : 'category');
            setGeometryMode(null);
          } else {
            setStep('category');
          }
        }
        break;
      case 'enrich_select':
        onEnableMapClick(false);
        setEnrichFeature(null);
        onClearHighlight?.();
        if (selectedFeatureType?.id === 'landmark') {
          setStep('landmark_group');
        } else {
          setStep('mode');
        }
        setGeometryMode(null);
        break;
      case 'official_select':
        setEnrichFeature(null);
        onClearHighlight?.();
        setOfficialBoundaryName('');
        setStep('feature_type');
        setSelectedFeatureType(null);
        break;
      case 'details':
        if (suggestEditForPlace) {
          if (onDismissSuggestEdit) {
            onDismissSuggestEdit();
          } else {
            handleClose();
          }
          break;
        }
        if (editContribution) {
          if (canReopenContributionGeometryCapture) {
            handleEditContributionGeometry();
          } else {
            handleClose();
          }
          break;
        }
        if (isOfficialAreaFlow) {
          setEnrichFeature(null);
          onClearHighlight?.();
          setStep('official_select');
        } else if (isGssBoundaryPlace) {
          setEnrichFeature(null);
          onClearHighlight?.();
          setStep('category');
        } else if (geometryMode === 'enrich') {
          resetEnrichSelection();
        } else if (motorRoadSurveyFlow) {
          setStep('road_capture_plan');
        } else if (geometryMode) {
          clearStructuredAddressFields();
          setAddressLookupStatus('idle');
          setAddressLookupKey((k) => k + 1);
          enterMarking(geometryMode);
        } else {
          setStep('marking');
        }
        break;
      case 'building_units':
        persistCurrentFloorDraft();
        setStep('details');
        break;
      case 'review':
        if (selectedFeatureType?.id === 'building') {
          restoreBuildingUnitsEditingState();
          setStep('building_units');
        } else {
          setStep('details');
        }
        break;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'feature_type': return 'What are you adding?';
      case 'enclosure_method': return 'Detect Enclosure';
      case 'enclosure_capture_mode': return 'Manual capture mode';
      case 'enclosure_map':
        if (enclosureMethod === 'point') return 'Tap inside the area';
        if (enclosureMethod === 'boundary') return 'Select boundary roads';
        return 'Draw the enclosure';
      case 'enclosure_marking': return 'Capture enclosure';
      case 'enclosure_preview': return 'Preview boundary';
      case 'enclosure_feature_type': return 'Choose feature type';
      case 'mode': {
        if (selectedFeatureType?.id === 'road') {
          return 'Draw line or GPS trace';
        }
        const modes = effectivePlacementModes(selectedFeatureType, selectedLandmarkGroup, selectedFeatureType?.id === 'landmark');
        return isPointPlacementOnlyModes(modes)
          ? `How to place this ${selectedFeatureType?.name || 'feature'}?`
          : `How to map this ${selectedFeatureType?.name || 'feature'}?`;
      }
      case 'landmark_group': return 'What kind of landmark?';
      case 'landmark_placement': return 'Add or update?';
      case 'category':
        return selectedFeatureType?.id === 'landmark'
          ? 'Select landmark subtype'
          : `Select ${selectedFeatureType?.name || ''} Type`.trim();
      case 'road_layout':
        return 'Road layout';
      case 'road_capture_plan':
        return 'Geometry strategy';
      case 'marking': {
        const target = roadMarkingTargetRef.current;
        if (target === 'divide') return 'Trace divide (median / seam)';
        if (target === 'strip_a') {
          return stripACaptureMode === 'trace_outer_kerb'
            ? 'Trace carriageway A (property-side kerb)'
            : 'Trace carriageway A';
        }
        if (target === 'strip_b') {
          return stripBCaptureMode === 'trace_outer_kerb'
            ? 'Trace carriageway B (property-side kerb)'
            : 'Trace carriageway B';
        }
        if (target === 'single_strip') {
          return singleStripCaptureMode === 'trace_outer_kerb'
            ? 'Trace property-side kerb'
            : 'Trace centre line';
        }
        if (awaitingCarriagewayB) return 'Trace carriageway B';
        if (geometryMode === 'point') return 'Tap to Place Pin';
        if (geometryMode === 'draw') return markingDrawsPolygon ? 'Draw Boundary' : 'Draw Line';
        if (geometryMode === 'coordinates') return 'Enter Coordinates';
        if (geometryMode === 'link') return 'Paste a Map Link';
        if (geometryMode === 'trace') return 'Trace Path';
        return 'Mark on Map';
      }
      case 'enrich_select': return `Tap a ${enrichTargetLabel} on the Map`;
      case 'official_select': return 'Select official area';
      case 'details': return isOfficialAreaFlow || enrichFeature ? 'Add details' : 'Add Details';
      case 'building_units': return 'Units & rooms';
      case 'review':
        return editContribution?.status === 'approved'
          ? 'Review & Submit Revision'
          : editContribution
            ? 'Review & Resubmit'
            : enrichFeature
              ? 'Review & Submit Update'
              : 'Review & Submit';
      default: return 'What are you adding?';
    }
  };

  const hasGeometry =
    selectedLocation ||
    (geometryMode === 'draw'
      ? hasDrawableGeometry(
          drawnGeometry,
          isEnclosureMarkingStep ? activeMarkingFeatureType : markingGeometryFeatureType,
        )
      : !!drawnGeometry) ||
    (isEnclosureMarkingStep
      ? geometryMode === 'trace'
        && hasValidTraceGeometry(tracePoints, activeMarkingFeatureType)
      : traceReady) ||
    enrichFeature;

  const renderFeatureTypeSelection = () => (
    <div className="space-y-2">
      {campaignFieldCapture && selectedLocation ? (
        <div className="mb-3 rounded-xl border border-cyan-200/70 bg-cyan-50/80 px-3 py-2.5 text-center dark:border-cyan-800/50 dark:bg-cyan-950/30">
          <p className="text-xs font-semibold text-cyan-900 dark:text-cyan-100">Live GPS captured from your session</p>
          <p className="mt-1 text-[10px] text-cyan-600/90 dark:text-cyan-400/90">
            Pick a feature type, then choose <strong className="font-semibold">Drop a Pin</strong> for a stop or{' '}
            <strong className="font-semibold">GPS Trace</strong> to walk a road or perimeter. Tracing starts
            automatically on the map step. Review the shape on the map, then label when done.
            {campaignId ? ' Submissions sync to your live campaign.' : ''}
          </p>
        </div>
      ) : null}
      <p className="text-sm text-gray-600 dark:text-neutral-400 text-center mb-3">
        Select the type of map feature you want to contribute, or discover a closed area first.
      </p>
      <button
        type="button"
        data-testid="enclosure-detect-entry"
        onClick={startEnclosureDetection}
        className="w-full mb-3 flex items-start gap-3 p-4 rounded-2xl border border-violet-200/90 bg-violet-50/70 dark:border-violet-900/50 dark:bg-violet-950/30 hover:border-violet-400 dark:hover:border-violet-700 transition-all text-left shadow-sm"
      >
        <div className="shrink-0 w-11 h-11 rounded-full bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
          <Scan size={22} className="text-violet-700 dark:text-violet-300" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm text-gray-900 dark:text-neutral-100">Detect Enclosure</p>
          <p className="text-[11px] text-gray-600 dark:text-neutral-400 mt-0.5 leading-snug">
            Find a closed area on the map, preview its boundary, then pick what it represents.
          </p>
        </div>
      </button>
      <div className="grid grid-cols-2 gap-2">
        {FEATURE_TYPES.map((ft) => (
          <button
            key={ft.id}
            type="button"
            onClick={() => handleFeatureTypeSelect(ft)}
            className="flex flex-col items-center gap-1.5 p-3 bg-gray-50 dark:bg-neutral-800/80 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-700/90 border border-transparent hover:border-primary-300 dark:hover:border-primary-700 transition-all text-center shadow-sm"
          >
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center">
              <DynamicIcon name={ft.icon} size={20} className="text-primary-600 dark:text-primary-400" />
            </div>
            <p className="font-semibold text-xs text-gray-900 dark:text-neutral-100 leading-tight">{ft.name}</p>
            <p className="text-[10px] text-gray-500 dark:text-neutral-500 leading-tight">{ft.description}</p>
          </button>
        ))}
      </div>
    </div>
  );

  const allowedModes = useMemo(
    () => effectivePlacementModes(
      selectedFeatureType,
      selectedLandmarkGroup,
      selectedFeatureType?.id === 'landmark' && step === 'mode',
    ),
    [selectedFeatureType, selectedLandmarkGroup, step],
  );

  const MODE_CONFIGS: { mode: GeometryMode; label: string; desc: string; icon: React.ReactNode; borderHover: string; bgIcon: string }[] = [
    { mode: 'point', label: 'Drop a Pin', desc: 'Tap the map, use your location, or enter coordinates', icon: <MapPin size={24} className="text-primary-600 dark:text-primary-400" />, borderHover: 'hover:border-primary-200/60 dark:hover:border-primary-800', bgIcon: 'bg-primary-100 dark:bg-primary-900/50' },
    { mode: 'draw', label: markingDrawsPolygon ? 'Draw Boundary' : 'Draw Line', desc: markingDrawsPolygon ? 'Draw the outline of an area' : 'Draw a line on the map', icon: <Pencil size={24} className="text-blue-600 dark:text-blue-400" />, borderHover: 'hover:border-blue-200/60 dark:hover:border-blue-800', bgIcon: 'bg-blue-100 dark:bg-blue-950/50' },
    {
      mode: 'trace',
      label: 'GPS Trace',
      desc:
        markingGeometryFeatureType && traceProducesPolygon(markingGeometryFeatureType)
          ? 'Walk the perimeter or mark corners with GPS'
          : 'Walk a path or mark bends with GPS',
      icon: <Navigation2 size={24} className="text-emerald-600 dark:text-emerald-400" />,
      borderHover: 'hover:border-emerald-200/60 dark:hover:border-emerald-800',
      bgIcon: 'bg-emerald-100 dark:bg-emerald-950/50',
    },
    { mode: 'coordinates', label: 'Enter Coordinates', desc: 'Type lat/lng from another source', icon: <Crosshair size={24} className="text-violet-600 dark:text-violet-400" />, borderHover: 'hover:border-violet-200/60 dark:hover:border-violet-800', bgIcon: 'bg-violet-100 dark:bg-violet-950/50' },
    { mode: 'link', label: 'Paste a Link', desc: 'Use a map link to set the location', icon: <Link size={24} className="text-cyan-600 dark:text-cyan-400" />, borderHover: 'hover:border-cyan-200/60 dark:hover:border-cyan-800', bgIcon: 'bg-cyan-100 dark:bg-cyan-950/50' },
    { mode: 'enrich', label: 'Enrich Existing Feature', desc: 'Tap an existing feature on the map to add details', icon: <Target size={24} className="text-amber-600 dark:text-amber-400" />, borderHover: 'hover:border-amber-200/60 dark:hover:border-amber-800', bgIcon: 'bg-amber-100 dark:bg-amber-950/50' },
  ];

  const renderModeSelection = () => (
    <div className="space-y-3">
      {selectedFeatureType?.id === 'road' && (
        <p className="text-sm text-gray-600 dark:text-neutral-400">
          {motorRoadSurveyFlow && roadCarriagewayCount != null
            ? 'Choose Draw line or GPS trace. Next you\u2019ll choose what to trace on the map.'
            : 'Choose Draw line or GPS trace to map this road or path.'}
        </p>
      )}
      {['landuse', 'landcover', 'water'].includes(selectedFeatureType?.id ?? '') && (
        <p className="text-xs text-amber-800 dark:text-amber-200/90 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50 rounded-xl px-3 py-2.5">
          GPS can drift under trees or buildings. <strong className="font-semibold">Draw Boundary</strong> is often more accurate than GPS Trace for parks and wooded areas.
        </p>
      )}
      {MODE_CONFIGS.filter(m => m.mode === 'enrich' || allowedModes.includes(m.mode)).map((m) => (
        <button
          key={m.mode}
          type="button"
          onClick={() => handleModeSelect(m.mode)}
          className={`w-full flex items-center gap-4 p-4 bg-gray-50 dark:bg-neutral-800/80 rounded-2xl hover:bg-gray-100 dark:hover:bg-neutral-700/90 border border-transparent ${m.borderHover} transition-all text-left shadow-sm`}
        >
          <div className={`w-12 h-12 ${m.bgIcon} rounded-full flex items-center justify-center shrink-0`}>
            {m.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 dark:text-neutral-100">{m.label}</p>
            <p className="text-sm text-gray-500 dark:text-neutral-400">{m.desc}</p>
          </div>
        </button>
      ))}
    </div>
  );

  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim()) return resolvedCategories;
    const q = categorySearch.toLowerCase();
    return resolvedCategories.filter((c) => categorySearchText(c).includes(q));
  }, [resolvedCategories, categorySearch]);

  const renderLandmarkPlacementSelection = () => {
    const groupName =
      LANDMARK_SOURCE_GROUPS.find((g) => g.id === selectedLandmarkGroup)?.name ?? 'feature';
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600 dark:text-neutral-400 text-center">
          Choose whether to improve an existing <span className="font-semibold">{groupName.toLowerCase()}</span> on the map or add a new one.
        </p>
        <button
          type="button"
          onClick={() => handleLandmarkPlacementSelect('enrich')}
          className="w-full flex items-center gap-4 p-4 bg-gray-50 dark:bg-neutral-800/80 rounded-2xl hover:bg-gray-100 dark:hover:bg-neutral-700/90 border border-transparent hover:border-amber-200/60 dark:hover:border-amber-800 transition-all text-left shadow-sm"
        >
          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-950/50 rounded-full flex items-center justify-center shrink-0">
            <Target size={24} className="text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 dark:text-neutral-100">Update existing</p>
            <p className="text-sm text-gray-500 dark:text-neutral-400">
              Tap on or near a {groupName.toLowerCase()} already on the map
            </p>
          </div>
        </button>
        <button
          type="button"
          onClick={() => handleLandmarkPlacementSelect('new')}
          className="w-full flex items-center gap-4 p-4 bg-gray-50 dark:bg-neutral-800/80 rounded-2xl hover:bg-gray-100 dark:hover:bg-neutral-700/90 border border-transparent hover:border-primary-200/60 dark:hover:border-primary-800 transition-all text-left shadow-sm"
        >
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/50 rounded-full flex items-center justify-center shrink-0">
            <MapPin size={24} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 dark:text-neutral-100">Add new</p>
            <p className="text-sm text-gray-500 dark:text-neutral-400">
              Map a new {groupName.toLowerCase()} that is not on the map yet
            </p>
          </div>
        </button>
      </div>
    );
  };

  const renderLandmarkGroupSelection = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-neutral-400 text-center flex items-center justify-center gap-2">
        <Sparkles size={16} className="text-primary-500 dark:text-primary-400 shrink-0" />
        Pick the main category — promote an important map feature to landmark styling.
      </p>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        {LANDMARK_SOURCE_GROUPS.map((group, index) => {
          const cc = colorClasses[group.color] || colorClasses.gray;
          const selected = selectedLandmarkGroup === group.id;
          return (
            <motion.button
              key={group.id}
              type="button"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.04, 0.5) }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleLandmarkGroupSelect(group.id)}
              className={`flex flex-col items-center gap-2.5 p-3.5 sm:p-4 rounded-2xl border-2 transition-all shadow-sm ${selected
                ? `border-primary-500 bg-primary-50 dark:bg-neutral-800 dark:border-primary-500 ring-2 ring-primary-500/30 dark:ring-primary-500/25`
                : `border-gray-200 dark:border-neutral-600 hover:border-primary-300 dark:hover:border-primary-600 bg-white dark:bg-neutral-800/50`
                }`}
            >
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${selected ? cc.bg : 'bg-gray-100 dark:bg-neutral-700'}`}>
                <DynamicIcon name={group.icon} size={22} className={selected ? 'text-white' : `${cc.text} dark:text-neutral-200`} />
              </div>
              <span className={`text-xs sm:text-sm font-semibold text-center leading-tight ${selected ? 'text-primary-800 dark:text-neutral-100' : 'text-gray-900 dark:text-neutral-100'}`}>
                {group.name}
              </span>
            </motion.button>
          );
        })}
      </div>
    </div>
  );

  const renderCategorySelection = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-neutral-400 text-center flex items-center justify-center gap-2">
        <Sparkles size={16} className="text-primary-500 dark:text-primary-400 shrink-0" />
        {selectedFeatureType?.id === 'place'
          ? 'What kind of place is this? Pick the closest match.'
          : selectedFeatureType?.id === 'landmark'
            ? selectedLandmarkGroup
              ? `Choose the ${LANDMARK_SOURCE_GROUPS.find((g) => g.id === selectedLandmarkGroup)?.name ?? 'landmark'} subtype.`
              : 'Choose the specific subtype for this landmark.'
            : selectedFeatureType?.id === 'road'
              ? 'Choose the road or path type that best matches what you are mapping.'
              : 'Choose what best describes this place. We use the same categories as search.'}
      </p>
      {selectedFeatureType?.id === 'place' && (
        <p className="text-xs text-gray-500 dark:text-neutral-400 text-center px-2">
          For Ghana, regions, or districts already on the map, use <strong className="font-semibold">Official Area</strong> instead.
          Towns and suburbs use a centre pin after you pick their category.
        </p>
      )}
      {/* Search input */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500" />
        <input
          type="text"
          value={categorySearch}
          onChange={(e) => setCategorySearch(e.target.value)}
          placeholder="Search categories…"
          className="w-full pl-9 pr-3 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
        />
      </div>
      {categoriesLoading ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3">
          <Loader2 className="w-9 h-9 animate-spin text-primary-500 dark:text-primary-400" />
          <span className="text-sm text-gray-500 dark:text-neutral-400">Loading categories…</span>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500 dark:text-neutral-400">No categories match "{categorySearch}"</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {filteredCategories.map((category, index) => {
            const cc = colorClasses[category.color] || colorClasses.gray;
            const selected = selectedCategory === category.id;
            return (
              <motion.button
                key={category.id}
                type="button"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.5) }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleCategorySelect(category.id)}
                className={`flex flex-col items-center gap-2.5 p-3.5 sm:p-4 rounded-2xl border-2 transition-all shadow-sm ${selected
                  ? `border-primary-500 bg-primary-50 dark:bg-neutral-800 dark:border-primary-500 ring-2 ring-primary-500/30 dark:ring-primary-500/25`
                  : `border-gray-200 dark:border-neutral-600 hover:border-primary-300 dark:hover:border-primary-600 bg-white dark:bg-neutral-800/50`
                  }`}
              >
                <div
                  className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${selected ? `${cc.bg} shadow-inner` : 'bg-gray-100 dark:bg-neutral-700'
                    }`}
                >
                  <DynamicIcon
                    name={category.icon}
                    size={22}
                    className={selected ? 'text-white' : `${cc.text} dark:text-neutral-200`}
                  />
                </div>
                <span
                  className={`text-xs sm:text-sm font-semibold text-center leading-tight ${selected ? 'text-primary-800 dark:text-neutral-100' : 'text-gray-900 dark:text-neutral-100'
                    }`}
                >
                  {category.name}
                </span>
                {category.hint && (
                  <span className="text-[10px] sm:text-[11px] text-gray-500 dark:text-neutral-400 text-center leading-snug px-1">
                    {category.hint}
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderEnclosureCaptureMode = () => (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 dark:text-neutral-400 text-center">
        Choose draw or GPS trace, then clean vertices before you continue to preview.
      </p>
      <p className="text-xs text-amber-800 dark:text-amber-200/90 bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50 rounded-xl px-3 py-2.5">
        Under tree cover, <strong className="font-semibold">Draw boundary</strong> is often more accurate than GPS trace.
      </p>
      {MODE_CONFIGS.filter((m) => m.mode === 'draw' || m.mode === 'trace').map((m) => (
        <button
          key={m.mode}
          type="button"
          data-testid={`enclosure-capture-${m.mode}`}
          onClick={() => handleEnclosureCaptureModeSelect(m.mode as 'draw' | 'trace')}
          className={`w-full flex items-center gap-4 p-4 bg-gray-50 dark:bg-neutral-800/80 rounded-2xl hover:bg-gray-100 dark:hover:bg-neutral-700/90 border border-transparent ${m.borderHover} transition-all text-left shadow-sm`}
        >
          <div className={`w-12 h-12 ${m.bgIcon} rounded-full flex items-center justify-center shrink-0`}>
            {m.mode === 'draw' ? (
              <Pencil size={24} className="text-blue-600 dark:text-blue-400" />
            ) : (
              <Navigation2 size={24} className="text-emerald-600 dark:text-emerald-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 dark:text-neutral-100">
              {m.mode === 'draw' ? 'Draw boundary' : 'GPS trace perimeter'}
            </p>
            <p className="text-sm text-gray-500 dark:text-neutral-400">
              {m.mode === 'draw'
                ? 'Tap corners on the map, then edit or snap to close'
                : 'Walk the perimeter or mark corners, then refine the path'}
            </p>
          </div>
        </button>
      ))}
    </div>
  );

  const renderMarkingStep = () => {
    const markingDrawsPolygonLocal = isEnclosureMarkingStep
      ? activeMarkingDrawsPolygon
      : markingDrawsPolygon;
    const markingFeatureTypeLocal = isEnclosureMarkingStep
      ? activeMarkingFeatureType
      : markingGeometryFeatureType;
    const showGeometryCleanup =
      Boolean(onRoadSnappingEnabledChange)
      && (geometryMode === 'draw' || geometryMode === 'trace');
    const cleanupIsLine = !markingDrawsPolygonLocal;
    const tracePathRefineEditing =
      geometryMode === 'trace'
      && !isTracing
      && tracePoints.length >= 2
      && traceCaptureMode !== 'corners';
    const traceVertexMapEdit =
      geometryMode === 'trace'
      && !isTracing
      && tracePoints.length >= 2;

    const geometryCleanupToolbar = showGeometryCleanup ? (
      <div className="mt-3 pt-3 border-t border-blue-200/60 dark:border-blue-800/50 space-y-2 text-left">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
            {cleanupIsLine ? 'Snap endpoints' : 'Snap / close boundary'}
          </span>
          <button
            type="button"
            role="switch"
            aria-label={cleanupIsLine ? 'Snap endpoints' : 'Snap close boundary'}
            aria-checked={roadSnappingEnabled}
            onClick={() => onRoadSnappingEnabledChange?.(!roadSnappingEnabled)}
            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              roadSnappingEnabled
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-neutral-700 text-gray-700 dark:text-neutral-200'
            }`}
          >
            {roadSnappingEnabled ? 'On' : 'Off'}
          </button>
        </div>
        {geometryMode === 'draw' && onDrawEnterVertexEdit ? (
          <div className="space-y-1.5 rounded-xl border border-blue-200/80 dark:border-blue-800/60 bg-white/70 dark:bg-neutral-900/40 p-2.5">
            <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
              Tap <span className="font-semibold">Edit vertices on map</span>, then tap a corner on the
              line or boundary. Delete that vertex with the button below or the trash icon top-right.
            </p>
            <button
              type="button"
              onClick={() => onDrawEnterVertexEdit()}
              className="w-full py-2 text-sm font-medium rounded-xl border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-950/40"
            >
              Edit vertices on map
            </button>
            <button
              type="button"
              disabled={!onDrawTrash}
              onClick={() => {
                onDrawEnterVertexEdit();
                onDrawTrash?.();
              }}
              className="w-full py-2 text-sm font-semibold rounded-lg border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Delete selected point
            </button>
          </div>
        ) : null}
        {geometryMode === 'trace' && tracePoints.length >= 2 && !isTracing ? (
          <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
            Tap a point on the map to select it
            {selectedTraceVertexIndex != null
              ? ` (point ${selectedTraceVertexIndex + 1})`
              : ''}
            {tracePathRefineEditing
              ? '. Start and End are labelled on the path. Use Delete point beside Undo/Redo.'
              : '.'}
          </p>
        ) : null}
        {onSnapEndpointsNow ? (
          <button
            type="button"
            onClick={() =>
              onSnapEndpointsNow({
                featureTypeId: markingFeatureTypeLocal ?? null,
                geometryMode: geometryMode as 'draw' | 'trace',
              })}
            className="w-full py-2 text-sm font-medium rounded-xl border border-blue-200 dark:border-blue-800 bg-white/80 dark:bg-neutral-800/80 text-blue-800 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-950/40"
          >
            {cleanupIsLine ? 'Snap endpoints now' : 'Close boundary now'}
          </button>
        ) : null}
        {snapStatusMessage ? (
          <p className="text-xs text-blue-700 dark:text-blue-300/90">{snapStatusMessage}</p>
        ) : roadSnappingEnabled ? (
          <p className="text-xs text-blue-600/80 dark:text-blue-400/80">
            {cleanupIsLine
              ? `Endpoints within ${roadSnapBandsHint.autoM} m snap on finish when unambiguous (search ${roadSnapBandsHint.searchM} m).`
              : `When start and end are within ${roadSnapBandsHint.autoM} m, the ring closes on finish or when you tap Close boundary now.`}
            {' '}Change radii in Settings → Field mapping.
          </p>
        ) : null}
        <p className="text-xs text-amber-700/90 dark:text-amber-300/90">
          Amber dashed lines are your pending road surveys (before map tiles update). Snap can connect to their endpoints.
        </p>
        <p className="text-xs text-blue-700/90 dark:text-blue-300/90">
          Blue dashed segments show where an endpoint moved when snapped (visual only; not saved separately).
        </p>
      </div>
    ) : null;

    return (
    <div className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-950/35 rounded-2xl p-4 text-center border border-blue-100/80 dark:border-blue-900/50">
        {geometryMode === 'enrich' && (
          <>
            <Target size={32} className="mx-auto text-amber-600 dark:text-amber-400 mb-2" />
            <p className="text-amber-800 dark:text-amber-200 font-medium">Tap on or near the map</p>
            <p className="text-sm text-amber-600 dark:text-amber-400/90">
              Select an existing {enrichTargetLabel.toLowerCase()} to update
            </p>
          </>
        )}
        {geometryMode === 'point' && (
          <>
            <Target size={32} className="mx-auto text-blue-600 dark:text-blue-400 mb-2" />
            <p className="text-blue-800 dark:text-blue-200 font-medium">Drop a pin on the map</p>
            <p className="text-sm text-blue-600 dark:text-blue-400/90">
              {selectedFeatureType?.id === 'place'
                ? 'Mark the centre of this place — tap the map or use your location'
                : selectedFeatureType?.id === 'motorway_junction'
                  ? 'On the exit ramp or slip-road where traffic leaves the motorway'
                  : 'Tap the map, use your location, or enter coordinates below'}
            </p>
            {userLocation && (
              <p className="text-xs text-blue-600/90 dark:text-blue-400/90 mt-2">
                Your location is shown on the map
                {userLocation.accuracyM != null ? ` (±${Math.round(userLocation.accuracyM)} m)` : ''}.
              </p>
            )}
          </>
        )}
        {geometryMode === 'draw' && (
          <>
            <Square size={32} className="mx-auto text-blue-600 dark:text-blue-400 mb-2" />
            <p className="text-blue-800 dark:text-blue-200 font-medium">Draw on the map</p>
            <p className="text-sm text-blue-600 dark:text-blue-400/90">
              {markingDrawsPolygonLocal
                ? 'Tap points to create a boundary, double-click the last point to finish'
                : 'Tap points along the path, double-click to finish the line'}
            </p>
            {selectedFeatureType?.id === 'road' && !markingDrawsPolygonLocal ? (
              <p className="text-xs text-blue-700 dark:text-blue-300/90 mt-2 font-medium">
                Traffic runs the same way you draw the line — trace along flow when you can.
              </p>
            ) : null}
            <p className="text-xs text-blue-600/90 dark:text-blue-400/90 mt-2">
              Zoom out for context, then zoom in to level {SURVEY_MIN_ZOOM} to anchor points precisely. Pan and tilt freely. Use the buttons below to undo or clear.
            </p>
            <div className="flex flex-wrap gap-2 justify-center mt-3">
              <button
                type="button"
                onClick={handleDrawUndo}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-blue-200 dark:border-blue-800 bg-white/80 dark:bg-neutral-800/80 text-sm font-medium text-blue-800 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-950/40"
              >
                <Undo2 size={16} />
                Undo last point
              </button>
              <button
                type="button"
                onClick={handleDrawUndoFirst}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-blue-200 dark:border-blue-800 bg-white/80 dark:bg-neutral-800/80 text-sm font-medium text-blue-800 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-950/40"
              >
                <Undo2 size={16} className="scale-x-[-1]" />
                Undo first point
              </button>
              <button
                type="button"
                onClick={handleDrawClear}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 dark:border-red-900/60 bg-white/80 dark:bg-neutral-800/80 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
              >
                <Trash2 size={16} />
                Clear
              </button>
            </div>
            {['landuse', 'landcover', 'water'].includes(selectedFeatureType?.id ?? '') && (
              <p className="text-xs text-amber-700 dark:text-amber-300/90 mt-2">
                Under tree cover, drawing on the map is usually more accurate than GPS trace.
              </p>
            )}
            {geometryCleanupToolbar}
          </>
        )}
        {geometryMode === 'trace' && (
          <>
            <Navigation2 size={32} className="mx-auto text-emerald-600 dark:text-emerald-400 mb-2" />
            <p className="text-emerald-800 dark:text-emerald-200 font-medium">
              {roadMarkingTargetRef.current === 'divide'
                ? 'Trace the divide (median or pavement seam)'
                : roadMarkingTargetRef.current === 'strip_a'
                  ? (stripACaptureMode === 'trace_outer_kerb'
                    ? 'Trace property-side kerb (A)'
                    : 'Trace carriageway A')
                  : roadMarkingTargetRef.current === 'strip_b'
                    ? (stripBCaptureMode === 'trace_outer_kerb'
                      ? 'Trace property-side kerb (B)'
                      : 'Trace carriageway B')
                  : roadMarkingTargetRef.current === 'single_strip'
                    ? (singleStripCaptureMode === 'trace_outer_kerb' ? 'Trace property-side kerb' : 'Trace centre line')
                    : awaitingCarriagewayB
                ? 'Trace the opposite carriageway (B)'
                : traceCaptureMode === 'corners'
                  ? 'Mark corners with GPS'
                  : isTracing
                    ? 'Recording your path...'
                    : 'Ready to trace'}
            </p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              {roadMarkingTargetRef.current === 'divide'
                ? 'Walk the centre of the median or seam once; strip centres can be derived from widths.'
                : roadMarkingTargetRef.current === 'strip_a'
                  ? (stripACaptureMode === 'trace_outer_kerb'
                    ? 'Walk the property-side kerb for carriageway A with the paved strip on your right. Width in Details derives the centreline.'
                    : 'Walk or draw the centre of carriageway A.')
                  : roadMarkingTargetRef.current === 'strip_b'
                    ? (stripBCaptureMode === 'trace_outer_kerb'
                      ? 'Walk the property-side kerb for carriageway B with the paved strip on your right. Width in Details derives the centreline.'
                      : 'Walk or draw carriageway B parallel to the amber guide (A).')
                  : roadMarkingTargetRef.current === 'single_strip'
                    ? (singleStripCaptureMode === 'trace_outer_kerb'
                      ? 'Walk the property-side kerb with the carriageway on your right. Strip width in Details derives the centreline.'
                      : 'Walk the middle of the paved strip.')
                    : awaitingCarriagewayB
                ? 'Walk or draw carriageway B parallel to the amber guide (A).'
                : traceCaptureMode === 'corners'
                ? traceProducesPolygon(markingGeometryFeatureType)
                  ? 'Stand at each corner and tap Add corner'
                  : 'Stand at each bend or endpoint and tap Add corner'
                : isTracing
                  ? `${tracePoints.length} points recorded`
                  : traceProducesPolygon(markingGeometryFeatureType)
                    ? 'Press Start and walk the perimeter'
                    : 'Press Start and walk the path'}
            </p>
            {geometryCleanupToolbar}
          </>
        )}
        {geometryMode === 'coordinates' && (
          <>
            <Crosshair size={32} className="mx-auto text-violet-600 dark:text-violet-400 mb-2" />
            <p className="text-violet-800 dark:text-violet-200 font-medium">Enter Coordinates</p>
            <p className="text-sm text-violet-600 dark:text-violet-400/90">Paste or type latitude &amp; longitude</p>
          </>
        )}
        {geometryMode === 'link' && (
          <>
            <Link size={32} className="mx-auto text-cyan-600 dark:text-cyan-400 mb-2" />
            <p className="text-cyan-800 dark:text-cyan-200 font-medium">Paste a Map Link</p>
            <p className="text-sm text-cyan-600 dark:text-cyan-400/90">Paste any map share link to extract the location</p>
          </>
        )}
      </div>

      {onUseMyLocation && (geometryMode === 'point' || geometryMode === 'coordinates' || geometryMode === 'link') && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => void handleUseMyLocation()}
            disabled={isLocating}
            className="w-full py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-md flex items-center justify-center gap-2"
          >
            {isLocating ? <Loader2 size={18} className="animate-spin" /> : <Locate size={18} />}
            {isLocating ? 'Getting location…' : 'Use my location'}
          </button>
          {locationPlacementError && (
            <p className="text-xs text-red-500 dark:text-red-400 text-center">{locationPlacementError}</p>
          )}
        </div>
      )}

      {geometryMode === 'coordinates' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-neutral-400 mb-1">Latitude</label>
              <input
                type="text"
                inputMode="decimal"
                value={coordLat}
                onChange={(e) => setCoordLat(e.target.value)}
                placeholder="e.g. 5.6037"
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 placeholder:text-gray-400 dark:placeholder:text-neutral-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-neutral-400 mb-1">Longitude</label>
              <input
                type="text"
                inputMode="decimal"
                value={coordLng}
                onChange={(e) => setCoordLng(e.target.value)}
                placeholder="e.g. -0.1870"
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 placeholder:text-gray-400 dark:placeholder:text-neutral-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm font-mono"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleCoordinatePlot}
            disabled={!coordLat.trim() || !coordLng.trim()}
            className="w-full py-3 bg-violet-500 text-white rounded-xl font-semibold hover:bg-violet-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-neutral-600 disabled:cursor-not-allowed shadow-md flex items-center justify-center gap-2"
          >
            <MapPin size={18} />
            Plot on Map
          </button>
        </div>
      )}

      {geometryMode === 'link' && (
        <div className="space-y-3">
          <input
            type="text"
            value={linkInput}
            onChange={(e) => { setLinkInput(e.target.value); setLinkError(null); }}
            placeholder="Paste map link here"
            className="w-full px-3 py-2.5 border border-gray-300 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 placeholder:text-gray-400 dark:placeholder:text-neutral-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
          />
          {linkError && (
            <p className="text-xs text-red-500 dark:text-red-400">{linkError}</p>
          )}
          <button
            type="button"
            onClick={handleLinkPlot}
            disabled={!linkInput.trim()}
            className="w-full py-3 bg-cyan-500 text-white rounded-xl font-semibold hover:bg-cyan-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-neutral-600 disabled:cursor-not-allowed shadow-md flex items-center justify-center gap-2"
          >
            <MapPin size={18} />
            Use This Location
          </button>
        </div>
      )}

      {hasGeometry && (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-2xl p-4 flex items-center gap-3 border border-emerald-100/80 dark:border-emerald-900/50">
          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center shrink-0">
            <Check size={20} className="text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-emerald-800 dark:text-emerald-200">Marked on map. Review before labeling.</p>
            {selectedLocation && geometryMode === 'point' && (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">Pin placed. Pan the map to adjust if needed.</p>
            )}
            {selectedLocation && (geometryMode === 'coordinates' || geometryMode === 'link') && (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">Location set from your input.</p>
            )}
            {drawnGeometry && (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                {drawnGeometry.type === 'LineString'
                  ? 'Line drawn on map'
                  : 'Boundary drawn on map'}
                {markedPolygonAreaLabel ? ` · ~${markedPolygonAreaLabel}` : ''}
              </p>
            )}
            {tracePoints.length > 0 && traceReady && markedPolygonAreaLabel && traceProducesPolygon(markingGeometryFeatureType) && (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">Area ~{markedPolygonAreaLabel}</p>
            )}
            {tracePoints.length > 0 && (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                {tracePoints.length} GPS point{tracePoints.length === 1 ? '' : 's'}
                {traceReady
                  ? traceProducesPolygon(markingGeometryFeatureType)
                    ? ' · perimeter closed'
                    : ' · path ready'
                  : ` · need ${traceMinimumPoints(markingGeometryFeatureType) - tracePoints.length} more`}
              </p>
            )}
            {tracePostProcessSummary && !isTracing ? (
              <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                {tracePostProcessSummary}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => {
              if (geometryMode === 'trace') handleReplayTrace();
              else handleDrawClear();
            }}
            className="p-2 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100/80 dark:hover:bg-emerald-900/40 rounded-lg shrink-0"
            title="Clear and redo"
          >
            <RotateCcw size={18} />
          </button>
        </div>
      )}

      {geometryMode === 'trace' && (
        <div className="space-y-3">
          <div className="rounded-2xl border border-emerald-100 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/20 p-3 space-y-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200">
                GPS capture mode
              </p>
              <p className="text-xs text-emerald-600/90 dark:text-emerald-400/90 mt-1">
                Points save on this device if you close the panel or refresh the page.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={isTracing || isAddingTraceCorner || (tracePoints.length > 0 && traceCaptureMode !== 'continuous')}
                onClick={() => handleTraceCaptureModeChange('continuous')}
                className={`rounded-xl border px-3 py-2.5 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  traceCaptureMode === 'continuous'
                    ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                    : 'border-emerald-200 dark:border-emerald-800 bg-white dark:bg-neutral-800 text-emerald-900 dark:text-emerald-100 hover:bg-emerald-100/60 dark:hover:bg-emerald-950/40'
                }`}
              >
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  <Play size={16} />
                  Walk continuously
                </span>
                <span className={`block text-xs mt-0.5 ${traceCaptureMode === 'continuous' ? 'text-emerald-50/90' : 'text-emerald-700 dark:text-emerald-300'}`}>
                  Record while you move
                </span>
              </button>
              <button
                type="button"
                disabled={isTracing || isAddingTraceCorner || (tracePoints.length > 0 && traceCaptureMode !== 'corners')}
                onClick={() => handleTraceCaptureModeChange('corners')}
                className={`rounded-xl border px-3 py-2.5 text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  traceCaptureMode === 'corners'
                    ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                    : 'border-emerald-200 dark:border-emerald-800 bg-white dark:bg-neutral-800 text-emerald-900 dark:text-emerald-100 hover:bg-emerald-100/60 dark:hover:bg-emerald-950/40'
                }`}
              >
                <span className="flex items-center gap-1.5 text-sm font-semibold">
                  <Locate size={16} />
                  Mark corners
                </span>
                <span className={`block text-xs mt-0.5 ${traceCaptureMode === 'corners' ? 'text-emerald-50/90' : 'text-emerald-700 dark:text-emerald-300'}`}>
                  One GPS point per stop
                </span>
              </button>
            </div>

            {traceCaptureMode === 'continuous' ? (
              <>
                <div>
                  <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-0.5">
                    {traceSamplingMode === 'distance'
                      ? `Smart trace · records a point every ${traceMinDistanceM} m`
                      : 'Every fix · records each GPS update'}
                  </p>
                  <p className="text-xs text-emerald-600/90 dark:text-emerald-400/90 mt-1">
                    When you stop tracing, resting runs are collapsed and GPS spikes are removed.
                    Optional resampling spaces points evenly along the cleaned path.
                  </p>
                </div>

                {traceSamplingMode === 'distance' ? (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-emerald-800/80 dark:text-emerald-200/80">
                      Min distance between points
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {TRACE_DISTANCE_OPTIONS_M.map((distanceM) => {
                        const selected = traceMinDistanceM === distanceM;
                        const locked = isTracing;
                        return (
                          <button
                            key={distanceM}
                            type="button"
                            disabled={locked || !onTraceMinDistanceMChange}
                            onClick={() => onTraceMinDistanceMChange?.(distanceM)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              selected
                                ? 'bg-emerald-600 text-white shadow-sm'
                                : 'bg-white dark:bg-neutral-800 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100/80 dark:hover:bg-emerald-950/50'
                            }`}
                          >
                            {distanceM} m
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={() => setTraceAdvancedOpen((open) => !open)}
                  className="flex w-full items-center justify-between text-xs font-medium text-emerald-800/90 dark:text-emerald-200/90 hover:text-emerald-900 dark:hover:text-emerald-100"
                >
                  Advanced
                  {traceAdvancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {traceAdvancedOpen ? (
                  <div className="space-y-2">
                    <label
                      className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${
                        isTracing
                          ? 'border-emerald-200/70 dark:border-emerald-900/40 opacity-60 cursor-not-allowed'
                          : 'border-emerald-200 dark:border-emerald-800 cursor-pointer hover:bg-emerald-100/50 dark:hover:bg-emerald-950/30'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={traceSamplingMode === 'always'}
                        disabled={isTracing || !onTraceSamplingModeChange}
                        onChange={(e) =>
                          onTraceSamplingModeChange?.(e.target.checked ? 'always' : 'distance')
                        }
                        className="mt-0.5 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-emerald-900 dark:text-emerald-100">
                          Record every fix
                        </span>
                        <span className="block text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
                          Dense capture for short segments or tight curves. Uses more points.
                        </span>
                      </span>
                    </label>
                    <label
                      className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 ${
                        isTracing
                          ? 'border-emerald-200/70 dark:border-emerald-900/40 opacity-60 cursor-not-allowed'
                          : 'border-emerald-200 dark:border-emerald-800 cursor-pointer hover:bg-emerald-100/50 dark:hover:bg-emerald-950/30'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={traceResampleOnStop}
                        disabled={isTracing || !onTraceResampleOnStopChange}
                        onChange={(e) => onTraceResampleOnStopChange?.(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span>
                        <span className="block text-sm font-medium text-emerald-900 dark:text-emerald-100">
                          Resample on stop
                        </span>
                        <span className="block text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
                          After cleanup, place points every {traceMinDistanceM} m along the path.
                          Off by default — keeps captured vertices unless you want even spacing.
                        </span>
                      </span>
                    </label>
                  </div>
                ) : null}

                {isTracing ? (
                  <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80">
                    Sampling settings are locked while tracing. Stop to change mode or distance.
                  </p>
                ) : null}

                {tracePoints.length >= 2 && !isTracing ? (
                  <div className="space-y-1.5 pt-1 border-t border-emerald-200/60 dark:border-emerald-800/50">
                    <p className="text-xs font-medium text-emerald-800/80 dark:text-emerald-200/80">
                      Clean & refine path
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        disabled={!onRemoveTraceOutliers || tracePoints.length < 3}
                        onClick={() => onRemoveTraceOutliers?.()}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white dark:bg-neutral-800 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100/80 dark:hover:bg-emerald-950/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Drop GPS spikes off the line"
                      >
                        Remove outliers
                      </button>
                      <button
                        type="button"
                        disabled={!onStraightenTrace || tracePoints.length < 3}
                        onClick={() => onStraightenTrace?.()}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white dark:bg-neutral-800 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100/80 dark:hover:bg-emerald-950/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Drop mid points that sit on a straight segment"
                      >
                        Straighten
                      </button>
                      <button
                        type="button"
                        disabled={!onThinTrace || tracePoints.length < 2}
                        onClick={() => onThinTrace?.()}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white dark:bg-neutral-800 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100/80 dark:hover:bg-emerald-950/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Fewer vertices along the path"
                      >
                        Thin (~{traceThinSpacingM} m)
                      </button>
                      <button
                        type="button"
                        disabled={!onResampleFinerTrace || traceFinerSpacingM == null || tracePoints.length < 2}
                        onClick={() => onResampleFinerTrace?.()}
                        className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white dark:bg-neutral-800 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100/80 dark:hover:bg-emerald-950/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="More vertices at tighter spacing"
                      >
                        {traceFinerSpacingM != null
                          ? `Resample finer (~${traceFinerSpacingM} m)`
                          : 'Resample finer'}
                      </button>
                      {canResetTracePath ? (
                        <button
                          type="button"
                          disabled={!onResetTracePath}
                          onClick={() => onResetTracePath?.()}
                          className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white dark:bg-neutral-800 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100/80 dark:hover:bg-emerald-950/50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Restore raw GPS capture from when you stopped tracing"
                        >
                          Reset path
                        </button>
                      ) : null}
                    </div>
                    <p className="text-xs text-emerald-600/90 dark:text-emerald-400/90">
                      Remove outliers and straighten clean GPS noise; thin / resample finer adjust
                      density. Reset path restores the raw GPS capture from when you stopped.
                    </p>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                {traceProducesPolygon(markingGeometryFeatureType)
                  ? 'Walk to each corner, stand still, and wait for the map GPS pill to show ±15 m or less, then tap Add corner.'
                  : 'Walk to each bend or endpoint, stand still until the GPS pill shows ±15 m or less, then tap Add corner.'}
              </p>
            )}
          </div>

          {traceCaptureMode === 'continuous' ? (
            <div className="flex gap-3">
              {!isTracing ? (
                <button
                  type="button"
                  onClick={onStartTrace}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-emerald-500 dark:bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-600 dark:hover:bg-emerald-500 transition-colors shadow-md"
                >
                  <Play size={20} />
                  {tracePoints.length > 0 ? 'Resume Tracing' : 'Start Tracing'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onStopTrace}
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors shadow-md"
                >
                  <Pause size={20} />
                  Stop Tracing
                </button>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => void handleAddTraceCorner()}
              disabled={isAddingTraceCorner || !onAddTraceCorner}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-emerald-500 dark:bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-600 dark:hover:bg-emerald-500 transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isAddingTraceCorner ? <Loader2 size={20} className="animate-spin" /> : <Locate size={20} />}
              {isAddingTraceCorner
                ? 'Getting location…'
                : tracePoints.length === 0
                  ? 'Add first corner'
                  : 'Add corner'}
            </button>
          )}

          {cornerCaptureError ? (
            <p className="text-xs text-red-500 dark:text-red-400 text-center">{cornerCaptureError}</p>
          ) : null}

          {tracePoints.length > 0 && (traceCaptureMode === 'corners' || !isTracing) ? (
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                {tracePathRefineEditing ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onTraceEditUndo?.()}
                      disabled={!onTraceEditUndo || !traceEditCanUndo}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold rounded-xl border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 disabled:opacity-40"
                    >
                      <Undo2 size={16} />
                      Undo
                    </button>
                    <button
                      type="button"
                      onClick={() => onTraceEditRedo?.()}
                      disabled={!onTraceEditRedo || !traceEditCanRedo}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold rounded-xl border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 disabled:opacity-40"
                    >
                      <Redo2 size={16} />
                      Redo
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={onUndoTracePoint}
                      disabled={!onUndoTracePoint}
                      className="flex-1 py-2.5 text-sm font-semibold rounded-xl border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 disabled:opacity-40"
                    >
                      Undo last point
                    </button>
                    <button
                      type="button"
                      onClick={() => onUndoFirstTracePoint?.()}
                      disabled={!onUndoFirstTracePoint}
                      className="flex-1 py-2.5 text-sm font-semibold rounded-xl border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 disabled:opacity-40"
                    >
                      Undo first point
                    </button>
                  </>
                )}
                {traceVertexMapEdit ? (
                  <button
                    type="button"
                    onClick={() => onDeleteSelectedTraceVertex?.()}
                    disabled={selectedTraceVertexIndex == null || !onDeleteSelectedTraceVertex}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold rounded-xl border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40 disabled:cursor-not-allowed min-w-[7.5rem]"
                  >
                    <MapPinOff size={16} />
                    Delete point
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={handleReplayTrace}
                className="w-full py-2.5 text-sm font-semibold rounded-xl border border-gray-200 dark:border-neutral-600 text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800"
              >
                Clear all
              </button>
            </div>
          ) : null}
        </div>
      )}

      <button
        type="button"
        onClick={handleLocationConfirm}
        disabled={!hasGeometry}
        className="w-full py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-neutral-600 disabled:cursor-not-allowed shadow-md"
      >
        Continue
      </button>
    </div>
    );
  };

  const inputClass =
    'w-full px-4 py-3 border border-gray-300 dark:border-neutral-600 rounded-xl bg-white dark:bg-neutral-800 text-gray-900 dark:text-neutral-100 placeholder:text-gray-400 dark:placeholder:text-neutral-500 outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-neutral-300 mb-1';

  // POI, Landmark, Building always get full form; so do edit and suggest-edit flows (except GSS metadata)
  const isFullDetails =
    !isGssMetadataEdit &&
    (selectedFeatureType?.id === 'poi' ||
    selectedFeatureType?.id === 'landmark' ||
    selectedFeatureType?.id === 'building' ||
    !!editContribution ||
    !!suggestEditForPlace);

  const showLocationSuburb = contributionShowsSuburbField(selectedFeatureType?.id, isFullDetails);

  const showBusinessContactDetails = contributionFeatureShowsBusinessDetails(
    selectedFeatureType?.id,
    selectedLandmarkGroup,
  );

  const officialCategoryMeta = useMemo(() => {
    if (!isOfficialAreaFlow) return selectedCategoryMeta;
    const level = (selectedCategory || officialLevel) as OfficialAreaLevel;
    const labels: Record<OfficialAreaLevel, string> = {
      country: 'Country',
      region: 'Region',
      district: 'District',
    };
    const icons: Record<OfficialAreaLevel, string> = {
      country: 'globe',
      region: 'map',
      district: 'landmark',
    };
    const colors: Record<OfficialAreaLevel, string> = {
      country: 'rose',
      region: 'indigo',
      district: 'violet',
    };
    return {
      id: level,
      name: labels[level] ?? level,
      icon: icons[level] ?? 'landmark',
      color: colors[level] ?? 'gray',
    };
  }, [isOfficialAreaFlow, selectedCategory, officialLevel, selectedCategoryMeta]);

  const renderOfficialSelect = () => (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-neutral-400 text-center">
        Ghana, regions, and districts are already on the map. Pick what you want to update. No new boundary or pin.
      </p>

      <div>
        <label className={labelClass} htmlFor="official-update-intent">
          What are you updating?
        </label>
        <select
          id="official-update-intent"
          value={officialUpdateIntent}
          onChange={(e) => setOfficialUpdateIntent(e.target.value as MetadataUpdateIntent)}
          className={inputClass}
        >
          <option value="description">Description (and source link)</option>
          <option value="photos">Photos</option>
          <option value="both">Description and photos</option>
        </select>
      </div>

      <div>
        <label className={labelClass} htmlFor="official-level">
          Area type
        </label>
        <select
          id="official-level"
          value={officialLevel}
          onChange={(e) => {
            const level = e.target.value as OfficialAreaLevel;
            setOfficialLevel(level);
            setOfficialBoundaryName('');
            setOfficialFilterRegion('');
          }}
          className={inputClass}
        >
          <option value="country">Country (Ghana)</option>
          <option value="region">Region</option>
          <option value="district">District / Municipal</option>
        </select>
      </div>

      {officialLevel === 'country' ? (
        <div className="rounded-xl border border-gray-200 dark:border-neutral-600 bg-gray-50/80 dark:bg-neutral-800/40 px-3 py-3">
          <p className="text-sm font-medium text-gray-900 dark:text-neutral-100">Ghana</p>
          <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">The national boundary is already on the map.</p>
        </div>
      ) : officialLevel === 'district' ? (
        <>
          <SearchableBoundaryInput
            label="Filter by region (optional)"
            value={officialFilterRegion}
            onChange={(regionName) => {
              setOfficialFilterRegion(regionName);
              setOfficialBoundaryName('');
            }}
            fetchOptions={fetchOfficialRegions}
            minQueryLength={0}
            placeholder="Search regions to narrow districts"
            inputClass={inputClass}
            labelClass={labelClass}
          />
          <SearchableBoundaryInput
            label="District *"
            value={officialBoundaryName}
            onChange={(districtName) => setOfficialBoundaryName(districtName)}
            fetchOptions={fetchOfficialDistricts}
            minQueryLength={0}
            strict
            placeholder={officialFilterRegion ? `Search districts in ${officialFilterRegion}` : 'Search all districts'}
            hint="Pick the official district or municipal area from the list."
            inputClass={inputClass}
            labelClass={labelClass}
          />
        </>
      ) : (
        <SearchableBoundaryInput
          label="Region *"
          value={officialBoundaryName}
          onChange={(regionName) => setOfficialBoundaryName(regionName)}
          fetchOptions={fetchOfficialRegions}
          minQueryLength={0}
          strict
          placeholder="Search or select a region"
          hint="Pick an official region from the list (e.g. Greater Accra Region)."
          inputClass={inputClass}
          labelClass={labelClass}
        />
      )}

      <button
        type="button"
        onClick={handleOfficialSelectContinue}
        disabled={
          officialSelectLoading
          || (officialLevel !== 'country' && !officialBoundaryName.trim())
        }
        className="w-full py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-neutral-600 disabled:cursor-not-allowed shadow-md flex items-center justify-center gap-2"
      >
        {officialSelectLoading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            Verifying…
          </>
        ) : (
          'Continue'
        )}
      </button>
    </div>
  );

  const resetBatchInputs = () => {
    setUnitInput('');
    setAutoNumberCount('');
    setAutoNumberStart('1');
  };

  const persistCurrentFloorDraft = () => {
    const floorKey = unitBatchFloor.trim();
    if (!floorKey || parseUnitFloorInput(floorKey) === null) return;
    floorBatchDraftsRef.current[floorKey] = {
      autoNumberStart,
      autoNumberCount,
      unitInput,
      unitMode,
    };
    lastActiveFloorRef.current = floorKey;
  };

  const restoreFloorDraft = (floorKey: string) => {
    const draft = floorBatchDraftsRef.current[floorKey];
    if (draft) {
      setAutoNumberStart(draft.autoNumberStart);
      setAutoNumberCount(draft.autoNumberCount);
      setUnitInput(draft.unitInput);
      setUnitMode(draft.unitMode);
    } else {
      resetBatchInputs();
    }
  };

  const setUnitModeAndPersist = (mode: 'manual' | 'auto') => {
    setUnitMode(mode);
    const floorKey = unitBatchFloor.trim();
    if (floorKey && parseUnitFloorInput(floorKey) !== null) {
      floorBatchDraftsRef.current[floorKey] = {
        autoNumberStart,
        autoNumberCount,
        unitInput,
        unitMode: mode,
      };
    }
  };

  const restoreBuildingUnitsEditingState = () => {
    const floorKey =
      lastActiveFloorRef.current ||
      (buildingUnits.length > 0
        ? String(Math.max(...buildingUnits.map((u) => u.floor)))
        : '1');
    setUnitBatchFloor(floorKey);
    restoreFloorDraft(floorKey);
  };

  const handleUnitBatchFloorChange = (value: string) => {
    persistCurrentFloorDraft();
    setUnitBatchFloor(value);
    const parsed = parseUnitFloorInput(value);
    if (parsed !== null) {
      const floorKey = value.trim();
      restoreFloorDraft(floorKey);
      lastActiveFloorRef.current = floorKey;
    }
  };

  const addManualUnit = () => {
    const label = unitInput.trim();
    const floor = parseUnitFloorInput(unitBatchFloor);
    if (!label) {
      setSubmitFeedback({ type: 'error', message: 'Enter a unit or room label.' });
      return;
    }
    if (floor === null) {
      setSubmitFeedback({
        type: 'error',
        message: 'Enter a floor number (1 = ground, 0 = underground, negative = basement).',
      });
      return;
    }
    if (buildingUnits.some((u) => u.label.toLowerCase() === label.toLowerCase() && u.floor === floor)) {
      setSubmitFeedback({ type: 'error', message: `“${label}” already exists on ${formatUnitFloorLabel(floor)}.` });
      return;
    }
    setBuildingUnits((prev) => [...prev, { label, floor }]);
    setUnitInput('');
    persistCurrentFloorDraft();
    setSubmitFeedback(null);
  };

  const generateAutoUnits = () => {
    const count = parseInt(autoNumberCount, 10);
    const start = parseInt(autoNumberStart, 10) || 1;
    const floor = parseUnitFloorInput(unitBatchFloor);
    if (floor === null) {
      setSubmitFeedback({
        type: 'error',
        message: 'Choose a floor before generating units (1 = ground, 0 = underground).',
      });
      return;
    }
    if (!count || count < 1 || count > 200) return;
    const units: BuildingUnit[] = [];
    for (let i = 0; i < count; i++) {
      units.push({ label: String(start + i), floor });
    }
    setBuildingUnits((prev) => {
      const merged = [...prev];
      for (const unit of units) {
        if (!merged.some((u) => u.label.toLowerCase() === unit.label.toLowerCase() && u.floor === unit.floor)) {
          merged.push(unit);
        }
      }
      return merged;
    });
    resetBatchInputs();
    delete floorBatchDraftsRef.current[unitBatchFloor.trim()];
    persistCurrentFloorDraft();
    setSubmitFeedback(null);
  };

  const getKnownFloorNumbers = (): number[] => {
    const floors = new Set<number>();
    for (const u of buildingUnits) {
      floors.add(u.floor);
    }
    for (const key of Object.keys(floorBatchDraftsRef.current)) {
      const parsed = parseUnitFloorInput(key);
      if (parsed !== null) floors.add(parsed);
    }
    const current = parseUnitFloorInput(unitBatchFloor);
    if (current !== null) floors.add(current);
    return [...floors].sort((a, b) => a - b);
  };

  const navigateToFloor = (floorKey: string) => {
    persistCurrentFloorDraft();
    setUnitBatchFloor(floorKey);
    lastActiveFloorRef.current = floorKey;
    restoreFloorDraft(floorKey);
    setSubmitFeedback(null);
  };

  const goToPreviousFloor = () => {
    const current = parseUnitFloorInput(unitBatchFloor);
    if (current === null) return;
    const lower = getKnownFloorNumbers().filter((f) => f < current);
    if (lower.length === 0) return;
    navigateToFloor(String(lower[lower.length - 1]));
  };

  const prepareAnotherFloor = () => {
    const current = parseUnitFloorInput(unitBatchFloor);
    const floorsUsed = new Set(buildingUnits.map((u) => u.floor));
    let next = current !== null ? current + 1 : 2;
    while (floorsUsed.has(next) && next < 200) next += 1;
    navigateToFloor(String(next));
  };

  const removeUnit = (label: string, floor: number) => {
    setBuildingUnits((prev) => prev.filter((u) => !(u.label === label && u.floor === floor)));
  };

  const clearCurrentFloorUnits = () => {
    const floor = parseUnitFloorInput(unitBatchFloor);
    if (floor === null) return;
    setBuildingUnits((prev) => prev.filter((u) => u.floor !== floor));
  };

  const renderBuildingUnits = () => {
    const currentFloor = parseUnitFloorInput(unitBatchFloor);
    const unitsOnCurrentFloor =
      currentFloor !== null ? buildingUnits.filter((u) => u.floor === currentFloor) : [];
    const otherFloorCount = buildingUnits.length - unitsOnCurrentFloor.length;
    const previousFloor =
      currentFloor !== null
        ? (() => {
            const lower = getKnownFloorNumbers().filter((f) => f < currentFloor);
            return lower.length ? lower[lower.length - 1] : null;
          })()
        : null;

    return (
    <div className="space-y-4">
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          <Building2 size={14} className="inline mr-1.5 -mt-0.5" />
          Add units or rooms within this building (apartments, offices, shops, bedrooms). Each entry needs a floor.
          You can skip this and add units later from <strong>My Contributions</strong> once the building is approved.
        </p>
      </div>

      <div>
        <label className={labelClass}>Floor for this batch *</label>
        <input
          type="number"
          value={unitBatchFloor}
          onChange={(e) => handleUnitBatchFloorChange(e.target.value)}
          placeholder="1"
          className={inputClass}
        />
        <p className="text-xs text-gray-500 dark:text-neutral-500 mt-1">
          1 = ground, 0 = underground, -1 / -2 = basement. Change floor and generate again for another level.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setUnitModeAndPersist('manual')}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${unitMode === 'manual'
            ? 'bg-primary-500 text-white'
            : 'bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-neutral-300'
            }`}
        >
          Add Manually
        </button>
        <button
          type="button"
          onClick={() => setUnitModeAndPersist('auto')}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${unitMode === 'auto'
            ? 'bg-primary-500 text-white'
            : 'bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-neutral-300'
            }`}
        >
          Auto-Number
        </button>
      </div>

      {unitMode === 'manual' ? (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="text"
                value={unitInput}
                onChange={(e) => setUnitInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addManualUnit()}
                placeholder="Unit or room (e.g. 1, A1, Shop 3)"
                className={inputClass}
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={addManualUnit}
              className="px-3 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors"
            >
              <Plus size={18} />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-neutral-400">
            Generate numbered units on the selected floor. Pick another floor above to add another batch.
          </p>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className={labelClass}>Start from</label>
              <input
                type="number"
                min={1}
                value={autoNumberStart}
                onChange={(e) => setAutoNumberStart(e.target.value)}
                placeholder="1"
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label className={labelClass}>Total units</label>
              <input
                type="number"
                min={1}
                max={200}
                value={autoNumberCount}
                onChange={(e) => setAutoNumberCount(e.target.value)}
                placeholder="e.g., 10"
                className={inputClass}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={generateAutoUnits}
            disabled={!autoNumberCount || parseInt(autoNumberCount, 10) < 1 || parseUnitFloorInput(unitBatchFloor) === null}
            className="w-full py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-neutral-600 flex items-center justify-center gap-2"
          >
            <Hash size={16} />
            Generate {autoNumberCount ? `${autoNumberCount} units` : 'units'}
          </button>
        </div>
      )}

      {otherFloorCount > 0 && unitsOnCurrentFloor.length === 0 && currentFloor !== null && (
        <p className="text-xs text-gray-500 dark:text-neutral-500">
          No units on {formatUnitFloorLabel(currentFloor)} yet.{' '}
          {otherFloorCount} unit{otherFloorCount !== 1 ? 's' : ''} on other floor
          {otherFloorCount !== 1 ? 's' : ''}.
        </p>
      )}

      {/* Units list — current floor only */}
      {unitsOnCurrentFloor.length > 0 && currentFloor !== null && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
              {unitsOnCurrentFloor.length} unit{unitsOnCurrentFloor.length !== 1 ? 's' : ''} on{' '}
              {formatUnitFloorLabel(currentFloor)}
              {otherFloorCount > 0 && (
                <span className="text-gray-400 dark:text-neutral-500 font-normal">
                  {' '}
                  · {buildingUnits.length} total
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={clearCurrentFloorUnits}
              className="text-xs text-red-500 hover:text-red-600"
            >
              Clear this floor
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl border border-gray-200 dark:border-neutral-600 p-2">
            {unitsOnCurrentFloor.map((unit) => (
              <div
                key={`${unit.floor}-${unit.label}`}
                className="flex items-center justify-between py-1.5 px-2 bg-gray-50 dark:bg-neutral-800 rounded-lg text-sm"
              >
                <span className="text-gray-900 dark:text-neutral-100">
                  <Hash size={12} className="inline mr-1 text-gray-400" />
                  {unit.label}
                </span>
                <button
                  type="button"
                  onClick={() => removeUnit(unit.label, unit.floor)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => {
            persistCurrentFloorDraft();
            setStep('review');
          }}
          className="w-full py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors shadow-md"
        >
          {buildingUnits.length > 0 ? 'Continue to Review' : 'Review & Submit'}
        </button>
        {(previousFloor !== null || buildingUnits.length > 0) && (
          <div className="flex gap-2">
            {previousFloor !== null && (
              <button
                type="button"
                onClick={goToPreviousFloor}
                className={`${buildingUnits.length > 0 ? 'flex-1' : 'w-full'} py-3 border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-neutral-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2`}
              >
                <ChevronLeft size={16} />
                {formatUnitFloorLabel(previousFloor)}
              </button>
            )}
            {buildingUnits.length > 0 && (
              <button
                type="button"
                onClick={prepareAnotherFloor}
                className={`${previousFloor !== null ? 'flex-1' : 'w-full'} py-3 border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 rounded-xl font-semibold hover:bg-primary-50 dark:hover:bg-primary-950/30 transition-colors flex items-center justify-center gap-2`}
              >
                <Plus size={16} />
                Add another floor
              </button>
            )}
          </div>
        )}
        {buildingUnits.length === 0 && (
          <button
            type="button"
            onClick={() => {
              setBuildingUnits([]);
              setStep('review');
            }}
            className="w-full py-2.5 text-gray-500 dark:text-neutral-400 text-sm font-medium hover:text-gray-700 dark:hover:text-neutral-200 flex items-center justify-center gap-1.5"
          >
            <SkipForward size={14} />
            Skip, add units later
          </button>
        )}
      </div>
    </div>
    );
  };

  const renderReview = () => {
    const isUpdate = !!enrichFeature && !editContribution;
    return (
      <div className="space-y-4">
        {isUpdate && (
          <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100">
            <strong className="font-semibold">{enrichFeature.name}</strong> already exists on the map.
            Your submission will update its details after review.
            {selectedFeatureType?.id === 'place' || isOfficialAreaFlow ? (
              <span className="block mt-1 text-xs text-amber-800/90 dark:text-amber-200/90">
                {isGssBoundaryPlace || isOfficialAreaFlow
                  ? 'The official boundary shape is never changed. Only the description and/or photos you provide are updated.'
                  : 'The existing map position or boundary is kept. We only update name, place type, and other details you provide.'}
              </span>
            ) : null}
          </div>
        )}
        <div className="bg-gray-50 dark:bg-neutral-800/80 rounded-xl p-4 space-y-3 border border-gray-100 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedCategoryMeta
                ? (colorClasses[selectedCategoryMeta.color] || colorClasses.gray).bg
                : 'bg-primary-100 dark:bg-primary-900'
                }`}
            >
              {selectedCategoryMeta ? (
                <DynamicIcon name={selectedCategoryMeta.icon} size={20} className="text-white" />
              ) : selectedFeatureType?.icon ? (
                <DynamicIcon name={selectedFeatureType.icon} size={20} className="text-primary-600 dark:text-primary-300" />
              ) : (
                <MapPin size={20} className="text-primary-600 dark:text-primary-300" />
              )}
            </div>
            <div>
              <p className="font-semibold text-gray-900 dark:text-neutral-100">{name || selectedFeatureType?.name || 'Unnamed'}</p>
              <p className="text-sm text-gray-500 dark:text-neutral-400">
                {selectedFeatureType?.name && <span className="text-primary-500 dark:text-primary-400">{selectedFeatureType.name}</span>}
                {selectedFeatureType?.name && selectedCategoryMeta?.name && ' · '}
                {selectedCategoryMeta?.name}
              </p>
            </div>
          </div>

          {addressLine.trim() && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">
                {isPlaceFeature ? 'Within:' : 'Address:'}
              </span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">{addressLine}</span>
            </div>
          )}

          {showBusinessContactDetails && phone && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">Phone:</span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">{phone}</span>
            </div>
          )}

          {description && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">Description:</span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">{description}</span>
              {descriptionSourceUrl.trim() && (
                <>
                  {' '}
                  <a
                    href={descriptionSourceUrl.trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                  >
                    More
                  </a>
                </>
              )}
            </div>
          )}

          {selectedFeatureType?.id === 'tree' && treeHeight.trim() && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">Height:</span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">{treeHeight.trim()} m</span>
            </div>
          )}

          {selectedFeatureType?.id === 'aeroway' && (aerowayRef.trim() || aerowaySurface.trim() || aerowayWidth.trim()) && (
            <div className="text-sm space-y-1">
              {aerowayRef.trim() && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Ref:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{aerowayRef.trim()}</span>
                </div>
              )}
              {aerowaySurface.trim() && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Surface:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{aerowaySurface.trim()}</span>
                </div>
              )}
              {aerowayWidth.trim() && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Width:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{aerowayWidth.trim()} m</span>
                </div>
              )}
            </div>
          )}

          {selectedFeatureType?.id === 'motorway_junction' && (junctionRef.trim() || junctionExitRoads.length > 0) && (
            <div className="text-sm space-y-1">
              {junctionRef.trim() && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Junction ref:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{junctionRef.trim()}</span>
                </div>
              )}
              {junctionExitRoads.length > 0 && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Exit to:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{formatJunctionExitTo(junctionExitRoads)}</span>
                </div>
              )}
            </div>
          )}

          {selectedFeatureType?.id === 'transport_stop' && (
            stopRef.trim() || stopRoutes.length > 0 || stopOperators.length > 0 || stopShelter || stopBench || stopLighting
          ) && (
            <div className="text-sm space-y-1">
              {stopRef.trim() && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Stop ref:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{stopRef.trim()}</span>
                </div>
              )}
              {stopRoutes.length > 0 && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Routes:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{stopRoutes.join(' · ')}</span>
                </div>
              )}
              {stopOperators.length > 0 && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Operators:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{stopOperators.join(' · ')}</span>
                </div>
              )}
              {(stopShelter || stopBench || stopLighting) && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Facilities:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">
                    {[stopShelter && 'Shelter', stopBench && 'Bench', stopLighting && 'Lighting'].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {showBusinessContactDetails && openingHours.trim() && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">Opening hours:</span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">{openingHours.trim()}</span>
            </div>
          )}

          {['poi', 'landmark', 'building'].includes(selectedFeatureType?.id ?? '') && (
            poiCuisine.trim() || poiBrand.trim() || poiWheelchair.trim() || paymentMobileMoney || paymentCard
          ) && (
            <div className="text-sm space-y-1">
              {poiCuisine.trim() && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Cuisine:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{poiCuisine.trim()}</span>
                </div>
              )}
              {poiBrand.trim() && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Brand:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{poiBrand.trim()}</span>
                </div>
              )}
              {poiWheelchair.trim() && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Wheelchair:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">{poiWheelchair.trim()}</span>
                </div>
              )}
              {(paymentMobileMoney || paymentCard) && (
                <div>
                  <span className="text-gray-500 dark:text-neutral-400">Payment:</span>{' '}
                  <span className="text-gray-900 dark:text-neutral-100">
                    {[paymentMobileMoney && 'Mobile money', paymentCard && 'Card'].filter(Boolean).join(', ')}
                    {paymentMobileMoney && mobileMoneyProvider.trim() ? ` (${mobileMoneyProvider.trim()})` : ''}
                  </span>
                </div>
              )}
            </div>
          )}

          {isOfficialAreaFlow && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">Updating:</span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">
                {officialUpdateIntent === 'description'
                  ? 'Description'
                  : officialUpdateIntent === 'photos'
                    ? 'Photos'
                    : 'Description and photos'}
              </span>
            </div>
          )}

          {!isGssBoundaryPlace && !isOfficialAreaFlow && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">Location:</span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">
                {geometryMode === 'point' ? 'Point marker' : geometryMode === 'draw' ? (drawnGeometry?.type === 'LineString' ? 'Drawn line' : 'Drawn boundary') : geometryMode === 'coordinates' ? 'Coordinates' : geometryMode === 'enrich' ? 'Enriched feature' : geometryMode === 'link' ? 'Map link' : 'GPS trace'}
              </span>
            </div>
          )}

          {photoDataUrls.length > 0 && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">Photos:</span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">{photoDataUrls.length} attached</span>
            </div>
          )}

          {buildingUnits.length > 0 && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-neutral-400">Units / rooms:</span>{' '}
              <span className="text-gray-900 dark:text-neutral-100">
                {summarizeBuildingUnitsForReview(buildingUnits)}
              </span>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 dark:text-neutral-400 text-center">
          Your contribution will be reviewed before appearing on the map. This usually takes 24-48 hours.
        </p>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:bg-gray-400 dark:disabled:bg-neutral-600 shadow-md"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={20} className="animate-spin" />
              {editContribution?.status === 'approved'
                ? 'Submitting revision...'
                : editContribution
                  ? 'Resubmitting...'
                  : 'Submitting...'}
            </span>
          ) : (
            editContribution?.status === 'approved'
              ? 'Submit Revision for Review'
              : editContribution
                ? 'Resubmit for Review'
                : isUpdate
                  ? 'Submit Update for Review'
                  : 'Submit for Review'
          )}
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  const isMinimizedSheet = sheetSnap === 'minimized' && !isCompactSheetStep;
  const showMinimizedMarkingChrome =
    isMinimizedSheet && (step === 'marking' || step === 'enclosure_marking');

  return (
    <motion.div
      ref={sheetRef}
      initial={{ y: '100%' }}
      animate={sheetControls}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      drag={isCompactSheetStep ? false : 'y'}
      dragControls={sheetDragControls}
      dragListener={false}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={0.15}
      dragMomentum={false}
      onDragStart={handleSheetDragStart}
      onDragEnd={handleSheetDragEnd}
      style={{
        touchAction: 'pan-y',
        height: resolveSheetHeight(step, sheetSnap),
        maxHeight: resolveSheetMaxHeight(step),
      }}
      className="fixed bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 rounded-t-3xl shadow-2xl z-[60] overflow-hidden border-t border-gray-100 dark:border-neutral-700 flex flex-col safe-area-bottom transition-[height] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
    >
      {/* Drag handle */}
      <button
        type="button"
        aria-label={
          sheetSnap === 'expanded'
            ? 'Collapse panel'
            : sheetSnap === 'minimized'
              ? 'Expand panel'
              : sheetSnap === 'compact'
                ? 'Expand panel'
                : 'Expand panel'
        }
        onClick={handleSheetHandleClick}
        onPointerDown={(e) => {
          if (!isCompactSheetStep) sheetDragControls.start(e);
        }}
        className="flex flex-col items-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing flex-shrink-0 w-full select-none touch-none"
      >
        <div className="w-10 h-1 bg-gray-300 dark:bg-neutral-600 rounded-full" />
        <span className="text-[11px] text-gray-400 dark:text-neutral-500 mt-0.5">
          {isCompactSheetStep
            ? 'Sheet fits content'
            : sheetSnap === 'expanded'
              ? 'Drag down to collapse'
              : sheetSnap === 'minimized'
                ? 'Drag up to expand'
                : sheetSnap === 'compact'
                  ? 'Drag down to minimize map'
                  : 'Drag up or down'}
        </span>
      </button>

      {showMinimizedMarkingChrome ? (
        <div className="px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex-shrink-0">
          <button
            type="button"
            onClick={handleLocationConfirm}
            disabled={!hasGeometry}
            className="w-full py-3.5 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-neutral-600 disabled:cursor-not-allowed shadow-md"
          >
            Continue
          </button>
        </div>
      ) : null}

      {isMinimizedSheet && step !== 'marking' ? (
        <p className="px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-center text-xs text-gray-500 dark:text-neutral-400 flex-shrink-0">
          Drag up to continue
        </p>
      ) : null}

      {!isMinimizedSheet ? (
      <>
      <div className="flex items-center justify-between p-4 pt-2 sm:pt-2 border-b border-gray-100 dark:border-neutral-700 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          {step !== 'feature_type' && (
            <button
              type="button"
              onClick={handleBack}
              className="p-1 text-gray-500 dark:text-neutral-400 hover:text-gray-800 dark:hover:text-neutral-100 shrink-0 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100 truncate">{getStepTitle()}</h2>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="p-2 text-gray-500 dark:text-neutral-400 hover:text-gray-800 dark:hover:text-neutral-100 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-800 shrink-0"
        >
          <X size={24} />
        </button>
      </div>

      {ALLOW_FIELD_MAPPING_OUTSIDE_GHANA ? (
        <div className="mx-4 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs text-amber-900 dark:text-amber-100 flex-shrink-0">
          QA mode: live GPS trace and contributions work outside Ghana. Turn off before production release.
        </div>
      ) : null}

      {tracePoints.length > 0 && step !== 'marking' ? (
        <div className="mx-4 mt-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-900 dark:text-emerald-100 flex-shrink-0">
          {tracePoints.length} GPS point{tracePoints.length === 1 ? '' : 's'} saved on the map
          {savedTraceFeatureLabel ? ` from ${savedTraceFeatureLabel.toLowerCase()} tracing` : ''}.
          {' '}Pick GPS Trace to continue, or clear from the trace screen.
        </div>
      ) : null}

      <div
        className="flex-1 min-h-0 overflow-y-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))] overscroll-contain"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {step === 'feature_type' && renderFeatureTypeSelection()}
            {step === 'enclosure_method' && (
              <EnclosureMethodStep onSelect={beginEnclosureMapStep} />
            )}
            {step === 'enclosure_capture_mode' && renderEnclosureCaptureMode()}
            {step === 'enclosure_map' && enclosureMethod ? (
              <EnclosureMapStep
                method={enclosureMethod}
                boundaryRoadCount={enclosureBoundaryRoadIds.length}
                loading={enclosureLoading}
                feedback={enclosureFeedback}
                onFindFromBoundary={
                  enclosureMethod === 'boundary' ? runEnclosureBoundaryDetect : undefined
                }
              />
            ) : null}
            {step === 'enclosure_preview' && (
              <EnclosurePreviewStep
                detectResult={enclosureDetectResult}
                selectedCandidateId={enclosureSelectedCandidateId}
                onSelectCandidate={setEnclosureSelectedCandidateId}
                onEnrichExisting={handleEnclosureEnrichExisting}
                onPreviewSelected={handleEnclosurePreviewSelected}
                onConfirmBoundary={handleEnclosureConfirmBoundary}
                onRetry={handleEnclosureRetry}
                previewAreaSqm={enclosurePreviewAreaSqm}
              />
            )}
            {step === 'enclosure_feature_type' && (
              <EnclosureFeatureTypeStep
                options={ENCLOSURE_POLYGON_FEATURE_IDS.map((id) => FEATURE_TYPES.find((f) => f.id === id)!)
                  .filter(Boolean)
                  .map((ft) => ({
                    id: ft.id,
                    name: ft.name,
                    description: ft.description,
                    icon: ft.icon,
                  }))}
                onSelect={handleEnclosureTargetFeatureType}
              />
            )}
            {step === 'mode' && renderModeSelection()}
            {step === 'landmark_group' && renderLandmarkGroupSelection()}
            {step === 'landmark_placement' && renderLandmarkPlacementSelection()}
            {step === 'category' && renderCategorySelection()}
            {(step === 'road_layout' || step === 'road_capture_plan') && (
              <RoadGeometryWizard
                step={step}
                carriagewayCount={roadCarriagewayCount}
                singleStripCaptureMode={singleStripCaptureMode}
                onSingleStripCaptureModeChange={handleSingleStripCaptureModeChange}
                stripACaptureMode={stripACaptureMode}
                onStripACaptureModeChange={handleStripACaptureModeChange}
                stripBCaptureMode={stripBCaptureMode}
                onStripBCaptureModeChange={handleStripBCaptureModeChange}
                capturePlanError={roadCapturePlanError}
                onLayoutSelectSingle={() => handleRoadLayoutSelect(1)}
                onLayoutSelectDivided={() => handleRoadLayoutSelect(2)}
                onCapturePlanContinue={handleRoadCapturePlanContinue}
              />
            )}
            {step === 'official_select' && renderOfficialSelect()}
            {(step === 'marking' || step === 'enclosure_marking') && renderMarkingStep()}
            {step === 'enrich_select' && (
              <div className="space-y-4 text-center py-8">
                {enrichLoading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 animate-spin text-amber-500" />
                    <p className="text-sm text-gray-600 dark:text-neutral-400">Finding nearest {enrichTargetLabel.toLowerCase()}…</p>
                  </div>
                ) : (
                  <>
                    <div className="mx-auto w-16 h-16 bg-amber-100 dark:bg-amber-950/50 rounded-full flex items-center justify-center">
                      <Target size={32} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-sm text-gray-700 dark:text-neutral-300 font-medium">
                      {ENRICH_CONTAIN_FEATURE_TYPES.has(enrichSearchFeatureType ?? selectedFeatureType?.id ?? '')
                        ? <>Tap <span className="font-semibold text-amber-600 dark:text-amber-400">inside</span> the {enrichTargetLabel.toLowerCase()} boundary on the map</>
                        : <>Tap on or near an existing <span className="font-semibold text-amber-600 dark:text-amber-400">{enrichTargetLabel.toLowerCase()}</span> on the map</>}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-neutral-500">
                      We'll find the closest matching feature and let you fill in missing details like name, address, or description.
                    </p>
                  </>
                )}
              </div>
            )}
            {step === 'details' && (
              <>
                {canReopenContributionGeometryCapture
                  && !(
                    selectedFeatureType?.id === 'road'
                    && motorRoadSurveyFlow
                    && roadCarriagewayCount != null
                  ) ? (
                  <div className="mb-4 rounded-xl border border-sky-200 dark:border-sky-900/50 bg-sky-50/60 dark:bg-sky-950/20 p-3 space-y-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100">
                      Survey geometry
                    </p>
                    <p className="text-xs text-gray-600 dark:text-neutral-400">
                      Refine the line or shape on the map — tap a vertex, then Delete point (top-right or below).
                    </p>
                    {geometryMode === 'trace'
                      && !isTracing
                      && tracePoints.length >= 2 ? (
                      <p className="text-xs font-medium text-sky-800 dark:text-sky-200">
                        {selectedTraceVertexIndex != null
                          ? `Selected point ${selectedTraceVertexIndex + 1} — use Delete point or map controls.`
                          : 'Tap a path point on the map to select it.'}
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleEditContributionGeometry}
                        className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {step === 'details' ? 'Expand map editing panel' : 'Edit geometry on map'}
                      </button>
                      {geometryMode === 'trace'
                        && !isTracing
                        && tracePoints.length >= 2 ? (
                        <button
                          type="button"
                          onClick={() => onDeleteSelectedTraceVertex?.()}
                          disabled={selectedTraceVertexIndex == null || !onDeleteSelectedTraceVertex}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline disabled:opacity-40 disabled:no-underline"
                        >
                          <MapPinOff size={14} />
                          Delete point
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {geometryMode === 'enrich' && enrichFeature && (
                  <div className="mb-4 rounded-xl border border-amber-200/80 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-neutral-700 dark:bg-neutral-800/90 dark:text-neutral-200">
                    <p>
                      Updating existing <span className="font-semibold">{enrichFeature.category?.toLowerCase() || enrichTargetLabel.toLowerCase()}</span>
                      {enrichFeature.name ? <> — <span className="font-semibold">{enrichFeature.name}</span></> : null}
                      {markedPolygonAreaLabel ? <span className="block mt-1 text-xs text-amber-800/90 dark:text-neutral-400">Area: {markedPolygonAreaLabel}</span> : null}
                    </p>
                    <button
                      type="button"
                      onClick={resetEnrichSelection}
                      className="mt-2 text-xs font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-900 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      Pick a different feature on the map
                    </button>
                  </div>
                )}
              <RoadContributionDetailsForm
                details={roadDetails}
                motorRoadSurveyFlow={motorRoadSurveyFlow}
                selectedCategoryMeta={officialCategoryMeta ?? selectedCategoryMeta}
                singleStripCaptureMode={singleStripCaptureMode}
                onSingleStripCaptureModeChange={handleSingleStripCaptureModeChange}
                stripACaptureMode={stripACaptureMode}
                onStripACaptureModeChange={handleStripACaptureModeChange}
                stripBCaptureMode={stripBCaptureMode}
                onStripBCaptureModeChange={handleStripBCaptureModeChange}
                divideWidthM={divideWidthM}
                onDivideWidthMChange={setDivideWidthM}
                dividedDivideReady={!!dividedDivideLine}
                dividedCarriagewayAReady={dividedCarriagewayAReady}
                dividedCarriagewayBReady={dividedCarriagewayBReady}
                dividedCarriagewayReady={dividedCarriagewayReady}
                stripATraceReady={stripATraceReady}
                stripBTraceReady={stripBTraceReady}
                singleStripTraceReady={singleStripTraceReady}
                onStartMarkingDivide={handleStartMarkingDivide}
                onStartMarkingStripA={handleStartMarkingStripA}
                onStartMarkingStripB={handleStartMarkingStripB}
                onStartMarkingSingleStrip={handleStartMarkingSingleStrip}
                dividedSurveySetupHint={dividedSurveySetupHint}
                onDismissDividedSurveySetupHint={() => setDividedSurveySetupHint(false)}
                roadGeometryStatusRows={roadGeometryStatusRows}
                onEditRoadGeometry={handleEditRoadGeometry}
                onRoadCarriagewayCountChange={handleRoadCarriagewayCountChange}
                featureType={selectedFeatureType}
                selectedCategory={selectedCategory}
                categoryMeta={officialCategoryMeta ?? null}
                name={name}
                onNameChange={handleNameChange}
                onChangeCategoryStep={() => {
                  if (isOfficialAreaFlow) setStep('official_select');
                  else setStep('category');
                }}
                addrRegion={addrRegion}
                addrDistrict={addrDistrict}
                addrCity={addrCity}
                addrStreet={addrStreet}
                houseNumber={hostHouseNumber}
                lockHouseNumber={usesBuildingAddressFormat && !!hostHouseNumber.trim()}
                addrSuburb={addrSuburb}
                ghanaPostGps={ghanaPostGps}
                onAddrRegionChange={(name, meta) => {
                  setAddrRegion(name);
                  if (meta?.id) { setAddrDistrict(''); setAddrCity(''); setAddrSuburb(''); }
                }}
                onAddrDistrictChange={(name, meta) => {
                  setAddrDistrict(name);
                  if (meta?.id) { setAddrCity(''); setAddrSuburb(''); }
                }}
                onAddrCityChange={setAddrCity}
                onAddrStreetChange={setAddrStreet}
                onAddrSuburbChange={setAddrSuburb}
                onGhanaPostGpsChange={setGhanaPostGps}
                fetchRegions={fetchRegions}
                fetchDistricts={fetchDistricts}
                fetchTowns={fetchTowns}
                fetchSuburbs={fetchSuburbs}
                addressLine={addressLine}
                addressLookupStatus={addressLookupStatus}
                suggestedAddressFields={suggestedAddressFields}
                landmarkSourceGroup={selectedLandmarkGroup}
                phone={phone} onPhoneChange={setPhone}
                mobile={mobile} onMobileChange={setMobile}
                whatsapp={whatsapp} onWhatsappChange={setWhatsapp}
                contactEmail={contactEmail} onContactEmailChange={setContactEmail}
                website={website} onWebsiteChange={setWebsite}
                facebook={facebook} onFacebookChange={setFacebook}
                instagram={instagram} onInstagramChange={setInstagram}
                openingHours={openingHours} onOpeningHoursChange={setOpeningHours}
                treeHeight={treeHeight} onTreeHeightChange={setTreeHeight}
                aerowayRef={aerowayRef} onAerowayRefChange={setAerowayRef}
                aerowaySurface={aerowaySurface} onAerowaySurfaceChange={setAerowaySurface}
                aerowayWidth={aerowayWidth} onAerowayWidthChange={setAerowayWidth}
                junctionRef={junctionRef} onJunctionRefChange={setJunctionRef}
                junctionExitRoads={junctionExitRoads}
                onJunctionExitRoadsChange={setJunctionExitRoads}
                junctionSearchProximity={selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : null}
                turnFromRoad={turnFromRoad}
                onTurnFromRoadChange={setTurnFromRoad}
                turnToRoad={turnToRoad}
                onTurnToRoadChange={setTurnToRoad}
                turnRestrictionType={turnRestrictionType}
                onTurnRestrictionTypeChange={setTurnRestrictionType}
                turnSearchProximity={selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : null}
                stopRef={stopRef} onStopRefChange={setStopRef}
                stopRoutes={stopRoutes} onStopRoutesChange={setStopRoutes}
                stopOperators={stopOperators} onStopOperatorsChange={setStopOperators}
                stopShelter={stopShelter} onStopShelterChange={setStopShelter}
                stopBench={stopBench} onStopBenchChange={setStopBench}
                stopLighting={stopLighting} onStopLightingChange={setStopLighting}
                poiCuisine={poiCuisine} onPoiCuisineChange={setPoiCuisine}
                poiBrand={poiBrand} onPoiBrandChange={setPoiBrand}
                poiWheelchair={poiWheelchair} onPoiWheelchairChange={setPoiWheelchair}
                paymentMobileMoney={paymentMobileMoney} onPaymentMobileMoneyChange={setPaymentMobileMoney}
                paymentCard={paymentCard} onPaymentCardChange={setPaymentCard}
                mobileMoneyProvider={mobileMoneyProvider} onMobileMoneyProviderChange={setMobileMoneyProvider}
                description={description} onDescriptionChange={setDescription}
                descriptionSourceUrl={descriptionSourceUrl} onDescriptionSourceUrlChange={setDescriptionSourceUrl}
                photoDataUrls={photoDataUrls}
                onPhotoInputChange={handlePhotoInputChange}
                onPhotoDrop={handlePhotoDrop}
                onRemovePhoto={removePhotoAt}
                isFullDetails={isFullDetails}
                showLocationSuburb={showLocationSuburb}
                onRetryAddressLookup={retryAddressLookup}
                metadataOnly={isGssMetadataEdit}
                metadataUpdateIntent={officialUpdateIntent}
                officialBoundaryLabel={officialBoundaryName || name || enrichFeature?.name}
                submitLabel={
                  enrichFeature && (selectedFeatureType?.id === 'place' || isOfficialAreaFlow)
                    ? 'Review & Submit Update'
                    : undefined
                }
                submitLoading={matchingExisting}
                onSubmit={handleDetailsSubmit}
              />
              </>
            )}
            {step === 'building_units' && renderBuildingUnits()}
            {step === 'review' && renderReview()}
          </motion.div>
        </AnimatePresence>
      </div>
      </>
      ) : null}

      {/* Feedback Modal */}
      <AnimatePresence>
        {submitFeedback && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-t-3xl"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: 'spring', damping: 22, stiffness: 300 }}
              className="mx-6 w-full max-w-sm bg-white dark:bg-neutral-800 rounded-2xl shadow-xl p-6 text-center space-y-4"
            >
              <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center ${submitFeedback.type === 'success'
                ? 'bg-emerald-100 dark:bg-emerald-900/40'
                : 'bg-red-100 dark:bg-red-900/40'
                }`}>
                {submitFeedback.type === 'success' ? (
                  <CheckCircle2 size={28} className="text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <AlertCircle size={28} className="text-red-600 dark:text-red-400" />
                )}
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
                  {submitFeedback.type === 'success' ? 'Submitted!' : 'Something went wrong'}
                </h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-neutral-400">
                  {submitFeedback.message}
                </p>
                {submitFeedback.type === 'success' && (
                  <p className="mt-2 text-xs text-gray-400 dark:text-neutral-500">
                    Your contribution will be reviewed within 24-48 hours.
                  </p>
                )}
              </div>

              {submitFeedback.type === 'success' ? (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      handleClose();
                      setSubmitFeedback(null);
                    }}
                    className="flex-1 py-3 rounded-xl font-semibold transition-colors shadow-sm bg-gray-200 dark:bg-neutral-700 hover:bg-gray-300 dark:hover:bg-neutral-600 text-gray-900 dark:text-neutral-100"
                  >
                    Done
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Reset form but keep panel open
                      setSubmitFeedback(null);
                      setStep('feature_type');
                      setSelectedFeatureType(null);
                      setGeometryMode(null);
                      setSelectedCategory(null);
                      setName('');
                      setAddrStreet('');
                      setAddrSuburb('');
                      setAddrCity('');
                      setAddrDistrict('');
                      setAddrRegion('');
                      setGhanaPostGps('');
                      setBuildingUnits([]);
                      setUnitBatchFloor('1');
                      setAutoNumberCount('');
                      setAutoNumberStart('1');
                      floorBatchDraftsRef.current = {};
                      lastActiveFloorRef.current = '1';
                      setAddressLookupStatus('idle');
                      setPhone('');
                      setRoadSurface('');
                      setRoadSmoothness('');
                      setRoadCarriagewayCount(null);
                      setRoadLanes('');
                      setRoadLanesForward('');
                      setRoadLanesBackward('');
                      setRoadLaneDirections([]);
                      setRoadManualLaneDirections(false);
                      setRoadToll(false);
                      setRoadFloodProne(false);
                      setRoadPotholes(false);
                      setRoadStreetVendors(false);
                      setRoadAccidentProne(false);
                      setRoadHawkers(false);
                      setTreeHeight('');
                      setAerowayRef('');
                      setAerowaySurface('');
                      setAerowayWidth('');
                      setAerowayWidth('');
                      setJunctionRef('');
                      setJunctionExitRoads([]);
                      setDescription('');
                      setPhotoDataUrls([]);
                      setCoordLat('');
                      setCoordLng('');
                      setEnrichFeature(null);
                      setCategorySearch('');
                      setLinkInput('');
                      setLinkError(null);
                      setMobile('');
                      setWhatsapp('');
                      setContactEmail('');
                      setWebsite('');
                      setFacebook('');
                      setInstagram('');
                      setOpeningHours('');
                      setSheetSnap('half');
                      onClearGeometry();
                      onEnableMapClick(false);
                      onEnableDrawing(false);
                    }}
                    className="flex-1 py-3 rounded-xl font-semibold transition-colors shadow-sm bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    Add Another
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (step === 'enrich_select') {
                      resetEnrichSelection();
                      return;
                    }
                    setSubmitFeedback(null);
                  }}
                  className="w-full py-3 rounded-xl font-semibold transition-colors shadow-sm bg-gray-200 dark:bg-neutral-700 hover:bg-gray-300 dark:hover:bg-neutral-600 text-gray-900 dark:text-neutral-100"
                >
                  Try Again
                </button>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
